import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CustomLayout } from '@/components/ui/custom-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CreditService } from '@/services/creditService';
import { useCredits } from '@/hooks/useCredits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, Linkedin, Building2, Briefcase, Target, BarChart3, GraduationCap, Heart, Users, X, Paperclip, ChevronDown, ChevronRight, Settings, CheckCircle, Mail, ArrowLeft, TrendingUp, Loader2, MessageSquare, Clock, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { ResumeService } from '@/services/resumeService';
import { TailoredResumeService } from '@/services/tailoredResumeService';
import { ResumeLibraryService } from '@/services/resumeLibraryService';
import { supabase } from '@/integrations/supabase/client';
import { JobWizard } from '@/components/JobWizard/JobWizard';
import { DocumentUpload } from '@/components/DocumentUpload';
import { JobAnalysisService, type JobAnalysisData, type SavedJobAnalysis } from "@/services/jobAnalysisService";
import { Step1Responsibilities } from "@/components/AdvancedTailoringWizard/Step1Responsibilities";
import { Step2Requirements } from "@/components/AdvancedTailoringWizard/Step2Requirements";
import { Step3Skills } from "@/components/AdvancedTailoringWizard/Step3Skills";
import { convertPDFToText } from "@/services/pdfConversionService";
import { TailoringDebugViewer } from '@/components/TailoringDebugViewer';
import { PaywallGuard } from '@/components/PaywallGuard';
import { TailoringProcessingModal } from '@/components/TailoringProcessingModal';
import { onQuickStartRequest, onQuickStartStop, setQuickStartPhase, resetQuickStartProgress, clearQuickStartProgress, setQuickStartStep, type QuickStartStepId, type QuickStartStepStatus } from '@/lib/tailoringQuickStart';
import { buildRunReport, saveRunReport, type StageUsage } from '@/lib/runCostReport';
import { startLedgerRun, ledgerRunFields } from '@/lib/ledgerRun';
import { setTailoringRunStatus } from '@/lib/tailoringRunStatus';
import { ActiveRun, beginOrResumeRun, claimAutoResume, endRun, getActiveRun, markRunFailed, mirrorRunForHandoff } from '@/lib/tailoringCheckpoint';
import { startTailorRun, watchTailorRun } from '@/lib/tailorRun';
import { releaseTab } from '@/lib/keepTabAlive';

function ResumeTailoringClaudeDirectContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toast
  } = useToast();
  const {
    user,
    loading
  } = useAuth();
  const { balance: creditBalance, refetch: refetchCredits } = useCredits();
  const [basicOption, setBasicOption] = useState('');
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [selectedUploadMethod, setSelectedUploadMethod] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // The client's own Word file (needed for the "Original Format" design); null for PDFs and pasted text.
  const [originalDocxUrl, setOriginalDocxUrl] = useState<string | null>(null);
  // Design chosen on the dashboard: the results page opens in it.
  const designRef = useRef<'rdr-blue' | 'original-format'>('rdr-blue');
  const [pastedResumeText, setPastedResumeText] = useState('');
  const [userResumes, setUserResumes] = useState<any[]>([]);
  const [savedJobs, setSavedJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [showJobWizard, setShowJobWizard] = useState(false);
  const [showResumeDropdown, setShowResumeDropdown] = useState(true);
  const [showJobDropdown, setShowJobDropdown] = useState(true);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [processingStep1, setProcessingStep1] = useState(false);
  const [step1Complete, setStep1Complete] = useState(false);
  const [processedResumeData, setProcessedResumeData] = useState<string>('');
  const [tailoringSource, setTailoringSource] = useState(''); // No default selection
  const [revisionInstructions, setRevisionInstructions] = useState('');
  const [selectedRevisionOptions, setSelectedRevisionOptions] = useState<string[]>([]);
  const [generateAnalysis, setGenerateAnalysis] = useState(true); // Default to true
  // Auto-run the formatting fixers (Layout QA) on the results page, so no one
  // has to click Perfect Fit / Layout QA by hand. Default ON; per-run opt-out.
  const [autoFixFormatting, setAutoFixFormatting] = useState(true);
  const [jobDescription, setJobDescription] = useState('');
  const [jobPositionTitle, setJobPositionTitle] = useState('');
  const [jobPositionIndustry, setJobPositionIndustry] = useState('');
  const [careerLevel, setCareerLevel] = useState('');
  const [institutionType, setInstitutionType] = useState('');
  const [missionAlignment, setMissionAlignment] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  // `linkedInDataFile` state removed — the UI that drove it was orphaned and
  // the value was never sent to any API. See the comment in Step 1 where the
  // LinkedIn (Optional) block used to render.
  const [additionalDocuments, setAdditionalDocuments] = useState<{
    file: File;
    extractedText: string;
  }[]>([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  // Client worksheets — REQUIRED INPUTS in Marcus's jd-tailored-resume skill
  // (new client worksheet + metrics/quantification worksheet). Pasted text,
  // sent with every stage so the skill's source-material scans see them.
  const [newClientWorksheet, setNewClientWorksheet] = useState('');
  const [quantWorksheet, setQuantWorksheet] = useState('');
  const [showWorksheets, setShowWorksheets] = useState(false);
  const [showAdvancedOptionsJob, setShowAdvancedOptionsJob] = useState(false);
  const [showAdvancedOptionsInstitution, setShowAdvancedOptionsInstitution] = useState(false);
  const [institutionName, setInstitutionName] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>(['resume']);
  // Tailoring depth: 'quick' = current surface tailoring; 'deep' = inject an
  // uploaded JobIntel-360-style Tailoring Report as special instructions.
  const [tailoringDepth, setTailoringDepth] = useState<'quick' | 'deep'>('quick');
  // Career Highlights count — default 5; 0 removes the section; 2-10 allowed.
  const [highlightCount, setHighlightCount] = useState(5);
  const [tailoringReportText, setTailoringReportText] = useState('');
  const [tailoringReportFilename, setTailoringReportFilename] = useState('');
  // Apollo-sourced hiring manager candidates from the auto-generated
  // Tailoring Report. Saved so they can be forwarded to the results page
  // and consumed by the Hiring Manager Contact Report producer.
  // deno-lint-ignore no-explicit-any
  const [hiringManagerCandidates, setHiringManagerCandidates] = useState<any[]>([]);
  // Structured pain-point objects from `company_problems_coverage`, kept in
  // their original shape so the in-page preview can render them as cards
  // (problem_title / problem_description / recommendation / coverage rating).
  // The string-formatted version still lives in `tailoringReportPainPoints`
  // below — that one is what ships to VectorShift / the producers.
  // deno-lint-ignore no-explicit-any
  const [tailoringReportPainPointsStructured, setTailoringReportPainPointsStructured] = useState<any[]>([]);
  // Pain points extracted as a clean array (separate from the formatted
  // report markdown) for the Solution Asset producer.
  const [tailoringReportPainPoints, setTailoringReportPainPoints] = useState<string>('');
  const [tailoringReportGenerating, setTailoringReportGenerating] = useState(false);
  // Progress % for the Tailoring Report popup — simulated ramp while the
  // analyze-resume-match call runs, jumps to 100 on completion.
  const [tailoringReportProgress, setTailoringReportProgress] = useState(0);
  const tailoringReportProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Speculative stage-1 pre-run: fired in the background once the JD is locked
  // in, so Start Tailoring can skip straight to stage 2 when inputs match.
  const speculativeStage1Ref = useRef<{ key: string; promise: Promise<{
    organized_jd: string; pipeline1_package2: string;
    evidence_map: string; headline_output: string; skills_output: string;
  }> } | null>(null);

  // Assessment Answers mode (stage 7) — answers + generation popup state.
  const [assessmentAnswers, setAssessmentAnswers] = useState('');
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [answersProgress, setAnswersProgress] = useState(0);
  const [showAnswersResult, setShowAnswersResult] = useState(false);
  const [step3Completed, setStep3Completed] = useState(false); // Explicit step 3 completion
  const [step4Completed, setStep4Completed] = useState(false); // Job description/details completion
  const [additionalInstructionsAnswer, setAdditionalInstructionsAnswer] = useState<'yes' | 'no' | ''>(''); // Step 5 answer
  const [step5Completed, setStep5Completed] = useState(false); // Additional instructions question answered
  const [additionalInstructionsText, setAdditionalInstructionsText] = useState(''); // Step 6 content
  // Step 6 documents — store the File AND the extracted text so we can ship
  // the contents to VectorShift in `additionalNotes` on submit.
  const [additionalInstructionsDocuments, setAdditionalInstructionsDocuments] = useState<Array<{
    file: File;
    extractedText: string;
    filename: string;
  }>>([]);
  const [step6Completed, setStep6Completed] = useState(false); // Additional content completion
  const [jobDataReady, setJobDataReady] = useState(false);
  const [processedJobData, setProcessedJobData] = useState({
    title: '',
    description: ''
  });
  // The COMPLETE JD (built from the saved job's all_* arrays) — the resume is
  // TAILORED from the Personalized JD, but SCORED against this Complete JD.
  const [completeJdText, setCompleteJdText] = useState('');

  // Advanced options state
  const [mentionCompanyName, setMentionCompanyName] = useState(false);
  const [mentionCompanyMission, setMentionCompanyMission] = useState(false);
  const [mentionEducation, setMentionEducation] = useState(false);
  const [featureSolutionAsset, setFeatureSolutionAsset] = useState(false);
  const [workExperienceFramework, setWorkExperienceFramework] = useState('none');
  const [transferableSkillsFocus, setTransferableSkillsFocus] = useState(false);
  const [skillRelevanceOptimization, setSkillRelevanceOptimization] = useState(false);
  const [irrelevantJobFiltering, setIrrelevantJobFiltering] = useState(false);
  const [sectionsToTailor, setSectionsToTailor] = useState<string[]>(['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline']);
  const [careerTransitionOptions, setCareerTransitionOptions] = useState<string[]>([]);
  const [quantificationLevel, setQuantificationLevel] = useState('');
  const [writingStyle, setWritingStyle] = useState('chronological');
  const [brandingStyle, setBrandingStyle] = useState('');
  const [selectedTailoringType, setSelectedTailoringType] = useState('basic');
  const [showAdvancedWizard, setShowAdvancedWizard] = useState(false);
  const [jobAnalysis, setJobAnalysis] = useState<any>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState<string[]>([]);
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  // Advanced wizard state
  const [wizardStep, setWizardStep] = useState<'responsibilities' | 'requirements' | 'skills'>('responsibilities');
  const [savedJobAnalyses, setSavedJobAnalyses] = useState<SavedJobAnalysis[]>([]);
  const [loadingSavedAnalyses, setLoadingSavedAnalyses] = useState(false);
  const [selectedSavedAnalysisId, setSelectedSavedAnalysisId] = useState('');
  const [savingAnalysis, setSavingAnalysis] = useState(false);

  // Assessment questions state
  const [assessmentQuestions, setAssessmentQuestions] = useState('');
  const [selectedAssessmentMethod, setSelectedAssessmentMethod] = useState('');
  const [solutionAssetInSummary, setSolutionAssetInSummary] = useState(false);

  // BASELINE GUARANTEE: a run with no advanced tailoring selected must produce
  // the exact standard-methodology outcome. Advanced options therefore reset
  // to defaults after every completed run — they never silently carry into
  // the next one. The active list is also shown next to Start Tailoring.
  const resetAdvancedOptions = () => {
    setMentionCompanyName(false);
    setMentionCompanyMission(false);
    setMentionEducation(false);
    setFeatureSolutionAsset(false);
    setSolutionAssetInSummary(false);
    setWorkExperienceFramework('none');
    setTransferableSkillsFocus(false);
    setSkillRelevanceOptimization(false);
    setIrrelevantJobFiltering(false);
    setCareerTransitionOptions([]);
    setSectionsToTailor(['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline']);
    setQuantificationLevel('');
    setWritingStyle('chronological');
    setBrandingStyle('');
  };
  const activeAdvancedLabels = (): string[] => {
    const out: string[] = [];
    if (mentionCompanyName) out.push('Mention company name');
    if (mentionCompanyMission) out.push('Mention company mission');
    if (mentionEducation) out.push('Mention education');
    if (featureSolutionAsset || solutionAssetInSummary) out.push('Solution Asset in summary');
    if (workExperienceFramework !== 'none') out.push(`${workExperienceFramework.toUpperCase()} experience format`);
    if (transferableSkillsFocus) out.push('Transferable skills focus');
    if (skillRelevanceOptimization) out.push('Skill relevance filtering');
    if (irrelevantJobFiltering) out.push('Irrelevant job filtering');
    out.push(...careerTransitionOptions);
    if (sectionsToTailor.length > 0 && sectionsToTailor.length < 6) out.push(`Only tailoring: ${sectionsToTailor.join(', ')}`);
    if (quantificationLevel) out.push(`Quantification: ${quantificationLevel}`);
    if (writingStyle && writingStyle !== 'chronological') out.push(`${writingStyle} writing style`);
    if (brandingStyle) out.push(`${brandingStyle} branding`);
    return out;
  };

  // Debug data state - load asynchronously to prevent blocking
  const [debugData, setDebugData] = useState<any[]>([]);

  // Step management state
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [stepExpanded, setStepExpanded] = useState<Record<number, boolean>>({
    1: true,
    // Start with step 1 expanded only if not completed
    2: false,
    3: false,
    4: false,
    5: false,
    6: false
  });

  // Session storage key for persisting wizard state across tab switches
  const SESSION_STORAGE_KEY = 'resume-tailoring-claude-direct-state';
  
  // State to track if processing was interrupted
  const [wasProcessingInterrupted, setWasProcessingInterrupted] = useState(false);

  // Claude Direct engine — this page routes tailoring to the parallel
  // JD-tailoring pipeline (tailor-resume-claude-jd) instead of VectorShift.
  // Mode drives Prompt-2/4/6/7's evidence rules: "Full JD" = only claim what the
  // resume proves; "Personalized JD" = every JD item is confirmed as done.
  const [tailoringMode, setTailoringMode] = useState<'Full JD' | 'Personalized JD'>('Personalized JD');
  
  // Ref to disable persistence after successful completion or manual reset
  const disablePersistenceRef = useRef(false);

  // ── JobIntel 360 handoff ──────────────────────────────────────────────────
  // ?handoff=<id> means JobIntel pushed a job + Targeting Report here via a
  // single-use token. Claim it and pre-load Steps 2-4 so the user only has to
  // pick their resume and hit tailor.
  React.useEffect(() => {
    const handoffId = new URLSearchParams(window.location.search).get('handoff');
    if (!handoffId) return;
    (async () => {
      try {
        const res = await fetch('https://zjkzrobirkkrlmrfwshw.supabase.co/functions/v1/tailorwiz-handoff', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'claim', id: handoffId }),
        });
        const j = await res.json();
        if (!res.ok || !j?.success || !j?.payload) throw new Error(j?.error || 'Handoff could not be loaded');
        const p = j.payload as Record<string, string | null>;

        setTailoringSource('job-description');
        setSelectedUploadMethod('paste');
        const desc = String(p.job_description ?? '');
        setJobDescription(desc);
        setProcessedJobData({ title: String(p.job_title ?? ''), description: desc });
        setJobDataReady(true);
        setStep3Completed(true);
        setCompletedSteps(prev => Array.from(new Set([...prev, 2, 3])));
        if (p.targeting_report_markdown) {
          setTailoringDepth('deep');
          setTailoringReportText(String(p.targeting_report_markdown));
          setTailoringReportFilename('JobIntel 360 Targeting Report');
        }
        // Drop the param so refreshes don't re-claim a consumed token.
        window.history.replaceState({}, '', window.location.pathname);
        toast({
          title: `Job loaded from JobIntel 360`,
          description: `${p.job_title ?? 'Job'}${p.company_name ? ` at ${p.company_name}` : ''} is pre-loaded${p.targeting_report_markdown ? ' with your Targeting Report (Deep Tailor)' : ''}. Pick your resume and hit tailor.`,
        });
      } catch (e: any) {
        toast({ title: 'JobIntel handoff failed', description: e?.message || 'Open the job in JobIntel and try again.', variant: 'destructive' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore wizard state from sessionStorage on mount
  React.useEffect(() => {
    // Skip if we already have data from location.state (WordDocumentViewer)
    const locationState = location.state as any;
    if (locationState?.processedResumeData && locationState?.step1Complete) {
      return;
    }
    // Skip session restore when arriving via a JobIntel handoff — the handoff
    // payload is the fresher intent and must not be overwritten by stale state.
    if (new URLSearchParams(window.location.search).get('handoff')) {
      return;
    }

    try {
      const saved = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Check if data is less than 1 hour old
        const ONE_HOUR = 60 * 60 * 1000;
        const MAX_PROCESSING_TIME = 5 * 60 * 1000; // 5 minutes (matches VectorShift timeout)
        
        if (parsed.savedAt && (Date.now() - parsed.savedAt) < ONE_HOUR) {
          // Restore all state
          if (parsed.basicOption) setBasicOption(parsed.basicOption);
          if (parsed.selectedResumeId) setSelectedResumeId(parsed.selectedResumeId);
          if (parsed.selectedUploadMethod) setSelectedUploadMethod(parsed.selectedUploadMethod);
          if (parsed.pastedResumeText) setPastedResumeText(parsed.pastedResumeText);
          if (parsed.processedResumeData) setProcessedResumeData(parsed.processedResumeData);
          if (parsed.step1Complete) setStep1Complete(parsed.step1Complete);
          
          if (parsed.tailoringSource) setTailoringSource(parsed.tailoringSource);
          if (parsed.jobDescription) setJobDescription(parsed.jobDescription);
          if (parsed.jobPositionTitle) setJobPositionTitle(parsed.jobPositionTitle);
          if (parsed.jobPositionIndustry) setJobPositionIndustry(parsed.jobPositionIndustry);
          if (parsed.selectedJobId) setSelectedJobId(parsed.selectedJobId);
          if (parsed.processedJobData) setProcessedJobData(parsed.processedJobData);
          if (parsed.jobDataReady !== undefined) setJobDataReady(parsed.jobDataReady);
          
          if (parsed.selectedDocuments) setSelectedDocuments(parsed.selectedDocuments);
          if (parsed.step3Completed) setStep3Completed(parsed.step3Completed);
          
          if (parsed.step4Completed) setStep4Completed(parsed.step4Completed);
          if (parsed.sectionsToTailor) setSectionsToTailor(parsed.sectionsToTailor);
          if (typeof parsed.highlightCount === 'number') setHighlightCount(parsed.highlightCount);
          if (parsed.quantificationLevel) setQuantificationLevel(parsed.quantificationLevel);
          if (parsed.writingStyle) setWritingStyle(parsed.writingStyle);
          if (parsed.brandingStyle) setBrandingStyle(parsed.brandingStyle);
          if (parsed.careerLevel) setCareerLevel(parsed.careerLevel);
          if (parsed.selectedTailoringType) setSelectedTailoringType(parsed.selectedTailoringType);
          
          if (parsed.additionalInstructionsAnswer) setAdditionalInstructionsAnswer(parsed.additionalInstructionsAnswer);
          if (parsed.step5Completed) setStep5Completed(parsed.step5Completed);
          if (parsed.additionalInstructionsText) setAdditionalInstructionsText(parsed.additionalInstructionsText);
          if (parsed.step6Completed) setStep6Completed(parsed.step6Completed);
          
          if (parsed.revisionInstructions) setRevisionInstructions(parsed.revisionInstructions);
          if (parsed.selectedRevisionOptions) setSelectedRevisionOptions(parsed.selectedRevisionOptions);
          
          if (parsed.currentStep) setCurrentStep(parsed.currentStep);
          if (parsed.completedSteps) setCompletedSteps(parsed.completedSteps);
          if (parsed.stepExpanded) setStepExpanded(parsed.stepExpanded);

          // Check if processing was interrupted
          if (parsed.isProcessing && parsed.processingStartedAt) {
            const processingAge = Date.now() - parsed.processingStartedAt;
            
            if (processingAge < MAX_PROCESSING_TIME) {
              // Processing was interrupted recently - show recovery message
              setWasProcessingInterrupted(true);
              console.log('Processing was interrupted. User can retry.');
            } else {
              // Processing started too long ago - clear stale processing state
              console.log('Processing session expired. Clearing stale state.');
            }
          }

          console.log('Restored wizard state from session storage');
        } else {
          // Clear stale data
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.log('Error restoring wizard state:', error);
    }
  }, []); // Run only once on mount

  // Load debug data asynchronously
  React.useEffect(() => {
    const loadDebugData = async () => {
      try {
        const stored = localStorage.getItem('tailoring-debug-data');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Only keep last 10 entries to prevent memory issues
          const limited = parsed.slice(0, 10);
          setDebugData(limited);
        }
      } catch (error) {
        console.log('Error loading debug data:', error);
        setDebugData([]);
      }
    };
    loadDebugData();
  }, []);

  // Step management functions - Flow-based progress tracking
  const getStepProgress = () => {
    // Progress based on 5 main sequential steps (1-5), Steps 6-7 are optional/conditional
    const mainStepsCompleted = completedSteps.filter(step => step <= 5).length;
    return Math.round(mainStepsCompleted / 5 * 100); // 5 main steps (0%, 20%, 40%, 60%, 80%, 100%)
  };
  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1:
        return step1Complete && !!processedResumeData;
      case 2:
        return !!tailoringSource;
      // Just having a selection is enough for step 2
      case 3:
        return step3Completed;
      // Requires explicit completion button click
      case 4:
        return step4Completed;
      // Job description/details completion
      case 5:
        return step5Completed;
      // Additional instructions question answered
      case 6:
        return step6Completed;
      // Additional content completion (only relevant if step 5 = yes)
      default:
        return false;
    }
  };

  // Auto-expand appropriate step when tailoring source is selected
  React.useEffect(() => {
    if (!tailoringSource) return;
    const newExpanded = {
      ...stepExpanded
    };
    let hasChanges = false;

    // Auto-expand the appropriate step based on tailoring source.
    // For job-description, Step 3 is now the JD input (was Step 4 before the
    // 2026-06-13 swap that put JD entry before document selection).
    if (tailoringSource === 'job-description' && !stepExpanded[3] && !isStepComplete(3)) {
      newExpanded[3] = true;
      hasChanges = true;
    } else if (tailoringSource === 'assessment-questions' && !stepExpanded[4] && !isStepComplete(4)) {
      newExpanded[4] = true;
      hasChanges = true;
    } else if (tailoringSource === 'revisions' && !stepExpanded[3] && !isStepComplete(3)) {
      newExpanded[3] = true;
      hasChanges = true;
    }
    if (hasChanges) {
      setStepExpanded(newExpanded);
    }
  }, [tailoringSource]);

  // Auto-collapse completed steps and auto-expand next step
  React.useEffect(() => {
    const newExpanded = {
      ...stepExpanded
    };
    let hasChanges = false;

    // Handle steps 1-5 (main flow)
    for (let step = 1; step <= 5; step++) {
      if (isStepComplete(step) && stepExpanded[step] === true) {
        newExpanded[step] = false; // Auto-collapse completed steps
        hasChanges = true;

        // Special logic for step 5 (additional instructions question)
        if (step === 5 && additionalInstructionsAnswer === 'yes') {
          // If user said YES, expand step 6
          if (!stepExpanded[6]) {
            newExpanded[6] = true;
            hasChanges = true;
          }
        } else if (step === 5 && additionalInstructionsAnswer === 'no') {
          // If user said NO, we're done with the main flow
          // No additional steps to show
        } else {
          // Auto-expand next step if it exists and isn't completed
          const nextStep = step + 1;
          if (nextStep <= 5 && !isStepComplete(nextStep) && !stepExpanded[nextStep]) {
            newExpanded[nextStep] = true;
            hasChanges = true;
          }
        }
      }
    }

    // Handle step 6 completion - end of flow
    if (isStepComplete(6) && stepExpanded[6] === true) {
      newExpanded[6] = false;
      hasChanges = true;
    }
    if (hasChanges) {
      setStepExpanded(newExpanded);
    }
  }, [step1Complete, processedResumeData, tailoringSource, step3Completed, step4Completed, step5Completed, additionalInstructionsAnswer, step6Completed]);

  // Enhanced step completion handlers that force immediate progress updates
  const markStepComplete = (step: number, completed: boolean = true) => {
    switch (step) {
      case 3:
        setStep3Completed(completed);
        break;
      case 4:
        setStep4Completed(completed);
        break;
      case 5:
        setStep5Completed(completed);
        break;
      case 6:
        setStep6Completed(completed);
        break;
    }
  };
  const toggleStepExpansion = (step: number) => {
    setStepExpanded(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
  };
  const canProceedToStep = (step: number): boolean => {
    if (step === 1) return true;
    return isStepComplete(step - 1);
  };
  const goToStep = (step: number) => {
    if (canProceedToStep(step)) {
      setCurrentStep(step as 1 | 2 | 3 | 4 | 5 | 6);
      setStepExpanded(prev => ({
        ...prev,
        [step]: true
      }));
    }
  };
  const continueToNextStep = () => {
    const nextStep = currentStep + 1;
    if (nextStep <= 6 && isStepComplete(currentStep)) {
      // Mark current step as complete
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }

      // Collapse current step and expand next step
      setStepExpanded(prev => ({
        ...prev,
        [currentStep]: false,
        [nextStep]: true
      }));
      setCurrentStep(nextStep as 1 | 2 | 3 | 4 | 5 | 6);
    }
  };

  // Update completedSteps when steps are actually completed
  React.useEffect(() => {
    const newCompletedSteps: number[] = [];
    for (let i = 1; i <= 6; i++) {
      if (isStepComplete(i)) {
        newCompletedSteps.push(i);
      }
    }
    setCompletedSteps(newCompletedSteps);
  }, [step1Complete, processedResumeData, tailoringSource, step3Completed, step4Completed, step5Completed, step6Completed]);

  // Automatically advance currentStep to next incomplete step
  React.useEffect(() => {
    // Find the first incomplete step
    for (let i = 1; i <= 6; i++) {
      if (!isStepComplete(i)) {
        setCurrentStep(i as 1 | 2 | 3 | 4 | 5 | 6);
        break;
      }
    }
  }, [step1Complete, processedResumeData, tailoringSource, step3Completed, step4Completed, step5Completed, step6Completed]);

  // Ensure correct initial state on mount
  React.useEffect(() => {
    // Set the initial current step and expansion state
    if (completedSteps.length === 0) {
      setCurrentStep(1);
      setStepExpanded(prev => ({
        ...prev,
        1: true
      }));
    }
  }, []); // Run only once on mount

  // Handle pre-loaded resume data from WordDocumentViewer
  useEffect(() => {
    const state = location.state as any;
    if (state?.processedResumeData && state?.step1Complete) {
      setProcessedResumeData(state.processedResumeData);
      setStep1Complete(state.step1Complete);
      setBasicOption(state.basicOption || 'upload');
      toast({
        title: "Resume loaded successfully!",
        description: `Resume from ${state.fileName || 'document viewer'} is ready for tailoring.`
      });
    }
  }, [location.state]);

  // Auto-deselect Deep Tailor-only documents when user switches away from Deep Tailor
  useEffect(() => {
    if (tailoringDepth !== 'deep') {
      setSelectedDocuments(prev => prev.filter(id => !['solution-asset-document', 'hiring-manager-contact-report'].includes(id)));
    }
  }, [tailoringDepth]);

  // Memoize heavy calculations and options
  const revisionOptions = useMemo(() => [{
    id: 'remove-redundancies',
    emoji: '✂️',
    label: 'Remove Redundancies'
  }, {
    id: 'change-designs',
    emoji: '🎨',
    label: 'Change Designs/Templates'
  }, {
    id: 'improve-formatting',
    emoji: '📝',
    label: 'Improve the Formatting'
  }, {
    id: 'remove-skills-gaps',
    emoji: '🔧',
    label: 'Remove Skills Gaps'
  }, {
    id: 'highlight-transferable',
    emoji: '🌟',
    label: 'Highlight Transferable Skills'
  }, {
    id: 'optimize-work-history',
    emoji: '📈',
    label: 'Optimize Work History'
  }, {
    id: 'add-metrics',
    emoji: '📊',
    label: 'Add Metrics & Numbers'
  }, {
    id: 'fact-check-content',
    emoji: '🔍',
    label: 'Fact Check Content for Accuracy'
  }], []);

  // Memoize writing styles to prevent re-creation
  const writingStyles = useMemo(() => [{
    value: "chronological",
    title: "Chronological Resume",
    description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
    tag: "Default"
  }, {
    value: "functional",
    title: "Functional Resume",
    description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
    tag: "Career Change"
  }, {
    value: "combination",
    title: "Combination (Hybrid) Resume",
    description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
    tag: "Balanced"
  }, {
    value: "targeted",
    title: "Targeted Resume",
    description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
    tag: "Focused"
  }, {
    value: "executive",
    title: "Executive Resume",
    description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
    tag: "Leadership"
  }, {
    value: "creative",
    title: "Creative Resume",
    description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
    tag: "Design"
  }, {
    value: "federal",
    title: "Federal Resume",
    description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
    tag: "Government"
  }, {
    value: "academic",
    title: "Academic / CV",
    description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
    tag: "Academic"
  }], []);
  // Enhanced company extraction with multiple patterns
  const extractCompanyFromJobData = (jobDesc: string) => {
    if (!jobDesc) return '';
    const lines = jobDesc.split('\n');

    // Try multiple extraction patterns
    for (const line of lines) {
      const lowerLine = line.toLowerCase();

      // Pattern 1: "Company: XYZ"
      if (lowerLine.startsWith('company:')) {
        return line.replace(/^company:\s*/i, '').trim();
      }

      // Pattern 2: "Organization: XYZ" 
      if (lowerLine.startsWith('organization:')) {
        return line.replace(/^organization:\s*/i, '').trim();
      }

      // Pattern 3: "Employer: XYZ"
      if (lowerLine.startsWith('employer:')) {
        return line.replace(/^employer:\s*/i, '').trim();
      }

      // Pattern 4: "At XYZ Company" or "at XYZ"
      if (lowerLine.includes(' at ') && line.length < 100) {
        const match = line.match(/\bat\s+([^,\n]+)/i);
        if (match) return match[1].trim();
      }

      // Pattern 5: "XYZ is seeking" or "XYZ seeks"
      if ((lowerLine.includes(' is seeking') || lowerLine.includes(' seeks')) && line.length < 100) {
        const match = line.match(/^([^,\n]+?)\s+(?:is seeking|seeks)/i);
        if (match) return match[1].trim();
      }
    }

    // Pattern 6: Look for company names in first few lines (common job posting format)
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i].trim();
      if (line && !line.toLowerCase().includes('job') && !line.toLowerCase().includes('position') && line.length > 2 && line.length < 80 && !line.includes('@')) {
        // Skip if it looks like a job title or email
        const lowerLine = line.toLowerCase();
        if (!lowerLine.includes('engineer') && !lowerLine.includes('manager') && !lowerLine.includes('director') && !lowerLine.includes('specialist') && !lowerLine.includes('analyst') && !lowerLine.includes('developer')) {
          return line;
        }
      }
    }
    return '';
  };

  // Enhanced job title extraction with fallback logic
  const extractJobTitleFromDescription = (jobDesc: string) => {
    if (!jobDesc) return '';
    const lines = jobDesc.split('\n');

    // Try multiple extraction patterns
    for (const line of lines) {
      const lowerLine = line.toLowerCase();
      const trimmedLine = line.trim();

      // Pattern 1: "Job Title: XYZ" or "Position: XYZ"
      if (lowerLine.startsWith('job title:') || lowerLine.startsWith('position:') || lowerLine.startsWith('role:')) {
        return trimmedLine.replace(/^(job title|position|role):\s*/i, '').trim();
      }

      // Pattern 2: Look for job-related keywords in first few lines
      if (trimmedLine && trimmedLine.length < 100) {
        const jobKeywords = ['engineer', 'manager', 'director', 'specialist', 'analyst', 'developer', 'coordinator', 'supervisor', 'lead', 'senior', 'junior', 'associate', 'assistant'];
        if (jobKeywords.some(keyword => lowerLine.includes(keyword))) {
          return trimmedLine;
        }
      }
    }

    // Pattern 3: First non-empty line if it looks like a job title
    const firstLine = lines.find(line => line.trim())?.trim() || '';
    if (firstLine && firstLine.length < 100 && !firstLine.includes('@') && !firstLine.toLowerCase().includes('company')) {
      return firstLine;
    }
    return '';
  };

  // Enhanced fallback functions
  const getJobTitleFallback = (source: string, data: any) => {
    console.log('getJobTitleFallback called with:', {
      source,
      data
    });
    switch (source) {
      case 'job-description':
        const titleFromData = data?.title || extractJobTitleFromDescription(data?.description || '');
        console.log('Extracted title from job description:', titleFromData);
        return titleFromData || null; // NO FALLBACK - return null if not found
      case 'job-position':
        console.log('Using jobPositionTitle:', jobPositionTitle);
        return jobPositionTitle || null; // NO FALLBACK
      case 'institution':
        const instTitle = institutionName ? `Position at ${institutionName}` : null;
        console.log('Institution title:', instTitle);
        return instTitle;
      case 'assessment-questions':
        return 'Assessment Questions Optimization';
      default:
        return null; // NO FALLBACK
    }
  };
  const getCompanyNameFallback = (source: string, data: any) => {
    console.log('getCompanyNameFallback called with:', {
      source,
      data
    });
    switch (source) {
      case 'job-description':
        const companyFromData = extractCompanyFromJobData(data?.description || '');
        console.log('Extracted company from job description:', companyFromData);
        return companyFromData || null; // NO FALLBACK - return null if not found
      case 'job-position':
        return null; // NO FALLBACK
      case 'institution':
        console.log('Using institutionName as company:', institutionName);
        return institutionName || null; // NO FALLBACK
      case 'assessment-questions':
        return null; // NO FALLBACK
      default:
        return null; // NO FALLBACK
    }
  };

  // Clear all fields function
  const clearAllFields = () => {
    setBasicOption('');
    setSelectedResumeId('');
    setSelectedUploadMethod('');
    setLinkedInUrl('');
    setUploadedFile(null);
    setPastedResumeText('');
    setSelectedJobId('');
    setJobDescription('');
    setJobPositionTitle('');
    setJobPositionIndustry('');
    setInstitutionType('');
    setInstitutionName('');
    setMissionAlignment('');
    setAdditionalInstructions('');
    setSelectedDocuments(['resume']);
    setTailoringSource('');
    setGenerateAnalysis(true);
    setStep1Complete(false);
    setProcessedResumeData('');
    setJobDataReady(false);
    setProcessedJobData({
      title: '',
      description: ''
    });
    setMentionCompanyName(false);
    setMentionCompanyMission(false);
    setMentionEducation(false);
    setFeatureSolutionAsset(false);
    setWorkExperienceFramework('none');
    setTransferableSkillsFocus(false);
    setSkillRelevanceOptimization(false);
    setIrrelevantJobFiltering(false);
    setSelectedTemplateMain('');
    setSectionsToTailor(['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline']);
    setCareerTransitionOptions([]);
    setQuantificationLevel('');
    setWritingStyle('chronological');
    setUploadedFiles([]);
    setAdditionalDocuments([]);
    setShowAdvancedOptions(false);
    setShowAdvancedOptionsJob(false);
    setShowAdvancedOptionsInstitution(false);
    setSelectedTailoringType('basic');
    setShowAdvancedWizard(false);
    setJobAnalysis(null);
    setSelectedResponsibilities([]);
    setSelectedRequirements([]);
    setSelectedSkills([]);
    setSelectedSavedAnalysisId('');
    setAssessmentQuestions('');
    setSelectedAssessmentMethod('');
    setSolutionAssetInSummary(false);
  };

  // Check if institution type suggests academic/nonprofit/religious for expanded document options
  const showExpandedDocuments = institutionType && ['academic', 'nonprofit', 'religious'].includes(institutionType);

  // Load user resumes when 'existing' option is selected
  const loadUserResumes = async () => {
    console.log('loadUserResumes called, user:', user?.id);
    if (!user?.id) {
      console.log('No user ID available');
      toast({
        title: "Authentication required",
        description: "Please log in to access your saved resumes.",
        variant: "destructive"
      });
      return;
    }
    setLoadingResumes(true);
    console.log('Loading resumes for user:', user.id);
    try {
      const {
        resumes,
        error
      } = await ResumeService.getUserResumes(user.id);
      console.log('Resume loading result:', {
        resumes: resumes?.length || 0,
        error
      });
      if (error) {
        console.error('Resume loading error:', error);
        toast({
          title: "Error loading resumes",
          description: error.message || "Could not load your saved resumes.",
          variant: "destructive"
        });
      } else {
        console.log('Successfully loaded resumes:', resumes);
        setUserResumes(resumes || []);
        if (!resumes || resumes.length === 0) {
          toast({
            title: "No resumes found",
            description: "Upload your first resume to get started with tailoring."
          });
        }
      }
    } catch (error) {
      console.error('Resume loading exception:', error);
      toast({
        title: "Error loading resumes",
        description: error instanceof Error ? error.message : "Could not load your saved resumes.",
        variant: "destructive"
      });
    } finally {
      setLoadingResumes(false);
    }
  };

  // Load saved jobs when 'existing' job option is selected
  const loadSavedJobs = async () => {
    if (!user?.id) return;
    setLoadingJobs(true);
    try {
      const {
        JobLibraryService
      } = await import('@/services/jobLibraryService');
      const jobs = await JobLibraryService.getSavedJobs();
      setSavedJobs(jobs);
    } catch (error) {
      toast({
        title: "Error loading jobs",
        description: "Could not load your saved job descriptions.",
        variant: "destructive"
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  // Load saved job analyses for advanced tailoring
  const loadSavedJobAnalyses = async () => {
    if (!user?.id) return;
    setLoadingSavedAnalyses(true);
    try {
      const analyses = await JobAnalysisService.getSavedJobAnalyses();
      setSavedJobAnalyses(analyses);
    } catch (error) {
      toast({
        title: "Error loading saved analyses",
        description: "Could not load your saved job analyses.",
        variant: "destructive"
      });
    } finally {
      setLoadingSavedAnalyses(false);
    }
  };

  // Handle saved analysis selection
  const handleSavedAnalysisSelection = (analysisId: string) => {
    if (analysisId === 'clear') {
      // Clear selection
      setSelectedSavedAnalysisId('');
      setJobAnalysis(null);
      setSelectedResponsibilities([]);
      setSelectedRequirements([]);
      setSelectedSkills([]);
      return;
    }
    setSelectedSavedAnalysisId(analysisId);
    const selectedAnalysis = savedJobAnalyses.find(a => a.id === analysisId);
    if (selectedAnalysis) {
      setJobAnalysis({
        jobTitle: selectedAnalysis.job_title,
        industry: selectedAnalysis.industry,
        responsibilities: selectedAnalysis.all_responsibilities,
        requirements: selectedAnalysis.all_requirements,
        skills: selectedAnalysis.all_skills
      });
      setSelectedResponsibilities(selectedAnalysis.selected_responsibilities);
      setSelectedRequirements(selectedAnalysis.selected_requirements);
      setSelectedSkills(selectedAnalysis.selected_skills);
      setJobPositionTitle(selectedAnalysis.job_title);
      if (selectedAnalysis.industry) {
        setJobPositionIndustry(selectedAnalysis.industry);
      }
    }
  };

  // Handle file upload for new resume
  const handleResumeUpload = async (text: string, filename?: string, fileUrl?: string, originalFile?: File) => {
    // Save to resume library if we have the original file
    if (originalFile && user?.id) {
      try {
        const resumeTitle = filename ? filename.replace(/\.[^/.]+$/, '') : 'Untitled Resume';
        const savedResume = await ResumeLibraryService.saveResumeWithFile(resumeTitle, originalFile);
        const isDocx = /\.docx$/i.test(originalFile.name);
        setOriginalDocxUrl(isDocx && savedResume?.originalFileUrl ? savedResume.originalFileUrl : null);
        toast({
          title: "Resume saved to library",
          description: `${resumeTitle} has been added to your resume library.`
        });
      } catch (error) {
        console.error('Failed to save resume to library:', error);
        // Don't show error toast since the main process still works
      }
    }
    // If we have a fileUrl, use ConvertAPI to extract text from PDF
    if (fileUrl && fileUrl.includes('http')) {
      try {
        toast({
          title: "Processing PDF...",
          description: "Extracting text from your PDF document."
        });
        const result = await convertPDFToText(fileUrl);
        if (result.success && result.text) {
          // Use the extracted text from ConvertAPI
          setProcessedResumeData(result.text);
          setStep1Complete(true);
          toast({
            title: "PDF processed successfully!",
            description: "Text extracted from your PDF. Resume data is ready."
          });

          // Save to tailored library with extracted content (as "before" version)
          if (filename && user?.id) {
            TailoredResumeService.saveBeforeResume({
              title: filename.replace(/\.[^/.]+$/, '') || 'Untitled Resume',
              content: result.text,
              file_name: filename,
              file_type: 'text/plain'
            }).catch(error => {
              console.error('Failed to save before resume to tailored library:', error);
            });
          }
          return;
        } else {
          console.error('ConvertAPI error:', result.error);
          toast({
            title: "PDF processing failed",
            description: result.error || "Could not extract text from PDF. Using uploaded file as fallback.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error during PDF conversion:', error);
        toast({
          title: "PDF processing failed",
          description: "Could not extract text from PDF. Using uploaded file as fallback.",
          variant: "destructive"
        });
      }
    }

    // Fallback to original behavior if scraping fails or no URL
    setProcessedResumeData(text);
    setStep1Complete(true);
    toast({
      title: "Resume processed successfully!",
      description: "Your resume data has been loaded. Proceed to Step 2."
    });

    // Save to tailored library (as "before" version) if we have a filename and user
    if (filename && user?.id) {
      TailoredResumeService.saveBeforeResume({
        title: filename.replace(/\.[^/.]+$/, '') || 'Untitled Resume',
        content: text,
        file_name: filename,
        file_type: 'text/plain'
      }).catch(error => {
        console.error('Failed to save before resume to tailored library:', error);
      });
    }
  };

  // Auto-generate the Tailoring Report (JobIntel 360-style pain-points brief)
  // by running analyze-resume-match (full mode) on the resume + target, then
  // formatting its findings into a readable report. The text gets used as
  // special instructions in the Deep Tailor flow.
  const handleAutoGenerateTailoringReport = async () => {
    const resumeText = processedResumeData || '';
    let jd = '';
    if (tailoringSource === 'job-description' && processedJobData?.description) {
      jd = processedJobData.description;
    } else if (tailoringSource === 'job-position' && jobPositionTitle) {
      jd = `Job Title: ${jobPositionTitle}${jobPositionIndustry ? `\nIndustry: ${jobPositionIndustry}` : ''}`;
    } else if (tailoringSource === 'institution' && institutionName) {
      jd = `Institution: ${institutionName}${institutionType ? `\nType: ${institutionType}` : ''}`;
    }
    if (!resumeText) {
      toast({ title: 'Resume required', description: 'Upload your resume first (Step 1).', variant: 'destructive' });
      return;
    }
    if (!jd) {
      toast({ title: 'Target required', description: 'Select a target first (Step 2).', variant: 'destructive' });
      return;
    }
    setTailoringReportGenerating(true);
    // Simulated progress: ramp toward 90% over ~45s while the analysis runs;
    // the completion handler jumps it to 100 and closes the popup.
    setTailoringReportProgress(0);
    if (tailoringReportProgressRef.current) clearInterval(tailoringReportProgressRef.current);
    tailoringReportProgressRef.current = setInterval(() => {
      setTailoringReportProgress(p => (p >= 90 ? 90 : p + 2));
    }, 1000);
    try {
      const { data, error } = await supabase.functions.invoke('analyze-resume-match', {
        body: { action: 'resume_job_match', resumeText, job_description_text: jd }
      });
      if (error || !data?.success || !data?.result) {
        throw new Error((error as any)?.message || data?.error || 'Failed to generate Tailoring Report');
      }
      const r: any = data.result;
      const parts: string[] = ['# Tailoring Report', ''];
      if (r.tailoring_verdict?.verdict_reasoning) {
        parts.push('## Verdict', r.tailoring_verdict.verdict_reasoning, '');
      }
      if (Array.isArray(r.company_problems_coverage) && r.company_problems_coverage.length) {
        parts.push('## Company Pain Points to Address');
        r.company_problems_coverage.forEach((p: any, i: number) => {
          parts.push(`${i + 1}. ${p.problem_title}: ${p.problem_description}`);
          if (p.recommendation) parts.push(`   - Recommendation: ${p.recommendation}`);
        });
        parts.push('');
      }
      if (Array.isArray(r.missing_keywords) && r.missing_keywords.length) {
        parts.push('## Missing Keywords to Incorporate');
        r.missing_keywords.forEach((k: any) => {
          parts.push(`- ${k.keyword} (${k.importance}) — ${k.context}`);
        });
        parts.push('');
      }
      if (Array.isArray(r.top_recommended_changes) && r.top_recommended_changes.length) {
        parts.push('## Top Recommended Changes');
        r.top_recommended_changes.forEach((c: any) => {
          parts.push(`${c.rank}. ${c.change_title}: ${c.change_description}`);
        });
        parts.push('');
      }
      // Apollo-sourced hiring-manager candidates (real people from a structured
      // people database). Prefer the response-root array (always Apollo); fall
      // back to Claude's structured pass-through inside `result`.
      const hmCandidates = Array.isArray((data as any)?.hiring_manager_candidates)
        ? (data as any).hiring_manager_candidates
        : (Array.isArray(r.hiring_manager_candidates) ? r.hiring_manager_candidates : []);
      if (hmCandidates.length) {
        parts.push('## Likely Hiring Manager / Decision Makers');
        parts.push('Sourced from Apollo (verified people database). Use these as your outreach targets.');
        parts.push('');
        hmCandidates.forEach((c: any, i: number) => {
          const bits: string[] = [`${i + 1}. ${c.name || 'Unknown'} — ${c.title || ''}`];
          if (c.linkedin_url) bits.push(`   - LinkedIn: ${c.linkedin_url}`);
          if (c.email) bits.push(`   - Email: ${c.email}${c.email_status ? ` (${c.email_status})` : ''}`);
          if (c.source) bits.push(`   - Source: ${c.source}${c.confidence ? ` · confidence ${c.confidence}` : ''}`);
          parts.push(bits.join('\n'));
        });
        parts.push('');
      }
      const reportText = parts.join('\n');
      setTailoringReportText(reportText);
      setTailoringReportFilename('Auto-Generated Tailoring Report');
      // Save the structured data so the Solution Asset + HM Report producers
      // can use them without re-extracting from the markdown.
      setHiringManagerCandidates(hmCandidates);
      const painPointsArr: any[] = Array.isArray(r.company_problems_coverage) ? r.company_problems_coverage : [];
      const painPointsList = painPointsArr
        .map((p: any, i: number) => `${i + 1}. ${p.problem_title}: ${p.problem_description}${p.recommendation ? `\n   Recommendation: ${p.recommendation}` : ''}`)
        .join('\n');
      setTailoringReportPainPoints(painPointsList);
      // Keep the structured objects so the in-page preview can render
      // them as cards (with coverage rating + recommendation).
      setTailoringReportPainPointsStructured(painPointsArr);
      const apolloMsg = hmCandidates.length
        ? ` ${hmCandidates.length} hiring-manager candidate${hmCandidates.length > 1 ? 's' : ''} from Apollo included.`
        : '';
      toast({ title: 'Tailoring Report generated', description: `Pain points + recommendations ready.${apolloMsg} They will be injected as special instructions when you tailor.` });
    } catch (e: any) {
      toast({ title: 'Could not generate report', description: e?.message || 'Try again or upload your own.', variant: 'destructive' });
    } finally {
      if (tailoringReportProgressRef.current) {
        clearInterval(tailoringReportProgressRef.current);
        tailoringReportProgressRef.current = null;
      }
      setTailoringReportProgress(100);
      // Brief pause at 100% so the user sees completion before the popup closes.
      setTimeout(() => setTailoringReportGenerating(false), 600);
    }
  };


  // ── Assessment Answers mode (stage 7) ────────────────────────────────────
  // Answers the pasted assessment questions AS the candidate from their real
  // resume + materials. Completely separate from the 9-prompt resume pipeline.
  const handleGenerateAssessmentAnswers = async () => {
    setGeneratingAnswers(true);
    setAnswersProgress(0);
    const progressTimer = setInterval(() => setAnswersProgress(p => (p >= 90 ? 90 : p + 3)), 1000);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('You are not signed in.');

      const extraMaterials = [
        ...additionalDocuments.map(d => d.extractedText),
        ...additionalInstructionsDocuments.map(d => d.extractedText),
      ].filter(Boolean).join('\n\n---\n\n');

      const STAGE_URL = 'https://gxatnnzwaggcvzzurgsq.supabase.co/functions/v1/tailor-resume-claude-jd';
      const STAGE_APIKEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd4YXRubnp3YWdnY3Z6enVyZ3NxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTI2MTQzMzMsImV4cCI6MjA2ODE5MDMzM30.btK3YXr_f0uPm_mwT5q9-8U3d10_QMevDSlC2NSZO_I';
      const res = await fetch(STAGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, apikey: STAGE_APIKEY },
        body: JSON.stringify({
          stage: 7,
          resume: processedResumeData,
          questions: assessmentQuestions.trim(),
          extra_materials: extraMaterials,
          special_instructions: additionalInstructionsText.trim(),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${(await res.text()).slice(0, 200)}`);
      const j = await res.json();
      if (!j?.success || !j?.answers) throw new Error(j?.error || 'Answer generation failed');
      setAssessmentAnswers(j.answers);
      setShowAnswersResult(true);
      toast({ title: 'Answers ready!', description: 'Every question answered from your real resume. Review, copy, or download below.' });
    } catch (e: any) {
      toast({ title: 'Answer generation failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      clearInterval(progressTimer);
      setAnswersProgress(100);
      setTimeout(() => setGeneratingAnswers(false), 500);
    }
  };

  // Handle additional document upload
  const handleAdditionalDocumentUpload = (text: string, filename: string) => {
    // Create a new File object for storage (since we only get text back)
    const file = new File([text], filename, {
      type: 'text/plain'
    });
    setAdditionalDocuments(prev => [...prev, {
      file,
      extractedText: text
    }]);
  };

  // Handle basic option change
  const handleBasicOptionChange = (value: string) => {
    setBasicOption(value);
    if (value === 'existing') {
      loadUserResumes();
    }
    // Reset sub-selections when changing main option
    setSelectedResumeId('');
    setSelectedUploadMethod('');
    setUploadedFile(null);
    setPastedResumeText('');
    setLinkedInUrl('');
    setShowResumeDropdown(true);
  };

  // Handle job upload method change
  const handleJobUploadMethodChange = (value: string) => {
    setSelectedUploadMethod(value);
    if (value === 'existing') {
      loadSavedJobs();
    }
    // Reset selections and job data ready state
    setSelectedJobId('');
    setJobDescription('');
    setJobDataReady(false);
    setShowJobDropdown(true);
    setProcessedJobData({
      title: '',
      description: ''
    });
  };

  // Build the COMPLETE JD (the full, modified job stored in the library) from a
  // saved job's all_* arrays. The tailored resume is ALWAYS scored against this —
  // never the personalized subset, never a fallback.
  const buildCompleteJd = (job: any): string => `MODIFIED JOB DESCRIPTION - ${job.jobTitle}
Company Name: ${job.companyName}
Company Mission: ${job.companyMission}

Top Responsibilities (ranked by importance):
${(job.allResponsibilities ?? []).map((resp: string, i: number) => `${i + 1}. ${resp}`).join('\n')}

Top Requirements (ranked by importance):
${(job.allRequirements ?? []).map((req: string, i: number) => `${i + 1}. ${req}`).join('\n')}

Top Skills (ranked by importance):
${(job.allSkills ?? []).map((skill: string, i: number) => `${i + 1}. ${skill}`).join('\n')}`;

  // Handle job selection from existing jobs
  const handleJobSelection = (jobId: string) => {
    console.log('Debug - Job selection:', jobId, savedJobs.length);
    setSelectedJobId(jobId);
    setShowJobDropdown(false);
    const selectedJob = savedJobs.find(job => job.id === jobId);
    console.log('Debug - Found selected job:', selectedJob);
    if (selectedJob) {
      // Construct job description from the saved job data
      const jobDesc = `Company: ${selectedJob.companyName}
Job Title: ${selectedJob.jobTitle}
Company Mission: ${selectedJob.companyMission}

Key Responsibilities:
${selectedJob.selectedResponsibilities.map(resp => `• ${resp}`).join('\n')}

Requirements:
${selectedJob.selectedRequirements.map(req => `• ${req}`).join('\n')}

Required Skills:
${selectedJob.selectedSkills.map(skill => `• ${skill}`).join('\n')}`;
      // COMPLETE JD (the full job) — used to SCORE the tailored resume.
      const completeJd = buildCompleteJd(selectedJob);
      setCompleteJdText(completeJd);
      console.log('Debug - Setting job data:', {
        title: selectedJob.jobTitle,
        descLength: jobDesc.length,
        completeJdLength: completeJd.length
      });
      setProcessedJobData({
        title: selectedJob.jobTitle,
        description: jobDesc
      });
      setJobDataReady(true);
      markStepComplete(3, true); // Mark step 3 (JD input) as completed when job is selected

      // Show success toast
      toast({
        title: "Job Selected Successfully!",
        description: `${selectedJob.jobTitle} at ${selectedJob.companyName} is ready for tailoring.`
      });
    } else {
      console.log('Debug - No job found, clearing data');
      setJobDataReady(false);
      setStep3Completed(false); // Reset step 3 completion when job is cleared
      setProcessedJobData({
        title: '',
        description: ''
      });
    }
  };

  // Handle job description paste
  const handleJobDescriptionChange = (value: string) => {
    setJobDescription(value);
    if (value.trim()) {
      setProcessedJobData({
        title: 'Position from Job Description',
        description: value.trim()
      });
      setJobDataReady(true);
      markStepComplete(3, true); // Mark step 3 (JD input) as completed when job description is provided
    } else {
      setJobDataReady(false);
      markStepComplete(3, false);
      setProcessedJobData({
        title: '',
        description: ''
      });
    }
  };

  // processStep1 removed — it was never called from anywhere, and it carried a
  // latent bug (overwrote tailoringSource, the Step 2 answer, with a display
  // label). Resume processing happens inline in each Step 1 option's handler.

  // State for progress tracking
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Simulate progress bar that takes about 30 seconds
  const simulateAnalysisProgress = () => {
    setAnalysisProgress(0);
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev < 85) {
          // Increment by 2-4% every 1-2 seconds to reach 85% in ~30 seconds
          const increment = Math.random() * 2 + 2;
          const timeFactor = prev < 70 ? 1 : 0.5; // Slow down after 70%
          return Math.min(prev + increment * timeFactor, 85);
        }
        return prev;
      });
    }, 1000 + Math.random() * 1000); // Random interval between 1-2 seconds

    return progressInterval;
  };

  // Generate AI job analysis
  const generateJobAnalysis = async () => {
    if (!jobPositionTitle) return;
    setLoadingAnalysis(true);
    const progressInterval = simulateAnalysisProgress();
    try {
      // Wait for at least 30 seconds or until we reach 85%
      const startTime = Date.now();
      while (analysisProgress < 85 && Date.now() - startTime < 30000) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-job-analysis', {
        body: {
          jobTitle: jobPositionTitle,
          industry: jobPositionIndustry
        }
      });
      if (error) throw error;
      if (data.success) {
        // Complete the progress bar
        clearInterval(progressInterval);
        setAnalysisProgress(100);

        // Brief delay to show 100% completion
        await new Promise(resolve => setTimeout(resolve, 500));
        setJobAnalysis(data.analysis);
        // Pre-select all items by default
        setSelectedResponsibilities(data.analysis.responsibilities);
        setSelectedRequirements(data.analysis.requirements);
        setSelectedSkills(data.analysis.skills);
        toast({
          title: "Job analysis complete!",
          description: `Generated tailored questions for ${jobPositionTitle}${jobPositionIndustry ? ` in ${jobPositionIndustry}` : ''}.`
        });
      } else {
        throw new Error(data.error || 'Failed to generate job analysis');
      }
    } catch (error: any) {
      console.error('Job analysis error:', error);
      clearInterval(progressInterval);
      setAnalysisProgress(0);
      toast({
        title: "Analysis failed",
        description: error.message || "Could not generate job-specific questions. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingAnalysis(false);
    }
  };
  const removeFile = (index: number) => {
    setAdditionalDocuments(prev => prev.filter((_, i) => i !== index));
  };
  const handleSectionToggle = (section: string) => {
    setSectionsToTailor(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  };
  const handleCareerTransitionToggle = (option: string) => {
    setCareerTransitionOptions(prev => prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]);
  };
  const handleDocumentToggle = (document: string) => {
    setSelectedDocuments(prev => prev.includes(document) ? prev.filter(doc => doc !== document) : [...prev, document]);
  };
  const [isProcessing, setIsProcessing] = useState(false);
  // Stop button: aborts the in-flight stage call and makes every later step of
  // the run bail out, so a stopped run never navigates to results or charges.
  const runAbortRef = useRef<AbortController | null>(null);
  const isRunCancelled = () => !!runAbortRef.current?.signal.aborted;
  const [tailoringResults, setTailoringResults] = useState<any>(null);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // Speculative stage-1 pre-run REMOVED (Oct 25 2026): it ran three paid
  // Claude calls in the background whenever a resume and job were loaded,
  // including on page load from saved state and 3s after every run ended,
  // with nobody pressing Start. Nothing calls Claude unless the user starts a run.


  // Exit warning: Warn users when leaving page during processing or with unsaved progress
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check if there's work in progress that would be lost
      const hasUnsavedProgress = step1Complete || processedResumeData || tailoringSource || jobDescription;
      
      if (isProcessing || hasUnsavedProgress) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [isProcessing, step1Complete, processedResumeData, tailoringSource, jobDescription]);

  const [elapsedTime, setElapsedTime] = useState(0); // in seconds
  const [timerInterval, setTimerIntervalState] = useState<NodeJS.Timeout | null>(null);
  const [finalDuration, setFinalDuration] = useState<number | null>(null);

  // Publish the live run status for the app-wide floating progress pill: the
  // page stays MOUNTED across route changes (App.tsx), so the run keeps going
  // while the user works on other screens and the pill brings them back.
  useEffect(() => {
    setTailoringRunStatus({ active: isProcessing, progress: progressPercentage, elapsedSeconds: elapsedTime });
  }, [isProcessing, progressPercentage, elapsedTime]);
  
  // Ref to track start timestamp for reliable duration calculation
  const timerStartRef = React.useRef<number | null>(null);
  // Checkpoint record for the current run (survives a browser tab reload);
  // interruptedRun drives the "resume" banner after the tab was discarded.
  const activeRunRef = React.useRef<ActiveRun | null>(null);
  const [interruptedRun, setInterruptedRun] = useState<ActiveRun | null>(() => getActiveRun());
  const autoResumeAttempted = useRef(false);

  // AUTO-RESUME: when the browser reloaded the tab mid-run (Memory Saver,
  // renderer restart), restart the run WITHOUT a click as soon as the restored
  // form state is valid again. Completed stages replay from checkpoints, and
  // the timer continues from the original start. The banner stays as the
  // fallback for when the form could not restore itself.
  useEffect(() => {
    if (!interruptedRun || autoResumeAttempted.current || isProcessing) return;
    if (!step1Complete || !processedResumeData || !tailoringSource) return;
    if (tailoringSource === 'job-description' && selectedUploadMethod === 'paste' && !jobDescription) return;
    if (tailoringSource === 'job-description' && selectedUploadMethod === 'existing' && !selectedJobId) return;
    autoResumeAttempted.current = true;
    const t = setTimeout(() => {
      if (!claimAutoResume()) {
        markRunFailed();
        setInterruptedRun(null);
        setQuickStartPhase({ phase: 'error', message: 'Tailoring was interrupted. Your finished steps are saved. Please press start to continue.' });
        return;
      }
      console.log('⏯ Auto-resuming interrupted tailoring run from checkpoints…');
      // Every real entry point for this is the dashboard's quick-start box, so
      // a resumed run must stay tracked as one too: otherwise it falls back to
      // the legacy full-page behavior and pops the blocking
      // TailoringProcessingModal every time the tab comes back, instead of
      // just the small pill a quick-start run uses.
      quickStartActiveRef.current = true;
      handleSubmit();
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interruptedRun, isProcessing, step1Complete, processedResumeData, tailoringSource, selectedUploadMethod, jobDescription, selectedJobId]);

  // ── Dashboard quick start ─────────────────────────────────────────────────
  // The dashboard's tailoring box posts its settings through
  // lib/tailoringQuickStart. This page is always mounted (hidden when the user
  // is elsewhere), so it applies the settings, builds the Deep Tailoring
  // Report when asked, and starts the run while the user stays on the
  // dashboard. Phases are reported back so the box can show progress.
  const quickStartPendingRef = useRef(false);
  const quickStartActiveRef = useRef(false);
  // The dashboard's Stop button (TailoringSteps) aborts the run from anywhere.
  const cancelRef = useRef<() => void>(() => {});
  useEffect(() => onQuickStartStop(() => cancelRef.current()), []);
  const quickStartReportRequestedRef = useRef(false);
  const quickStartRunStartedRef = useRef(false);
  const isProcessingRef = useRef(false);
  const handleSubmitRef = useRef<null | (() => Promise<void>)>(null);
  // Bumped on every dashboard request so the launcher re-checks even when the
  // request repeats settings this page already holds (no other state changes).
  const [quickStartNonce, setQuickStartNonce] = useState(0);
  // Mark a real stage for the dashboard's live step list (quick-start runs only).
  const qsStep = (id: QuickStartStepId, status: QuickStartStepStatus) => {
    if (quickStartActiveRef.current) setQuickStartStep(id, status);
  };
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);

  useEffect(() => onQuickStartRequest((q) => {
    if (isProcessingRef.current) {
      setQuickStartPhase({ phase: 'error', message: 'A tailoring run is already in progress. Wait for it to finish, then try again.' });
      return;
    }
    setTailoringSource('job-description');
    setSelectedUploadMethod('paste');
    setJobDescription(q.jobDescription);
    setProcessedJobData({ title: q.jobTitle, description: q.jobDescription });
    // Scoring target: the saved job's complete JD, or the pasted text itself.
    // Clear any job left selected from an earlier studio session so it can't
    // be picked up as the scoring target for this run.
    setCompleteJdText(q.completeJd);
    setSelectedJobId('');
    setJobDataReady(true);
    setBasicOption('upload');
    setProcessedResumeData(q.resumeText);
    setOriginalDocxUrl(q.originalDocxUrl ?? null);
    designRef.current = q.design === 'original-format' && q.originalDocxUrl ? 'original-format' : 'rdr-blue';
    setStep1Complete(true);
    setSelectedDocuments(q.documents);
    setTailoringDepth(q.depth);
    setTailoringMode(q.mode);
    setHighlightCount(q.highlightCount);
    setGenerateAnalysis(q.generateAnalysis);
    setAutoFixFormatting(q.autoFixFormatting);
    setAdditionalInstructionsAnswer(q.instructions.trim() ? 'yes' : 'no');
    setAdditionalInstructionsText(q.instructions);
    setNewClientWorksheet(q.newClientWorksheet);
    setQuantWorksheet(q.quantWorksheet);
    // Nothing from an earlier studio session may ride along into this run.
    setAdditionalInstructionsDocuments([]);
    setAdditionalDocuments([]);
    setUploadedFiles([]);
    // A new job needs its own Tailoring Report; never reuse the last one.
    setTailoringReportText('');
    setTailoringReportFilename('');
    setStep3Completed(true);
    setStep4Completed(true);
    setStep5Completed(true);
    setStep6Completed(true);
    setCompletedSteps([1, 2, 3, 4, 5, 6]);
    quickStartPendingRef.current = true;
    quickStartActiveRef.current = true;
    setQuickStartNonce((n) => n + 1);
    quickStartReportRequestedRef.current = false;
    quickStartRunStartedRef.current = false;
    resetQuickStartProgress(q.depth === 'deep');
    if (q.depth === 'deep') setQuickStartStep('report', 'active');
    setQuickStartPhase(q.depth === 'deep' ? { phase: 'report' } : { phase: 'starting' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Launch once the applied settings have landed in state.
  useEffect(() => {
    if (!quickStartPendingRef.current || isProcessing) return;
    if (!step1Complete || !processedResumeData || tailoringSource !== 'job-description') return;
    if (!jobDataReady || !processedJobData.description) return;
    if (tailoringDepth === 'deep' && !tailoringReportText.trim()) {
      if (tailoringReportGenerating) return;
      if (!quickStartReportRequestedRef.current) {
        quickStartReportRequestedRef.current = true;
        handleAutoGenerateTailoringReport();
        return;
      }
      quickStartPendingRef.current = false;
      quickStartActiveRef.current = false;
      setQuickStartPhase({ phase: 'error', message: 'The Tailoring Report could not be built, so Deep tailoring did not start. Try again, or switch to Quick.' });
      return;
    }
    quickStartPendingRef.current = false;
    if (tailoringDepth === 'deep') qsStep('report', 'done');
    setQuickStartPhase({ phase: 'starting' });
    // Fire once and never cancel: a re-render in the next moment (an auth
    // refresh, say) must not swallow the launch, since pending is already off.
    // The ref always holds the latest handleSubmit, so it reads settled state.
    setTimeout(async () => {
      await handleSubmitRef.current?.();
      if (quickStartActiveRef.current && !quickStartRunStartedRef.current) {
        quickStartActiveRef.current = false;
        setQuickStartPhase({ phase: 'error', message: 'Tailoring did not start. Check the message on screen, then try again.' });
      }
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickStartNonce, isProcessing, step1Complete, processedResumeData, tailoringSource, jobDataReady, processedJobData.description, tailoringDepth, tailoringReportText, tailoringReportGenerating]);

  // Report the run itself back to the dashboard.
  useEffect(() => {
    if (!quickStartActiveRef.current) return;
    if (isProcessing) {
      quickStartRunStartedRef.current = true;
      setQuickStartPhase({ phase: 'running' });
    } else if (quickStartRunStartedRef.current) {
      quickStartActiveRef.current = false;
      setQuickStartPhase({ phase: 'idle' });
    }
  }, [isProcessing]);

  // Save wizard state to sessionStorage whenever critical state changes (including processing state)
  React.useEffect(() => {
    // Don't save if persistence is disabled (after successful completion or manual reset)
    if (disablePersistenceRef.current) {
      return;
    }
    
    // Don't save if we're in an initial/empty state
    if (!step1Complete && !tailoringSource && !processedResumeData && !isProcessing) {
      return;
    }
    
    // Don't save if tailoring is complete (results exist)
    if (tailoringResults) {
      return;
    }

    const stateToSave = {
      // Resume data
      basicOption,
      selectedResumeId,
      selectedUploadMethod,
      pastedResumeText,
      processedResumeData,
      step1Complete,
      
      // Tailoring source and job data
      tailoringSource,
      jobDescription,
      jobPositionTitle,
      jobPositionIndustry,
      selectedJobId,
      processedJobData,
      jobDataReady,
      
      // Document selection
      selectedDocuments,
      step3Completed,
      
      // Preferences
      step4Completed,
      sectionsToTailor,
      highlightCount,
      quantificationLevel,
      writingStyle,
      brandingStyle,
      careerLevel,
      selectedTailoringType,

      // Additional instructions
      additionalInstructionsAnswer,
      step5Completed,
      additionalInstructionsText,
      step6Completed,
      
      // Revision options
      revisionInstructions,
      selectedRevisionOptions,
      
      // Step management
      currentStep,
      completedSteps,
      stepExpanded,
      
      // Processing state - CRITICAL for tab switch recovery
      isProcessing,
      progressPercentage,
      processingStartedAt: isProcessing ? (timerStartRef.current || Date.now()) : null,
      
      // Timestamp to detect stale data
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
      mirrorRunForHandoff();
    } catch (error) {
      console.log('Error saving wizard state:', error);
    }
  }, [
    basicOption, selectedResumeId, selectedUploadMethod, pastedResumeText, processedResumeData, step1Complete,
    tailoringSource, jobDescription, jobPositionTitle, jobPositionIndustry, selectedJobId, processedJobData, jobDataReady,
    selectedDocuments, step3Completed, step4Completed, sectionsToTailor, highlightCount, quantificationLevel, writingStyle,
    brandingStyle, careerLevel, selectedTailoringType,
    additionalInstructionsAnswer, step5Completed, additionalInstructionsText, step6Completed,
    revisionInstructions, selectedRevisionOptions, currentStep, completedSteps, stepExpanded,
    isProcessing, progressPercentage
  ]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer control functions
  const startTimer = () => {
    setElapsedTime(0);
    setFinalDuration(null);
    timerStartRef.current = Date.now(); // Store start timestamp
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    const interval = setInterval(() => {
      // Timestamp-based so the counter stays correct even when Chrome
      // throttles background-tab timers to one tick per minute.
      setElapsedTime(timerStartRef.current ? Math.floor((Date.now() - timerStartRef.current) / 1000) : 0);
    }, 1000);
    setTimerIntervalState(interval);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerIntervalState(null);
    }
  };
  
  // Get reliable duration from start timestamp (immune to state timing issues)
  const getReliableDuration = (): number => {
    if (timerStartRef.current) {
      return Math.floor((Date.now() - timerStartRef.current) / 1000);
    }
    return elapsedTime; // Fallback to state if ref not set
  };

  // Progressive progress bar that reaches 95% at 95 seconds (1% per second)
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null);
  const startProgressBar = () => {
    setProgressPercentage(1);
    if (progressInterval) {
      clearInterval(progressInterval);
    }
    const interval = setInterval(() => {
      // 1% per second capped at 95%, computed from the run's start timestamp
      // so background-tab timer throttling cannot stall the bar.
      setProgressPercentage((prev) => {
        const byTime = timerStartRef.current ? Math.min(95, Math.floor((Date.now() - timerStartRef.current) / 1000)) : prev + 1;
        return Math.min(95, Math.max(prev, byTime));
      });
    }, 1000); // 1 second per 1% increment

    setProgressInterval(interval);
  };
  const stopProgressBar = () => {
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
  };
  const cancelTailoring = () => {
    runAbortRef.current?.abort();
    // Quick Start runs: reset the dashboard panel too.
    quickStartActiveRef.current = false;
    clearQuickStartProgress();
    setQuickStartPhase({ phase: 'idle' });
    endRun();
    activeRunRef.current = null;
    setInterruptedRun(null);
    setIsProcessing(false);
    setProgressPercentage(0);
    setElapsedTime(0);
    setFinalDuration(null);
    setIsComplete(false);
    stopProgressBar();
    stopTimer();
    toast({
      title: "Tailoring stopped",
      description: "The run was stopped. Nothing was saved and no credits were used.",
      variant: "default"
    });
  };
  cancelRef.current = cancelTailoring;
  const updateProgress = (progress: number, message: string) => {
    // Only update progress if it's higher than current
    if (progress > progressPercentage) {
      setProgressPercentage(progress);
    }
    console.log(`Progress: ${progress}% - ${message}`);
  };

  // Auto-check solution asset option when conditions are met
  React.useEffect(() => {
    if (tailoringSource === 'job-description' && selectedDocuments.includes('solution-asset-document') && solutionAssetInSummary) {
      setFeatureSolutionAsset(true);
    }
  }, [tailoringSource, selectedDocuments, solutionAssetInSummary]);
  const handleSubmit = async () => {
    console.log('=== TAILORING SUBMIT STARTED ===');
    console.log('step1Complete:', step1Complete);
    console.log('processedResumeData length:', processedResumeData?.length || 0);
    console.log('tailoringSource:', tailoringSource);
    if (!step1Complete || !processedResumeData) {
      console.log('ERROR: Resume not processed - step1Complete:', step1Complete, 'processedResumeData length:', processedResumeData?.length || 0);
      toast({
        title: "Resume not processed",
        description: "Please complete Step 1 first by processing your resume.",
        variant: "destructive"
      });
      return;
    }
    if (!tailoringSource) {
      toast({
        title: "Please select a tailoring source",
        description: "Choose what you want to tailor your resume to.",
        variant: "destructive"
      });
      return;
    }
    let jobDesc = '';
    let jobTitle = '';
    if (tailoringSource === 'job-description') {
      console.log('Debug - tailoringSource:', tailoringSource);
      console.log('Debug - selectedUploadMethod:', selectedUploadMethod);
      console.log('Debug - selectedJobId:', selectedJobId);
      console.log('Debug - jobDataReady:', jobDataReady);
      console.log('Debug - processedJobData:', processedJobData);
      if (jobDataReady && processedJobData.description) {
        // Use the processed job data regardless of method
        jobDesc = processedJobData.description;
        jobTitle = processedJobData.title;
        console.log('Debug - Using processed job data - Title:', jobTitle, 'Desc length:', jobDesc.length);
      } else {
        console.log('Debug - Job data not ready or missing');
      }
      if (!jobDesc) {
        toast({
          title: "Job description required",
          description: "Please enter or select a job description.",
          variant: "destructive"
        });
        return;
      }
    }
    if (tailoringSource === 'job-position') {
      if (!jobPositionTitle) {
        toast({
          title: "Job position title required",
          description: "Please enter the job position title.",
          variant: "destructive"
        });
        return;
      }
      jobTitle = jobPositionTitle;
      // Advanced Tailoring: if the user completed the wizard and CONFIRMED the
      // responsibilities/requirements/skills they have actually done, those
      // user-attested lists ARE the job profile — the strongest possible input,
      // a pre-verified Modified JD. Basic (or no wizard data): a build-
      // instruction block so P1 constructs a full-strength profile instead of
      // ranking from a 2-line stub.
      const hasWizardData = selectedTailoringType === 'advanced' &&
        (selectedResponsibilities.length > 0 || selectedRequirements.length > 0 || selectedSkills.length > 0);
      if (hasWizardData) {
        jobDesc = `TARGET POSITION PROFILE (no specific job posting exists — the candidate confirmed every item below as work they have ACTUALLY done)

Job Title: ${jobPositionTitle}${jobPositionIndustry ? `\nIndustry: ${jobPositionIndustry}` : ''}${careerLevel ? `\nCareer Level: ${careerLevel}` : ''}

CONFIRMED RESPONSIBILITIES (user-attested, in priority order — treat as the ranked responsibilities of the Modified JD):
${selectedResponsibilities.map((r, i) => `${i + 1}. ${r}`).join('\n') || '(none confirmed — infer from title, industry, and career level)'}

CONFIRMED REQUIREMENTS (user-attested — treat as the ranked requirements):
${selectedRequirements.map((r, i) => `${i + 1}. ${r}`).join('\n') || '(none confirmed — infer from title, industry, and career level)'}

CONFIRMED SKILLS (user-attested — treat as the ranked skills list):
${selectedSkills.map((r, i) => `${i + 1}. ${r}`).join('\n') || '(none confirmed — infer from title, industry, and career level)'}

HOW TO BUILD THE MODIFIED JD FOR THIS TARGET:
- Use the confirmed lists above AS the ranked responsibilities, requirements, and skills, preserving their order. Fill any list short of its target count (15 responsibilities / 10 requirements / 20 skills) by inferring additional items typical for this title, industry, and career level, ranked BELOW the confirmed ones.
- SKILL FORMAT (mandatory): every entry in the Top 20 Skills list must be ONE short, individual, named skill or keyword of 1 to 4 words, exactly as it would appear in a job posting's skills list (e.g. "AutoCAD", "Capital Project Management", "OSHA Compliance", "Primavera P6"). NEVER a sentence, NEVER a descriptive phrase, NEVER a clause with "for", "including", or "and".
- Infer the Reason for Hiring from the problems this role type exists to solve at this career level in this industry.
- Every confirmed item is verified experience: treat coverage of these items as attested when mapping evidence.`;
      } else {
        jobDesc = `TARGET POSITION PROFILE (no specific job posting exists — the candidate is targeting this role type)

Job Title: ${jobPositionTitle}${jobPositionIndustry ? `\nIndustry: ${jobPositionIndustry}` : ''}${careerLevel ? `\nCareer Level: ${careerLevel}` : ''}

HOW TO BUILD THE MODIFIED JD FOR THIS TARGET:
- Generate the top 15 responsibilities, top 10 requirements, and top 20 skills that hiring managers genuinely expect for this exact title${jobPositionIndustry ? ' in this industry' : ''}${careerLevel ? ' at this career level' : ''}, ranked by importance, using the industry's real terminology.
- SKILL FORMAT (mandatory): every entry in the Top 20 Skills list must be ONE short, individual, named skill or keyword of 1 to 4 words, exactly as it would appear in a job posting's skills list (e.g. "AutoCAD", "Capital Project Management", "OSHA Compliance", "Primavera P6"). NEVER a sentence, NEVER a descriptive phrase, NEVER a clause with "for", "including", or "and".
- Infer the Reason for Hiring from the problems this role type exists to solve${careerLevel ? ` at the ${careerLevel} level` : ''}.
- Calibrate scope, tone, and seniority of every ranked item to the career level: an executive profile ranks strategy, P&L, and organizational leadership; an entry profile ranks execution and learning velocity.
- Never invent facts about any specific company; build the profile from what is typical and defensible for this role type.`;
      }
    }
    if (tailoringSource === 'institution') {
      if (!institutionName) {
        toast({
          title: "Institution name required",
          description: "Please enter the institution/organization name.",
          variant: "destructive"
        });
        return;
      }
      jobTitle = `Position at ${institutionName}`;
      // No job posting exists for this mode — give the JD Organizer explicit
      // instructions for HOW to build the Modified JD from the institution
      // profile + the candidate's own resume, so downstream prompts get a
      // full-strength organized JD instead of a 3-line stub. The type-specific
      // line teaches P1 which stakeholders, funding context, and vocabulary
      // this class of institution actually hires around.
      const INSTITUTION_TYPE_GUIDANCE: Record<string, string> = {
        academic: 'Academic institution: rank teaching/mentorship, research or program outcomes, accreditation and curriculum standards, committee and shared-governance collaboration, and student-success metrics. Vocabulary: departments, faculty, provost, enrollment, retention, accreditation.',
        nonprofit: 'Nonprofit: rank mission impact, program outcomes, grant and funder relationships, board reporting, volunteer and community engagement, and doing more with constrained budgets. Vocabulary: programs, grants, donors, beneficiaries, impact measurement.',
        government: 'Government agency: rank regulatory compliance, public accountability, procurement and budget stewardship, cross-agency coordination, and policy implementation. Vocabulary: statutes, appropriations, constituents, audits, interagency.',
        religious: 'Faith-based organization: rank mission and values alignment, community and congregation service, stewardship of resources, volunteer leadership, and pastoral or ministry collaboration. Vocabulary: ministry, congregation, stewardship, outreach, discipleship.',
        other: 'Weight mission alignment, stakeholder types, funding/regulatory context, and scale appropriate to how this organization describes itself.',
      };
      jobDesc = `TARGET INSTITUTION PROFILE (no specific job posting exists — the candidate is targeting this organization)

Institution: ${institutionName}${institutionType ? `\nInstitution Type: ${institutionType}` : ''}${missionAlignment ? `\nMission / Alignment Notes: ${missionAlignment}` : ''}

HOW TO BUILD THE MODIFIED JD FOR THIS TARGET:
- The target job title is the candidate's CURRENT or most natural next role (read it from their resume), positioned at this institution.
- Infer the Reason for Hiring from what organizations of this type genuinely struggle with, connected to this institution's mission where provided.
- Build the ranked responsibilities, requirements, and skills from what this type of institution requires for that role, using its real industry terminology.
- SKILL FORMAT (mandatory): every entry in the Top 20 Skills list must be ONE short, individual, named skill or keyword of 1 to 4 words, exactly as it would appear in a job posting's skills list. NEVER a sentence, NEVER a descriptive phrase, NEVER a clause with "for", "including", or "and".
- ${INSTITUTION_TYPE_GUIDANCE[institutionType] || INSTITUTION_TYPE_GUIDANCE.other}
- Never invent facts about the specific institution beyond what is stated above; where specifics are unknown, rank by what is typical and defensible for its type.`;
    }
    if (tailoringSource === 'assessment-questions') {
      if (!assessmentQuestions.trim()) {
        toast({
          title: "Assessment questions required",
          description: "Please enter or upload the assessment questions.",
          variant: "destructive"
        });
        return;
      }
      // Assessment mode does NOT tailor a resume — it ANSWERS the questions as
      // the candidate, grounded in their resume + materials, via stage 7.
      await handleGenerateAssessmentAnswers();
      return;
    }
    if (tailoringSource === 'revisions') {
      // Revisions don't have a JD — the user is asking us to revise their
      // existing document. Validate at least one option is selected, then
      // synthesize a clear "revision target" so VectorShift's pipeline,
      // which expects a jobDescription, has the instructions it needs.
      if (selectedRevisionOptions.length === 0 && !revisionInstructions.trim()) {
        toast({
          title: "Revision details required",
          description: "Pick at least one revision option or write revision instructions.",
          variant: "destructive"
        });
        return;
      }
      const REVISION_OPTION_INSTRUCTIONS: Record<string, string> = {
        'remove-redundancies':       'Remove redundant phrasing, duplicate bullets, and repeated information without losing any unique facts, metrics, or accomplishments.',
        'change-designs':            'Note that the user wants a different design/template — this primarily affects the Docmosis template chosen on the results page; content can stay focused on substance.',
        'improve-formatting':        'Improve formatting consistency: parallel bullet structure, consistent date formats, consistent capitalization, no orphaned half-lines.',
        'remove-skills-gaps':        'Identify and address skills gaps by repositioning existing experience and highlighting transferable skills. Never invent skills the user does not have.',
        'highlight-transferable':    'Highlight transferable hard skills and accomplishments that translate across roles and industries.',
        'optimize-work-history':     'Strengthen the Professional Experience section: lead with strong action verbs, emphasize achievements over responsibilities, sharpen bullet impact.',
        'add-metrics':               'Add or strengthen metrics where the user\'s text already implies them (team size, scale, percentage improvements, dollar impact). Use realistic ranges (e.g. "10 to 15"); never fabricate exact numbers.',
        'fact-check-content':        'Fact-check for internal accuracy: consistent dates, consistent job titles, consistent company names, no contradictions between sections.',
      };
      const optionsInstructions = selectedRevisionOptions
        .map((id) => REVISION_OPTION_INSTRUCTIONS[id] || `Apply revision option: ${id}.`)
        .join('\n• ');
      jobTitle = 'Document Revision';
      jobDesc = `REVISION MODE — The user wants their existing resume revised, NOT tailored to a new job. ` +
        `Apply the following revision actions to the resume the user uploaded. PRESERVE every truthful fact, metric, dollar amount, ` +
        `system name, and proper noun from the original. Do NOT invent experience or skills the user doesn't have.\n\n` +
        `HOW TO BUILD THE MODIFIED JD FOR THIS MODE:\n` +
        `- Do NOT invent a job posting. Derive EVERYTHING from the candidate's own resume: the target job title is their current or most recent title; ` +
        `the ranked responsibilities, requirements, and skills are the ones their resume already demonstrates, ranked by prominence in their actual experience. ` +
        `The Reason for Hiring is simply the value this candidate already delivers in their current direction.\n` +
        `- This guarantees the rewritten resume keeps the candidate's existing direction and simply presents it better, per the revision actions below.\n\n` +
        `REVISION ACTIONS TO APPLY:\n• ${optionsInstructions}` +
        (revisionInstructions.trim() ? `\n\nUSER-WRITTEN REVISION INSTRUCTIONS:\n${revisionInstructions.trim()}` : '');
    }

    // Deep Tailor requires an uploaded Tailoring Report.
    if (tailoringDepth === 'deep' && !tailoringReportText.trim()) {
      toast({
        title: "Tailoring Report required",
        description: "Deep Tailor needs your Tailoring Report. Upload it in Step 3, or switch to Quick Tailor.",
        variant: "destructive"
      });
      return;
    }

    console.log('=== VALIDATION PASSED, CHECKING CREDITS ===');
    
    // Only CHECK credits before processing - deduction happens AFTER successful save
    try {
      const creditCheck = await CreditService.hasEnoughCredits('resume_tailoring');
      if (!creditCheck.hasEnough) {
        toast({
          title: "Insufficient Credits",
          description: `Resume tailoring requires ${creditCheck.required} credits. You have ${creditCheck.available}. Please purchase more credits.`,
          variant: "destructive"
        });
        return;
      }
      
      // Credits will be deducted AFTER the resume is successfully saved to the library
      console.log('=== CREDITS VERIFIED, PROCEEDING (deduction on save) ===');
      
    } catch (creditError: any) {
      console.error('Credit check error:', creditError);
      toast({
        title: "Credit Error",
        description: creditError.message || "Failed to verify credits. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    console.log('=== CREDITS VERIFIED, STARTING PROCESSING ===');
    setIsProcessing(true);
    setIsComplete(false);
    setWasProcessingInterrupted(false); // Clear any previous interruption flag
    runAbortRef.current = new AbortController();

    // Start the gradual progress bar and timer
    startProgressBar();
    startTimer();
    // Checkpointing: begin a resumable run — or RESUME the one a browser tab
    // reload interrupted. On resume the timer continues from the ORIGINAL
    // start and completed stage calls replay instantly from checkpoints.
    {
      const { run, resumed } = beginOrResumeRun();
      activeRunRef.current = run;
      setInterruptedRun(null);
      if (resumed) {
        timerStartRef.current = run.startTs;
        setElapsedTime(Math.floor((Date.now() - run.startTs) / 1000));
        toast({
          title: 'Resuming interrupted tailoring',
          description: 'Completed steps are being restored from checkpoints — the run continues where it left off.',
        });
      }
    }
    try {
      console.log('=== PROCESSING STATE SET TO TRUE ===');
      toast({
        title: "Starting resume tailoring...",
        description: "Your resume is being tailored using TailorWiz AI."
      });
      console.log('=== TOAST DISPLAYED ===');

      // Check if any advanced options are actually selected.
      // BASELINE INVARIANT: with nothing selected this returns false and the
      // run's payload is identical to a plain run — defaults are silent.
      const hasAdvancedOptions = () => {
        return tailoringSource === 'job-position' || mentionCompanyName || mentionCompanyMission || mentionEducation || featureSolutionAsset || solutionAssetInSummary || workExperienceFramework !== 'none' || transferableSkillsFocus || skillRelevanceOptimization || irrelevantJobFiltering || careerTransitionOptions.length > 0 || (writingStyle && writingStyle !== 'chronological') || (sectionsToTailor.length > 0 && sectionsToTailor.length < 6) || brandingStyle !== '' || quantificationLevel !== ''
        ;
      };

      // Generate advanced tailoring instructions ONLY if options are selected
      // Shared directive builders — used by the main generator AND the
      // revisions-only path so the text can never drift between sources.
      const buildWritingStyleDirective = (): string => {
        if (!writingStyle || writingStyle === 'chronological') return '';
        switch (writingStyle) {
          case 'functional':
            return "FUNCTIONAL FORMAT: Group achievements and responsibilities under relevant skill categories rather than by employer. Minimize emphasis on specific dates and employers. Focus on transferable skills and accomplishments that align with target role requirements.";
          case 'combination':
            return "COMBINATION FORMAT: Begin with a strong skills summary highlighting top qualifications, followed by detailed chronological work history. Balance emphasis between skills demonstration and career progression. Show both competencies and professional development.";
          case 'targeted':
            return "TARGETED FORMAT: Ensure every section directly aligns with the specific job requirements. Prioritize relevant experience and skills. Remove or minimize anything not directly applicable to the target position. Use keywords from the job posting throughout.";
          case 'executive':
            return "EXECUTIVE FORMAT: Use boardroom-ready language and emphasize strategic leadership impact. Focus on quantifiable business results, scope of responsibility, and organizational influence. Include P&L responsibility, team size, and budget management. May extend to 2-3 pages with detailed accomplishments.";
          case 'creative':
            return "CREATIVE FORMAT: Incorporate visual elements and innovative layouts while maintaining professionalism. Highlight creative projects, portfolio work, and innovative solutions. Use industry-specific terminology and showcase design thinking or creative problem-solving abilities.";
          case 'federal':
            return "FEDERAL FORMAT: Include comprehensive detail with exact employment dates, hours worked per week, salary history, and supervisor contact information. Use specific federal keywords and KSAs (Knowledge, Skills, Abilities). Provide detailed duty descriptions and include all relevant experience, education, and training.";
          case 'academic':
            return "ACADEMIC/CV FORMAT: Include comprehensive sections for publications, presentations, research, grants, teaching experience, and service. Use academic formatting conventions and include all relevant scholarly activities. Emphasize research contributions, peer-reviewed publications, and academic achievements.";
          default:
            return '';
        }
      };
      const buildSectionsDirective = (): string => {
        if (!(sectionsToTailor.length > 0 && sectionsToTailor.length < 6)) return '';
        const ALL_SECTIONS = ['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'];
        const excluded = ALL_SECTIONS.filter(s => !sectionsToTailor.includes(s));
        return `SECTIONS TO TAILOR: Tailor ONLY these sections: ${sectionsToTailor.join(', ')}. ` +
          `The following sections must NOT be tailored, rewritten, or reworded in any way: ${excluded.join(', ')}. ` +
          `For each excluded section, reproduce its content EXACTLY as it appears in the candidate's original resume, word for word. ` +
          `If the original resume has no content for an excluded section, leave that section out entirely rather than writing new content for it.`;
      };

      const generateAdvancedInstructions = () => {
        if (!hasAdvancedOptions()) {
          return '';
        }
        // Per-source applicability: revisions shows only Writing Style +
        // Sections controls, so ONLY those directives may ship — stale
        // sessionStorage values from other panels (frameworks, branding,
        // company mentions) must never leak into a revision run. Assessment
        // mode bypasses the resume pipeline entirely: no directives.
        if (tailoringSource === 'assessment-questions') {
          return '';
        }
        if (tailoringSource === 'revisions') {
          const revInstructions: string[] = [];
          const ws = buildWritingStyleDirective();
          if (ws) revInstructions.push(ws);
          const sec = buildSectionsDirective();
          if (sec) revInstructions.push(sec);
          return revInstructions;
        }
        const instructions = [];

        // Special instruction for Job Position/Industry tailoring
        if (tailoringSource === 'job-position') {
          instructions.push("SPECIAL TAILORING MODE - JOB POSITION/INDUSTRY: This is a position/industry-based tailoring (no specific job description provided). We will tailor the resume to the job title and industry provided by the user. The AI will research and generate the top 15 most important responsibilities, top 15 key requirements, and top 20 essential skills for this position type within the specified industry, ranked by importance. This creates a comprehensive, AI-generated job profile that serves as our tailoring benchmark. The resume will be optimized against this researched position profile rather than a specific company's job posting.");
        }

        // Career Summary Enhancement instructions
        if (mentionCompanyName) {
          instructions.push("CAREER SUMMARY: Naturally integrate the target company name ONLY in the final sentence of the professional summary when describing how the candidate can help or contribute. CRITICAL: The applicant has NEVER worked for this target company - make no reference of them working at this company unless it was already mentioned in the original resume.");
        }
        if (mentionCompanyMission) {
          instructions.push("MISSION ALIGNMENT: Reference the company's mission statement ONLY at the end of the career summary to demonstrate cultural fit and value alignment. Place mission references in the final sentence after describing qualifications.");
        }
        if (mentionEducation) {
          instructions.push("EDUCATION EMPHASIS: Name the candidate's highest relevant credential (degree or certification, spelled out fully) in the FIRST sentence of the career summary, exactly as the resume states it. Never invent or upgrade a credential.");
        }
        if (featureSolutionAsset || solutionAssetInSummary) {
          instructions.push("SOLUTION ASSET FOCUS: In the career summary, name the candidate's single strongest repeatable capability (a specific method, system, or approach that is actually evidenced in the resume) and state the concrete business problem it solves and the measurable result it produced. No generic phrases like 'unique capabilities' or 'value propositions'; name the actual thing.");
        }

        // Experience Formatting Framework instructions
        if (workExperienceFramework !== 'none') {
          switch (workExperienceFramework) {
            case 'star':
              instructions.push("EXPERIENCE FORMAT - STAR: Restructure EVERY experience bullet in this exact order: brief situation/context clause first, then the action taken (strong verb), ending with the measurable result. Each bullet remains ONE sentence; the result is always the final element. Example shape: 'With renewals tracked only in spreadsheets, built an automated pipeline that cut churn 18%.'");
              break;
            case 'par':
              instructions.push("EXPERIENCE FORMAT - PAR: Restructure EVERY experience bullet in this exact order: the problem stated plainly, the action taken (strong verb), the measurable result last. One sentence per bullet; no bullet may omit the problem clause.");
              break;
            case 'arta':
              instructions.push("EXPERIENCE FORMAT - ARTA: Restructure EVERY experience bullet in this exact order: action verb first, measurable result second, then the specific tools/systems used (named exactly as the resume names them). Every bullet must name at least one real tool from the resume where one exists for that work.");
              break;
            case 'cosr':
              instructions.push("EXPERIENCE FORMAT - COSR: Restructure EVERY experience bullet in this exact order: the challenge or constraint (deadline, budget, scale), the outcome achieved, then the skill demonstrated. One sentence per bullet; the challenge clause must be concrete, never 'in a fast-paced environment'.");
              break;
          }
        }

        // Content Optimization instructions
        if (transferableSkillsFocus) {
          instructions.push("TRANSFERABLE SKILLS: Identify and prominently highlight the top 5 transferable hard skills and 3 key responsibilities that directly align with the target role requirements.");
        }
        if (skillRelevanceOptimization) {
          instructions.push("SKILL FILTERING: Remove or minimize bullet points that don't demonstrate transferable skills or target job requirements to maintain focus and relevance.");
        }
        if (irrelevantJobFiltering) {
          instructions.push("EXPERIENCE FILTERING: Minimize unrelated work positions to maximum 2 concise bullets each while preserving relevant qualifications and maintaining professional continuity.");
        }

        // Career Transition Support instructions
        careerTransitionOptions.forEach(option => {
          switch (option) {
            case 'Career/Industry Transition':
              instructions.push("CAREER TRANSITION: Emphasize transferable skills and bridge experience gaps. Reframe past accomplishments to highlight skills relevant to the new industry/role.");
              break;
            case 'Military to Civilian Transition':
              instructions.push("MILITARY TRANSITION: Translate all military terminology, ranks, and accomplishments into civilian-friendly language. Focus on leadership, project management, and technical skills.");
              break;
            case 'Job Relevancy Statements':
              instructions.push("RELEVANCY STATEMENTS: Add connecting statements or brief explanations showing how each past role or experience directly relates to the target position requirements.");
              break;
          }
        });

        // Branding Style — the resume's voice. One directive, concrete and testable.
        if (brandingStyle) {
          switch (brandingStyle) {
            case 'executive':
              instructions.push("BRANDING STYLE - EXECUTIVE PRESENCE: Write in boardroom register. Lead bullets with authority verbs (Directed, Oversaw, Led). Frame scope explicitly in every role: team size, budget, P&L, geography. Outcomes are framed as business results (revenue, margin, risk, growth), never task completion. No first-level task language like 'helped' or 'assisted'.");
              break;
            case 'plain-talk':
              instructions.push("BRANDING STYLE - PLAIN TALK: Every sentence must pass the out-loud test: it makes sense read aloud to someone with no business background. No stacked abstract nouns (no 'stakeholder alignment', 'revenue continuity', 'end-to-end anything'). Name the actual work and the actual result in words a stranger can picture.");
              break;
            case 'ats-optimized':
              instructions.push("BRANDING STYLE - ATS OPTIMIZED: Mirror the job description's exact keyword spellings everywhere they are truthful. Never substitute synonyms for JD terms in the Skills section or bullet keywords (if the JD says 'Project Management' do not write 'Program Oversight'). Include both the spelled-out form and the abbreviation on first use where the JD uses either (e.g. 'Enterprise Resource Planning (ERP)').");
              break;
            case 'story-snapshot':
              instructions.push("BRANDING STYLE - STORY SNAPSHOT: Each Career Highlight opens with the stake or problem BEFORE the action (what was broken, at risk, or missing), then the action, then the measurable outcome, so every highlight reads as a miniature story with tension and resolution. Experience bullets keep standard verb-first form.");
              break;
          }
        }

        // Quantification level — how aggressively to surface metrics.
        if (quantificationLevel === 'basic') {
          instructions.push("QUANTIFICATION - BASIC: Keep every metric already in the resume exactly as written. Do NOT add new numbers, ranges, or estimates anywhere, even where the text implies them.");
        } else if (quantificationLevel === 'advanced') {
          instructions.push("QUANTIFICATION - ADVANCED: Surface every metric the resume text already implies but does not state: team sizes, budgets, volumes, timeframes, percentage improvements, dollar impact. Express implied metrics as honest ranges (e.g. 'led a team of 10 to 15', 'reduced processing time roughly 30%'). NEVER fabricate an exact number the text does not support, and never alter a metric that is already stated.");
        }

        // Writing Style + Sections — shared builders (also used by revisions path)
        const wsDirective = buildWritingStyleDirective();
        if (wsDirective) instructions.push(wsDirective);
        const secDirective = buildSectionsDirective();
        if (secDirective) instructions.push(secDirective);
        return instructions;
      };

      /**
       * Format the per-rule strings as a numbered SPECIAL INSTRUCTIONS block
       * that VectorShift's pipeline reads with priority over its default
       * tailoring rules. Strong header so the downstream prompt treats the
       * items as overrides, not suggestions. When only one rule is selected
       * we still number it (1.) so the format is consistent.
       */
      const formatSpecialInstructions = (items: string[]): string => {
        if (!items || items.length === 0) return '';
        const numbered = items.map((it, i) => `${i + 1}. ${it.trim()}`).join('\n\n');
        return [
          'SPECIAL INSTRUCTIONS — APPLY WITH ABSOLUTE PRIORITY OVER ANY DEFAULT TAILORING RULES OR STYLE.',
          'These were selected by the user as advanced tailoring methods. Apply EACH numbered instruction below throughout the entire tailoring process. If any instruction conflicts with a default rule, the instruction below wins.',
          '',
          numbered,
        ].join('\n');
      };

      // Build the "user extras" block: text the user typed in Step 6 + the
      // extracted contents of any supporting docs they uploaded. Sent as
      // part of `additionalNotes` to VectorShift, and appended to the job
      // description as a defensive measure (the tailoring pipeline always
      // reads the job description, but not every prompt path reads
      // additionalNotes — appending guarantees the user's words land).
      const userExtrasParts: string[] = [];
      const userText = (additionalInstructionsText || '').trim();
      if (userText) {
        userExtrasParts.push(`USER-PROVIDED INSTRUCTIONS:\n${userText}`);
      }
      // Institution Step 5 "Institutional Guidelines" textarea — the highest-
      // signal field on the institution path (tone, values, admissions
      // requirements). Was captured but never shipped before Aug 2026.
      const institutionalGuidelines = (additionalInstructions || '').trim();
      if (tailoringSource === 'institution' && institutionalGuidelines) {
        userExtrasParts.push(`INSTITUTIONAL GUIDELINES (follow these exactly — tone, values, required themes, and any stated requirements):\n${institutionalGuidelines}`);
      }
      // Step 6 (additionalInstructionsDocuments) AND any other uploads the
      // user made in the JD / revisions / institution / position steps
      // (additionalDocuments). Both arrays are merged into one block; we
      // dedupe by filename so a doc uploaded into two places only appears
      // once.
      const docBlocks: string[] = [];
      const seenDocNames = new Set<string>();
      const collectDoc = (extractedText: string, filename: string) => {
        const txt = (extractedText || '').trim();
        const name = (filename || 'document').trim();
        if (!txt || seenDocNames.has(name)) return;
        seenDocNames.add(name);
        docBlocks.push(`--- ${name} ---\n${txt}`);
      };
      if (Array.isArray(additionalInstructionsDocuments)) {
        additionalInstructionsDocuments.forEach((d) => collectDoc(d?.extractedText || '', d?.filename || d?.file?.name || ''));
      }
      if (Array.isArray(additionalDocuments)) {
        additionalDocuments.forEach((d) => collectDoc(d?.extractedText || '', d?.file?.name || ''));
      }
      if (docBlocks.length > 0) {
        userExtrasParts.push(`USER-PROVIDED SUPPORTING DOCUMENTS:\n${docBlocks.join('\n\n')}`);
      }
      const userExtrasBlock = userExtrasParts.join('\n\n');

      // Advanced Options — every toggle in the three advanced panels
      // (mention company name, company mission, work-experience framework,
      // writing style, transferable skills focus, etc.) becomes one line
      // in `advancedInstructionsList`. We ship that list TWICE:
      //   1. As a dedicated `specialInstructions` field on the request body
      //      — VectorShift's pipeline reads this field with PRIORITY over
      //      its default tailoring rules. Numbered, priority-framed.
      //   2. As a backup paragraph inside `additionalNotes` + appended to
      //      `jobDescription` — so even pipeline branches that don't
      //      consume the new field still see the user's selections.
      const advancedInstructionsList = generateAdvancedInstructions();
      const specialInstructions = formatSpecialInstructions(advancedInstructionsList);
      if (advancedInstructionsList.length > 0) {
        console.log('Advanced Options:', advancedInstructionsList.length, 'rule(s) ·', specialInstructions.length, 'chars total');
      }

      // `combinedInstructions` is what we ship in `additionalNotes`. Order:
      // special instructions (priority) → user extras → (deep notes appended below).
      const combinedParts: string[] = [];
      if (specialInstructions) combinedParts.push(specialInstructions);
      if (userExtrasBlock) combinedParts.push(userExtrasBlock);
      let combinedInstructions = combinedParts.join('\n\n');

      console.log('=== TAILORING CALL DEBUG ===');
      console.log('Resume data length:', processedResumeData?.length || 0);
      console.log('Job description length:', jobDesc?.length || 0);
      console.log('Selected documents:', selectedDocuments);
      console.log('User extras length:', userExtrasBlock.length);
      console.log('Combined instructions being sent to VectorShift:');
      console.log(combinedInstructions);

      // Prepare debug data with enhanced extraction
      console.log('=== EXTRACTION DEBUG ===');
      console.log('tailoringSource:', tailoringSource);
      console.log('processedJobData:', processedJobData);
      console.log('jobPositionTitle:', jobPositionTitle);
      console.log('institutionName:', institutionName);
      const extractedCompany = getCompanyNameFallback(tailoringSource, processedJobData);
      const extractedJobTitle = getJobTitleFallback(tailoringSource, processedJobData);
      console.log('extractedCompany:', extractedCompany);
      console.log('extractedJobTitle:', extractedJobTitle);

      // Deep Tailor: inject the uploaded Tailoring Report as researched special
      // instructions — appended to the job description (always consumed by the
      // pipeline) and also passed as additionalNotes. Quick Tailor is unchanged.
      let finalJobDescription = jobDesc;
      let deepNotes = "";
      if (tailoringDepth === 'deep' && tailoringReportText.trim()) {
        deepNotes = `SPECIAL INSTRUCTIONS — RESEARCHED PAIN POINTS & REQUIREMENTS (from the uploaded Tailoring Report). Treat these as authoritative: weave the company's verified problems, priorities, and required competencies throughout the resume wherever it is truthful and supported by the candidate's real experience. Do NOT invent experience.\n\n${tailoringReportText.trim()}`;
        finalJobDescription = `${jobDesc}\n\n=== ${deepNotes} ===`;
        console.log('Deep Tailor: injected Tailoring Report (', tailoringReportText.length, 'chars)');
      }

      // Compose additionalNotes from THREE sources: deep-tailor report (if
      // Deep) + advanced options special-instructions block + Step 6 user
      // extras. All three are also appended to the job description so
      // downstream prompts that don't read additionalNotes still see the
      // content (defense in depth).
      const additionalNotesParts: string[] = [];
      if (deepNotes) additionalNotesParts.push(deepNotes);
      if (specialInstructions) additionalNotesParts.push(specialInstructions);
      if (userExtrasBlock) additionalNotesParts.push(userExtrasBlock);
      const additionalNotes = additionalNotesParts.join('\n\n');

      if (specialInstructions) {
        finalJobDescription = `${finalJobDescription}\n\n=== ${specialInstructions} ===`;
      }
      if (userExtrasBlock) {
        finalJobDescription = `${finalJobDescription}\n\n=== ${userExtrasBlock} ===`;
        console.log('Step 6 extras: appended user instructions/docs (', userExtrasBlock.length, 'chars)');
      }

      const requestBody = {
        resumeText: processedResumeData,
        jobDescription: finalJobDescription,
        additionalNotes,
        // NEW: dedicated field for VectorShift's "special instructions"
        // input. Carries the numbered Advanced Options block so the
        // pipeline can route it as a priority-override prompt without
        // having to re-parse it out of additionalNotes.
        specialInstructions,
        companyName: extractedCompany,
        jobTitle: extractedJobTitle
      };

      const debugEntry = {
        timestamp: new Date().toISOString(),
        resumeText: processedResumeData,
        jobDescription: jobDesc,
        requestBody: requestBody,
        response: null,
        error: null
      };

      console.log('=== STARTING CLAUDE-DIRECT TAILORING PIPELINE ===');

      let data: any = null;
      let error: any = null;

      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      if (!accessToken) {
        throw new Error('You are not logged in. Please sign in again and retry.');
      }

      // Per-stage wall-clock timings, recorded into the debug entry so we can
      // see exactly where pipeline time goes.
      const stageTimings: Record<string, number> = {};
      // Real token usage + seconds per stage (from the edge function's _usage).
      const runUsage: StageUsage[] = [];
      // One id per run: the server's cost ledger groups every Claude call under it.
      const ledgerRun = startLedgerRun(processedJobData?.title || jobPositionTitle || 'Tailoring run');
      (debugEntry as any).stageTimings = stageTimings;
      try {
        const basePayload = {
          mode: tailoringMode,
          special_instructions: additionalNotes,
          job_description: jobDesc,
          resume: processedResumeData,
          supporting_docs: '',
          new_client_worksheet: newClientWorksheet,
          quantification_worksheet: quantWorksheet,
        };

        // The server drives every step (_shared/tailor-run.ts), so a closed, suspended or
        // reloaded tab cannot stop or repeat one. This page only follows along.
        const localRun = activeRunRef.current;
        if (!localRun) throw new Error('Tailoring could not start. Please try again.');
        const serverRunId = await startTailorRun(localRun.runId, {
          base: basePayload, highlight_count: highlightCount, ...ledgerRunFields(),
        });
        mirrorRunForHandoff();
        const shown = new Set<string>();
        const STEP_PROGRESS: Record<string, number> = { start: 15, evidence: 35, write: 55, assemble: 78, rules: 92, fit: 95 };
        const finished = await watchTailorRun(serverRunId, runAbortRef.current?.signal, (p) => {
          for (const id of p.activeSteps) if (!shown.has(id)) { shown.add(id); qsStep(id as QuickStartStepId, 'active'); }
          for (const id of p.doneSteps) if (!shown.has(`${id}:done`)) { shown.add(`${id}:done`); qsStep(id as QuickStartStepId, 'done'); }
          if (STEP_PROGRESS[p.step]) setProgressPercentage(STEP_PROGRESS[p.step]);
        });
        data = finished.data;
        for (const u of finished.usage) { runUsage.push(u as StageUsage); stageTimings[u.stage] = u.seconds * 1000; }
        console.log('Rule check:', data.Rule_Check, 'Line fit:', data.Fit_Check);

        // Width-balance the skill rows (JD order preserved: only the row breaks
        // move) so no row renders far wider than its neighbors.
        const { balanceSkillRows } = await import('@/lib/resumeLayoutQa');
        const resumeDocForSkills = data?.Resume_JSON?.documents?.find((d: any) => d.documentType === 'resume');
        if (resumeDocForSkills?.data) {
          for (const [field, prefix] of [['SkillRows', 'Skill'], ['TechnicalSkillRows', 'TechSkill']] as const) {
            const balanced = balanceSkillRows(resumeDocForSkills.data[field], prefix);
            if (balanced.changed) resumeDocForSkills.data[field] = balanced.rows;
          }
        }
        setProgressPercentage(97);

        // Companion letters no longer block navigation — the results page
        // generates them in the background. Ship the inputs it needs.
        const letterDocs = selectedDocuments.filter(d =>
          ['cover-letter', 'thank-you-letter', 'outreach-letter', 'executive-bio'].includes(d));
        if (letterDocs.length > 0) {
          data._letterGenInputs = {
            documents: letterDocs,
            organized_jd: data.organized_jd,
            evidence_map: data.evidence_map,
            tailored_resume: data.Final_Resume_Text || '',
            job_title: data.job_title || jobTitle || '',
            company_name: data.company_name || '',
            special_instructions: additionalNotes,
          };
        }
        error = null;
      } catch (e: any) {
        error = e;
      }

      // Update debug entry with response/error
      if (error) {
        debugEntry.error = error.message || JSON.stringify(error);
      } else {
        debugEntry.response = data;
      }

      // Add to debug data and persist
      const newDebugData = [debugEntry, ...debugData.slice(0, 9)]; // Keep last 10 entries
      setDebugData(newDebugData);
      localStorage.setItem('tailoring-debug-data', JSON.stringify(newDebugData));
      console.log('=== CLAUDE-DIRECT PIPELINE COMPLETED ===');
      console.log('Error:', error);
      console.log('Data success:', data?.success);
      console.log('Raw data keys:', Object.keys(data || {}));
      console.log('Data structure:', JSON.stringify(data, null, 2));
      if (error) {
        console.error('Tailor resume API error:', error);
        throw error;
      }
      if (!data || !data.success) {
        console.error('Tailor resume processing failed:', data?.error || 'No data returned');
        throw new Error(data?.error || 'Tailor resume processing failed');
      }

      // Additional validation
      console.log('=== RESPONSE VALIDATION ===');
      console.log('Has Final_Resume_Text:', !!data.Final_Resume_Text);
      console.log('Has Cover_Letter:', !!data.Cover_Letter);
      console.log('Has changes_analysis:', !!data.changes_analysis);
      console.log('Changes analysis type:', typeof data.changes_analysis);
      if (data.changes_analysis && typeof data.changes_analysis === 'object') {
        console.log('Changes analysis keys:', Object.keys(data.changes_analysis));
        console.log('Keywords count:', data.changes_analysis.keywords_added?.length || 0);
        console.log('Skills count:', data.changes_analysis.skills_emphasized?.length || 0);
      }
      if (error) {
        console.error('Tailor resume API error:', error);
        throw error;
      }
      if (!data || !data.success) {
        console.error('Tailor resume processing failed:', data?.error || 'No data returned');
        throw new Error(data?.error || 'Tailor resume processing failed');
      }

      // Transform the response to match expected format
      // Use canonical score keys that match what results pages expect
      const vectorShiftResults = {
        Final_Resume_Text: data.Final_Resume_Text || data.data?.tailoredText,
        Resume_JSON: data.Resume_JSON || data.resume_json || null, // Pass Resume_JSON directly from VectorShift
        Cover_Letter: data.Cover_Letter,
        Cover_Letter_JSON: data.Cover_Letter_JSON || null,      // Stage 8 companion letters
        Thank_You_Letter: data.Thank_You_Letter,
        Thank_You_Letter_JSON: data.Thank_You_Letter_JSON || null,
        Executive_Bio: data.Executive_Bio,
        Executive_Bio_JSON: data.Executive_Bio_JSON || null,    // Structured navy bio (new design)
        Executive_Bio_QA: data.Executive_Bio_QA || null,        // Mechanical QA report for the bio
        Linkedin_Profile: data.Linkedin_Profile,
        Outreach_Letter: data.Outreach_Letter,
        Outreach_Email_JSON: data.Outreach_Email_JSON || null,
        Outreach_Letter_JSON: data.Outreach_Email_JSON || null, // results page reads both names
        analysis_results: data.analysis_results || data.analysisData,
        original_analysis: data.original_analysis || data.analysisData?.original_analysis,
        tailored_analysis: data.tailored_analysis || data.analysisData?.tailored_analysis,
        changes_analysis: data.changes_analysis || data.analysisData?.changes_analysis,
        company_name: data.company_name || data.jobInfo?.company || null, // NO FALLBACK
        job_title: data.job_title || data.jobInfo?.title || null, // NO FALLBACK
        // Use CANONICAL score keys that results pages expect
        Original_Match_Score: data.Original_Match_Score || data.original_match_score || data.data?.originalMatchScore || null,
        Tailored_Match_Score: data.Tailored_Match_Score || data.match_score || data.data?.matchScore || null,
        // Also keep legacy keys for backwards compatibility
        match_score: data.Tailored_Match_Score || data.match_score || data.data?.matchScore || null,
        original_match_score: data.Original_Match_Score || data.original_match_score || data.data?.originalMatchScore || null,
        improvements: data.improvements || data.data?.improvements,
        changes: data.changes || data.data?.changes,
        // Add run identity for debugging
        run_id: data.run_id || null,
        pipeline_used: data.pipeline_used || 'vectorshift',
        _source: 'vectorshift_tailoring'
      };
      console.log('=== TAILORING RESULTS EXTRACTED ===');
      console.log('Results keys:', Object.keys(vectorShiftResults || {}));
      console.log('Raw edge function response keys:', Object.keys(data || {}));
      // Handle both string and JSON types for logging
      const getContentPreview = (content: any, label: string) => {
        if (!content) return `${label}: NO CONTENT`;
        if (typeof content === 'string') return `${label}: ${content.substring(0, 200)}`;
        if (typeof content === 'object') return `${label} (JSON): ${JSON.stringify(content).substring(0, 200)}`;
        return `${label}: ${String(content).substring(0, 200)}`;
      };
      console.log('Final_Resume_Text:', typeof vectorShiftResults.Final_Resume_Text, vectorShiftResults.Final_Resume_Text ? 'present' : 'missing');
      console.log('Cover_Letter:', typeof vectorShiftResults.Cover_Letter, vectorShiftResults.Cover_Letter ? 'present' : 'missing');
      console.log(getContentPreview(vectorShiftResults.Cover_Letter, 'Cover_Letter content'));
      console.log('Thank_You_Letter:', typeof vectorShiftResults.Thank_You_Letter, vectorShiftResults.Thank_You_Letter ? 'present' : 'missing');
      console.log('Executive_Bio:', typeof vectorShiftResults.Executive_Bio, vectorShiftResults.Executive_Bio ? 'present' : 'missing');
      console.log('Analysis data structure:', data.analysis_results || data.analysisData);
      console.log('Original analysis extracted:', vectorShiftResults.original_analysis);
      console.log('Tailored analysis extracted:', vectorShiftResults.tailored_analysis);
      console.log('Match scores - Original:', vectorShiftResults.Original_Match_Score, 'Tailored:', vectorShiftResults.Tailored_Match_Score);

      // Stop the gradual progress and timer, set to 100%
      stopProgressBar();
      stopTimer();
      setProgressPercentage(100);
      setIsComplete(true);
      setTailoringResults(vectorShiftResults);

      // Get RELIABLE duration from timestamp ref (immune to React state timing)
      const capturedDuration = getReliableDuration();

      // Log the duration for debugging
      console.log(`Tailoring completed in ${capturedDuration} seconds (from timestamp ref)`);

      // Disable persistence and clear session storage after successful tailoring
      disablePersistenceRef.current = true;
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      // Advanced options are per-run: reset so they can never silently shape
      // the NEXT run. (buildCriteria below is computed from the pre-reset
      // values captured in this closure, so the record stays accurate.)
      resetAdvancedOptions();

      // SCORING JD — for the job-description path it is ALWAYS the COMPLETE JD
      // stored in the library (authoritative, never the personalized subset).
      // For the NO-JD paths (job position, institution, revisions) there is no
      // posting: the REAL scoring target is the organized position profile P1
      // built (echoed back by stage 3) — the exact target the resume was
      // tailored to. Falling back to the synthetic input block if the echo is
      // missing. Never an empty string, which made stage 4 return HTTP 400.
      const scoringJob = savedJobs.find(job => job.id === selectedJobId);
      const scoringCompleteJd = tailoringSource === 'job-description'
        ? (scoringJob ? buildCompleteJd(scoringJob) : (completeJdText || jobDesc))
        : (((data as any)?.organized_jd as string) || jobDesc);

      // Build Criteria — honest record of every wizard selection for the
      // results page. Only selections actually made are listed; nothing is
      // invented or defaulted in.
      const methodNames: Record<string, string> = {
        'job-description': 'Job Description',
        'job-position': 'Job Position (no posting)',
        'institution': 'Institution / Organization',
        'revisions': 'Revisions Only',
        'assessment-questions': 'Assessment Questions',
      };
      const buildCriteria: Array<{ step: string; items: Array<{ label: string; value: string }> }> = [];

      const resumeItems: Array<{ label: string; value: string }> = [];
      if (basicOption === 'existing' && selectedResumeId) {
        const r = userResumes.find((x) => x.id === selectedResumeId);
        if (r) resumeItems.push({ label: 'Source', value: `Saved resume — ${r.title || 'Untitled Resume'}` });
      } else if (uploadedFile) {
        resumeItems.push({ label: 'Source', value: `Uploaded file — ${uploadedFile.name}` });
      } else if (pastedResumeText.trim()) {
        resumeItems.push({ label: 'Source', value: 'Pasted resume text' });
      }
      if (resumeItems.length) buildCriteria.push({ step: 'Resume', items: resumeItems });

      const methodItems: Array<{ label: string; value: string }> = [];
      if (methodNames[tailoringSource]) methodItems.push({ label: 'Method', value: methodNames[tailoringSource] });
      if (tailoringSource === 'job-description') {
        methodItems.push({ label: 'Depth', value: tailoringDepth === 'deep' ? 'Deep Tailor (with Tailoring Report)' : 'Quick Tailor' });
        if (scoringJob) methodItems.push({ label: 'Job', value: `${scoringJob.jobTitle} at ${scoringJob.companyName}` });
        methodItems.push({
          label: 'JD scope',
          value: selectedTailoringType === 'advanced'
            ? `Personalized JD — ${selectedResponsibilities.length} responsibilities, ${selectedRequirements.length} requirements, ${selectedSkills.length} skills selected`
            : 'Full job description',
        });
      } else if (tailoringSource === 'job-position') {
        if (jobPositionTitle) methodItems.push({ label: 'Position', value: jobPositionTitle });
        if (jobPositionIndustry) methodItems.push({ label: 'Industry', value: jobPositionIndustry });
        if (careerLevel) methodItems.push({ label: 'Career level', value: careerLevel });
      } else if (tailoringSource === 'institution') {
        if (institutionType) methodItems.push({ label: 'Institution type', value: institutionType });
        if (institutionName) methodItems.push({ label: 'Institution', value: institutionName });
        if (missionAlignment) methodItems.push({ label: 'Mission alignment', value: missionAlignment });
        if (uploadedFiles.length) methodItems.push({ label: 'Guideline files', value: uploadedFiles.map((f) => f.name).join(', ') });
      } else if (tailoringSource === 'revisions') {
        if (selectedRevisionOptions.length) methodItems.push({ label: 'Revision options', value: selectedRevisionOptions.join(', ') });
        if (revisionInstructions.trim()) methodItems.push({ label: 'Instructions', value: revisionInstructions.trim() });
      }
      if (methodItems.length) buildCriteria.push({ step: 'Tailoring Method', items: methodItems });

      const advItems: Array<{ label: string; value: string }> = [];
      if (mentionCompanyName) advItems.push({ label: 'Mention company name', value: 'On' });
      if (mentionCompanyMission) advItems.push({ label: 'Mention company mission', value: 'On' });
      if (mentionEducation) advItems.push({ label: 'Mention education', value: 'On' });
      if (featureSolutionAsset) advItems.push({ label: 'Feature Solution Asset', value: solutionAssetInSummary ? 'On — referenced in Career Summary' : 'On' });
      if (workExperienceFramework !== 'none') advItems.push({ label: 'Work experience framework', value: workExperienceFramework });
      if (transferableSkillsFocus) advItems.push({ label: 'Transferable skills focus', value: 'On' });
      if (skillRelevanceOptimization) advItems.push({ label: 'Skill relevance optimization', value: 'On' });
      if (irrelevantJobFiltering) advItems.push({ label: 'Irrelevant job filtering', value: 'On' });
      if (careerTransitionOptions.length) advItems.push({ label: 'Career transition', value: careerTransitionOptions.join('; ') });
      if (sectionsToTailor.length > 0 && sectionsToTailor.length < 6) advItems.push({ label: 'Sections to tailor', value: sectionsToTailor.join(', ') });
      if (quantificationLevel) advItems.push({ label: 'Quantification', value: quantificationLevel });
      if (writingStyle && writingStyle !== 'chronological') advItems.push({ label: 'Writing style', value: writingStyle });
      if (brandingStyle) advItems.push({ label: 'Branding style', value: brandingStyle });
      if (advItems.length) buildCriteria.push({ step: 'Advanced Tailoring', items: advItems });

      const extraItems: Array<{ label: string; value: string }> = [];
      if (additionalInstructionsAnswer === 'yes' && additionalInstructionsText.trim()) {
        extraItems.push({ label: 'Additional instructions', value: additionalInstructionsText.trim() });
      }
      if (additionalInstructionsDocuments.length) {
        extraItems.push({ label: 'Supporting documents', value: additionalInstructionsDocuments.map((d) => d.filename).join(', ') });
      }
      if (extraItems.length) buildCriteria.push({ step: 'Additional Instructions', items: extraItems });

      const docNames: Record<string, string> = { resume: 'Resume', 'cover-letter': 'Cover Letter', 'outreach-letter': 'Outreach Letter', 'thank-you-letter': 'Thank You Letter' };
      buildCriteria.push({
        step: 'Documents & Output',
        items: [
          { label: 'Documents', value: selectedDocuments.map((d) => docNames[d] || d).join(', ') },
          { label: 'Career Highlights', value: highlightCount === 0 ? 'Removed' : `${highlightCount} bullets${highlightCount === 5 ? ' (standard)' : ''}` },
          { label: 'Tailoring analysis', value: generateAnalysis ? 'On' : 'Off' },
          { label: 'Auto-fix formatting', value: autoFixFormatting ? 'On' : 'Off' },
        ],
      });

      // Cost + timing report for this run (measurement only).
      try {
        const report = buildRunReport(runUsage, capturedDuration ?? 0);
        saveRunReport(report);
        console.log(`💲 Run report: ${report.totalCost.toFixed(4)} in ${report.totalSeconds}s`, report.unpricedModels.length ? `(unpriced: ${report.unpricedModels.join(', ')})` : '');
        console.table(report.stages);
      } catch (repErr) {
        console.warn('Run report failed:', repErr);
      }

      // Run complete: drop the resume checkpoints (nothing left to resume).
      endRun();
      activeRunRef.current = null;

      // One id per finished run: the results page saves and charges for it
      // exactly once, even if that page reloads or is reopened from history.
      const runId = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      if (isRunCancelled()) return;
      // Navigate to results page after a brief delay to show 100% completion
      setTimeout(() => {
        if (isRunCancelled()) return;
        navigate('/resume-results-templated', {
          state: {
            runId,
            tailoringResults: vectorShiftResults,
            originalResume: processedResumeData,
            // The client's Word file, when the run started from one: enables the "Original Format" design.
            originalDocxUrl: (() => {
              if (basicOption === 'existing' && selectedResumeId) {
                const r: any = userResumes.find((x) => x.id === selectedResumeId);
                const url = r?.originalFileUrl ?? r?.original_file_url ?? null;
                const name = r?.fileName ?? r?.file_name ?? '';
                return url && /\.docx$/i.test(String(name || url)) ? url : null;
              }
              return originalDocxUrl;
            })(),
            ledgerRun,
            // The design picked on the dashboard (RDR Blue or the client's own).
            initialTemplate: designRef.current === 'original-format' ? 'original-format' : undefined,
            jobTitle: vectorShiftResults?.job_title || null, // NO FALLBACK - pass null if not available
            jobDescription: jobDesc,
            companyName: vectorShiftResults?.company_name || null, // NO FALLBACK
            jobUrl: scoringJob?.job_url || null,
            generateAnalysis: generateAnalysis,
            autoFixFormatting: autoFixFormatting,
            // The run already did the ONE measured line fit: the results page must not fit again.
            fittedInRun: true,
            selectedDocuments: selectedDocuments,
            tailoringDuration: capturedDuration, // Use captured value to avoid stale closure
            source: 'vectorshift', // Mark the source explicitly
            // Inputs for the Claude Direct "Tailoring Analysis" section (stage 4).
            // Its presence tells the results page to auto-run the analysis.
            // SCORING JD is ALWAYS the COMPLETE JD from the library — the tailored
            // resume is measured against the full job, not the personalized subset.
            claudeDirectAnalysis: {
              organized_jd: scoringCompleteJd,
              evidence_map: (data as any)?.evidence_map || '',
              original_resume: processedResumeData,
              job_description: scoringCompleteJd,
              resume_json: vectorShiftResults?.Resume_JSON || null,
              tailored_resume_text: vectorShiftResults?.Final_Resume_Text || '',
              // Provenance echoed back by stage 4 so the score can be traced to
              // the exact job and resumes it was computed from.
              job_id: scoringJob?.id ?? (selectedJobId || null),
              job_title: scoringJob?.jobTitle ?? jobTitle ?? null,
              company_name: scoringJob?.companyName ?? (data as any)?.company_name ?? null,
              original_resume_name: uploadedFile?.name ?? null,
            },
            // Forwarded for the Solution Asset + Hiring Manager Contact Report
            // producers on the results page. Empty strings/arrays if Quick Tailor.
            tailoringReportText: tailoringReportText || '',
            tailoringReportPainPoints: tailoringReportPainPoints || '',
            hiringManagerCandidates: hiringManagerCandidates || [],
            // Companion letters generate in the BACKGROUND on the results page
            // (stage 8) so they never delay showing the finished resume.
            letterGenInputs: data._letterGenInputs || null,
            // Every wizard selection, listed verbatim on the results page.
            buildCriteria,
          }
        });
      }, 500); // Brief delay to show completion

      toast({
        title: "Resume tailoring complete!",
        description: `Your tailored resume is ready in ${formatTime(capturedDuration)}. Redirecting to results...`
      });
    } catch (error: any) {
      if (error?.cancelled || isRunCancelled()) {
        console.log('Tailoring run stopped by the user; no results, no save, no charge.');
        return;
      }
      console.error('Tailoring error:', error);
      markRunFailed();
      // Dashboard runs: show the failure in the box instead of a silent reset.
      if (quickStartActiveRef.current) {
        quickStartActiveRef.current = false;
        const raw = String(error?.message ?? "");
        setQuickStartPhase({
          phase: 'error',
          message: /credit balance is too low/i.test(raw)
            ? 'Tailoring is temporarily unavailable because the tailoring service account is out of funds. No credits were charged.'
            : 'Tailoring stopped before your resume was finished. No credits were charged. Please try again.',
        });
      }
      setProgressPercentage(0);
      setElapsedTime(0);
      setFinalDuration(null);
      setIsComplete(false);
      stopTimer();
      toast({
        title: "Tailoring failed",
        description: error.message || "Failed to tailor your resume. Please try again.",
        variant: "destructive"
      });
    } finally {
      releaseTab();
      setIsProcessing(false);
      stopProgressBar();
      stopTimer();
      // Reset progress after 2 seconds when complete
      if (progressPercentage === 100) {
        setTimeout(() => {
          setProgressPercentage(0);
          setElapsedTime(0);
          setIsComplete(false);
        }, 2000);
      }
    }
  };
  // Latest submit, for the dashboard quick-start launcher above.
  handleSubmitRef.current = handleSubmit;

  return <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/?view=libraries')} className="h-9 w-9 p-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">Resume Tailoring</h1>
                <p className="text-sm text-muted-foreground">AI-powered resume customization for specific jobs</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  disablePersistenceRef.current = true;
                  sessionStorage.removeItem(SESSION_STORAGE_KEY);
                  window.location.reload();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Start Fresh
              </Button>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-8 px-6 max-w-3xl">
        <div className="w-full">
          {/* Main Content Area */}
          <div className="flex-1 max-w-full">
            {/* Header Section */}
            <div className="mb-12 text-center">
              <div className="space-y-2 mb-8">
                
                
                <h1 className="text-6xl font-extrabold leading-tight tracking-tight relative">
                  <div className="absolute inset-0 bg-background/60 backdrop-blur-md rounded-lg shadow-lg -z-10"></div>
                  <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                    Resume Tailoring{" "}
                  </span>
                  <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                    Studio
                  </span>
                </h1>
                
                  <div className="max-w-3xl mx-auto px-4 mt-4 relative">
                   <div className="absolute inset-0 bg-background/50 backdrop-blur-md rounded-lg shadow-md -z-10"></div>
                   <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                     Tailor Your Resume Package to ANY Opportunity in Under 60 Seconds!
                   </p>
                    <div className="mt-4 relative">
                      {/* Step Icons */}
                      <div className="flex justify-between items-center mb-3 px-1">
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(1) ? 'bg-green-500 text-white' : 
                            currentStep === 1 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Upload className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(2) ? 'bg-green-500 text-white' : 
                            currentStep === 2 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Target className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(3) ? 'bg-green-500 text-white' : 
                            currentStep === 3 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Settings className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(4) ? 'bg-green-500 text-white' : 
                            currentStep === 4 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <FileText className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(5) ? 'bg-green-500 text-white' : 
                            currentStep === 5 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <MessageSquare className="w-3 h-3" />
                          </div>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                            completedSteps.includes(6) ? 'bg-green-500 text-white' : 
                            currentStep === 6 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <CheckCircle className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
                        <span className="font-medium">Progress</span>
                        <span className="font-medium">{getStepProgress()}% Complete</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full shadow-lg overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500 ease-out" 
                          style={{ width: `${getStepProgress()}%` }}
                        ></div>
                      </div>
                    </div>
                 </div>
                
                
              </div>
            </div>

            {/* Steps Section - Clean Card Layout */}
            <div className="space-y-4">
              {/* Step cards with clean styling */}
        <Collapsible open={stepExpanded[1]} onOpenChange={() => toggleStepExpansion(1)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(1) ? (stepExpanded[1] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[1] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(1) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(1) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 1: Resume Source</span>
                        {completedSteps.includes(1) && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-fade-in">
                            ✅ STEP 1 COMPLETE!
                          </Badge>}
                        {currentStep === 1 && !completedSteps.includes(1) && <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-pulse">
                            ⚡ PLEASE COMPLETE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                       {completedSteps.includes(1) && !stepExpanded[1] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                           {basicOption === 'existing' && selectedResumeId && userResumes.find(r => r.id === selectedResumeId) ? `Resume: ${userResumes.find(r => r.id === selectedResumeId)?.title || 'Selected Resume'}` : basicOption === 'upload' && uploadedFile ? `Uploaded: ${uploadedFile.name}` : basicOption === 'paste' ? 'Pasted resume text' : 'Resume processed'}
                         </div> : <CardDescription className="text-base">
                          {completedSteps.includes(1) ? "✅ Resume source selected and processed" : "Choose how you want to provide your resume"}
                        </CardDescription>}
                    </div>
                  </div>
                   <div className="flex items-center gap-2">
                     <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[1] ? 'rotate-180' : ''}`} />
                   </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            {/* Only show content when step is not completed OR when completed but expanded for editing */}
            {(!completedSteps.includes(1) || stepExpanded[1]) && <CollapsibleContent className="animate-accordion-down">
                <CardContent className="space-y-6 rounded-b-3xl pt-8 px-8 pb-8 bg-gradient-to-b from-white to-gray-50/50">
                <RadioGroup value={basicOption} onValueChange={handleBasicOptionChange} className="space-y-5">
                <div className={`flex items-center space-x-4 p-5 rounded-2xl border-2 cursor-pointer transition-all hover:scale-[1.02] ${basicOption === 'existing' ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200/60' : 'border-gray-300 bg-white hover:border-primary/30 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/3'}`}>
                 <RadioGroupItem value="existing" id="existing" className="mt-0.5" />
                 <Label htmlFor="existing" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${basicOption === 'existing' ? 'bg-green-100 text-green-600' : 'bg-gray-100/50 text-gray-600'}`}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Choose Existing Resume</div>
                    <div className="text-sm text-muted-foreground">Select from saved resumes in your library</div>
                  </div>
                </Label>
              </div>

              {/* Existing Resume Dropdown */}
              {basicOption === 'existing' && <div className="ml-12 space-y-3">
                  <Label className="text-sm font-medium">Select Resume</Label>
                  {loadingResumes ? <div className="p-6 border rounded-lg bg-white">
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm font-medium">Loading your resumes...</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Fetching saved resumes from your library</p>
                        </div>
                    </div> : showResumeDropdown ? <Select value={selectedResumeId} onValueChange={async (value: string) => {
                            if (value === 'upload-new') {
                              setBasicOption('upload');
                              return;
                            }
                            setSelectedResumeId(value);

                            // Automatically process the selected resume
                            if (value && user?.id) {
                              console.log('Processing selected resume:', value);
                              setProcessingStep1(true);
                              try {
                                const {
                                  resume,
                                  error
                                } = await ResumeService.getResume(value);
                                console.log('Resume fetch result:', {
                                  resume: !!resume,
                                  error
                                });
                                if (error || !resume) {
                                  console.error('Resume fetch error:', error);
                                  throw new Error(error?.message || 'Failed to load selected resume');
                                }
                                let resumeContent = '';

                                // Handle different resume types and extract text content
                                // Use stored content if available
                                if (resume.content && resume.content.trim().length > 50) {
                                  console.log('Using stored resume content directly');
                                  resumeContent = resume.content;
                                } else {
                                  console.error('Resume has insufficient stored content, length:', resume.content?.length || 0);
                                  throw new Error('This resume was saved without text content. Please re-upload it to the resume library or use the "Upload New Resume" option.');
                                }
                                if (!resumeContent.trim()) {
                                  console.error('No text content extracted');
                                  throw new Error('No text content could be extracted from the resume');
                                }
                                console.log('Resume processed successfully, content length:', resumeContent.length);
                                setProcessedResumeData(resumeContent);
                                setStep1Complete(true);
                                setShowResumeDropdown(false);
                                toast({
                                  title: "Resume loaded successfully!",
                                  description: "Your resume is ready for tailoring."
                                });
                              } catch (error: any) {
                                console.error('Resume processing error:', error);
                                toast({
                                  title: "Error loading resume",
                                  description: error.message || "Failed to load the selected resume. Please try again.",
                                  variant: "destructive"
                                });
                              } finally {
                                setProcessingStep1(false);
                              }
                            } else {
                              console.log('No value or user ID for resume processing');
                            }
                          }}>
                        <SelectTrigger className="w-full" disabled={processingStep1}>
                          <SelectValue placeholder={processingStep1 ? "Processing selected resume..." : "Choose from your saved resumes"} />
                        </SelectTrigger>
                        <SelectContent className="bg-popover">
                          {userResumes.length > 0 ? userResumes.map(resume => <SelectItem key={resume.id} value={resume.id}>
                                {resume.title || 'Untitled Resume'} - {new Date(resume.updated_at).toLocaleDateString()}
                              </SelectItem>) : <SelectItem value="no-resumes" disabled>
                              No saved resumes found
                            </SelectItem>}
                          <SelectItem value="upload-new">
                            <div className="flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              Upload New Resume Instead
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select> : null}
                    
                     {/* Processing Progress */}
                     {processingStep1 && <div className="mt-4 p-4 bg-muted/30 border border-muted rounded-lg">
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-5 h-5 border-2 border-foreground/60 border-t-transparent rounded-full animate-spin" />
                              <span className="text-sm font-medium">Processing Resume...</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Extracting text content and preparing for tailoring</p>
                          </div>
                       </div>}
                     
                     {/* Selected Resume Display */}
                      {selectedResumeId && step1Complete && !showResumeDropdown && userResumes.length > 0 && <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-950/20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-950/30 transition-colors" onClick={() => setShowResumeDropdown(true)} title="Click to change resume">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-slate-600" />
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Selected Resume:</span>
                          </div>
                          <div className="ml-7 space-y-1">
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                              {userResumes.find(r => r.id === selectedResumeId)?.title || 'Selected Resume'}
                            </div>
                            <div className="text-xs text-slate-700 dark:text-slate-300">
                              Updated: {userResumes.find(r => r.id === selectedResumeId) ? new Date(userResumes.find(r => r.id === selectedResumeId)!.updated_at).toLocaleDateString() : ''}
                            </div>
                          </div>
                        </div>}
                 </div>}
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${basicOption === 'upload' ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200/60' : 'border-gray-300 bg-white hover:border-primary/30 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/3'}`}>
                 <RadioGroupItem value="upload" id="upload" className="mt-0.5" />
                 <Label htmlFor="upload" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${basicOption === 'upload' ? 'bg-green-100 text-green-600' : 'bg-gray-100/50 text-gray-600'}`}>
                    <Upload className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Upload New Resume</div>
                    <div className="text-sm text-muted-foreground">Accepts PDF, DOC, DOCX, TXT (max 10MB)</div>
                  </div>
                </Label>
              </div>

              {/* Upload Resume Options */}
              {basicOption === 'upload' && <div className="ml-12 mt-4">
                  <DocumentUpload onUploadComplete={handleResumeUpload} title="Upload Resume" description="Upload a PDF, Word document, or paste your resume text" acceptedTypes={['.pdf', '.doc', '.docx']} />
                </div>}
               
                 <div className={`flex items-center space-x-4 p-4 rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${basicOption === 'paste' ? 'border-green-500 bg-green-50 shadow-lg shadow-green-200/60' : 'border-gray-300 bg-white hover:border-primary/30 hover:bg-gradient-to-r hover:from-primary/5 hover:to-accent/3'}`}>
                  <RadioGroupItem value="paste" id="paste" className="mt-0.5" />
                  <Label htmlFor="paste" className="flex items-center gap-3 cursor-pointer flex-1">
                     <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${basicOption === 'paste' ? 'bg-green-100 text-green-600' : 'bg-gray-100/50 text-gray-600'}`}>
                     <FileText className="w-4 h-4" />
                   </div>
                   <div>
                     <div className="font-medium">Paste Resume Text</div>
                     <div className="text-sm text-muted-foreground">Copy and paste your resume content</div>
                   </div>
                 </Label>
               </div>

                {/* Paste Resume Options */}
                {basicOption === 'paste' && <div className="ml-12 mt-4 space-y-4">
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Paste Resume Content</Label>
                      <Textarea placeholder="Paste your resume text here..." className="min-h-[200px]" value={pastedResumeText} onChange={async e => {
                              const content = e.target.value;
                              setPastedResumeText(content);
                              if (content.trim().length > 100) {
                                // Automatically process the pasted content
                                const blob = new Blob([content], {
                                  type: 'text/plain'
                                });
                                const file = new File([blob], 'pasted-resume.txt', {
                                  type: 'text/plain'
                                });
                                setUploadedFile(file);

                                // Auto-process in background
                                setTimeout(() => {
                                  handleResumeUpload(content, 'pasted-resume.txt', undefined, file);
                                }, 500);
                              } else if (content.trim().length === 0) {
                                // Clear processed data if content is empty
                                setUploadedFile(null);
                                setStep1Complete(false);
                                setProcessedResumeData('');
                              }
                            }} />
                      
                      {pastedResumeText && pastedResumeText.length > 100 && !step1Complete && <div className="text-sm text-muted-foreground">
                          {pastedResumeText.length} characters • Processing automatically...
                        </div>}
                    </div>
                  </div>}
              
              {/* The orphan "LinkedIn Data (Optional)" upload block that
                  used to live here was removed — the captured
                  `linkedInDataFile` was never sent to any API, so the
                  control did nothing. The LinkedIn Import Options below
                  (URL paste / export) is the live path. */}

              {/* LinkedIn import UI removed — there was never a 'linkedin' radio
                  option in this group, so the block could never render, and the
                  only code path that called import-linkedin-profile was dead. */}
                </RadioGroup>
              </CardContent>
              
              {/* Continue Button for Step 1 */}
              {currentStep === 1 && isStepComplete(1) && !completedSteps.includes(1) && <CardContent className="pt-0 rounded-b-lg">
                  <div className="flex justify-end">
                     <Button onClick={continueToNextStep} className="bg-green-600 hover:bg-green-700 text-white">
                      Continue to Step 2
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>}
            </CollapsibleContent>}
          </Card>
        </Collapsible>


        {/* Step 2: Tailoring Target */}
        <Collapsible open={stepExpanded[2]} onOpenChange={() => toggleStepExpansion(2)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(2) ? (stepExpanded[2] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[2] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(2) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(2) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Target className="w-5 h-5" />}
                     </div>
                     <div className="flex-1">
                       <CardTitle className="text-2xl flex items-center gap-3">
                         <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 2: Tailoring Target</span>
                          {completedSteps.includes(2) && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-fade-in">
                             ✅ STEP 2 COMPLETE!
                           </Badge>}
                         {currentStep === 2 && !completedSteps.includes(2) && <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-pulse">
                             ⚡ PLEASE COMPLETE
                           </Badge>}
                       </CardTitle>
                       
                       {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(2) && !stepExpanded[2] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                             <CheckCircle className="w-4 h-4 text-green-500" />
                            {tailoringSource === 'job-description' ? 'Job Description' : tailoringSource === 'job-position' ? `Job Position: ${jobPositionTitle || 'Selected'}` : tailoringSource === 'institution' ? `Institution: ${institutionName || 'Selected'}` : tailoringSource === 'revisions' ? 'Document Revisions' : tailoringSource === 'assessment-questions' ? 'Assessment Questions' : 'Target selected'}
                          </div> : <CardDescription className="text-base">
                           {completedSteps.includes(2) ? "✅ Tailoring target selected" : "Choose what you want to optimize your resume for"}
                         </CardDescription>}
                     </div>
                   </div>
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${stepExpanded[2] ? 'rotate-180' : ''}`} />
                    </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="animate-accordion-down">
              <CardContent className="space-y-3 rounded-b-lg">
            
            {/* Step 2 Debug - Hidden from users but kept for debugging */}
            {step1Complete && processedResumeData && <div className="hidden mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3">Step 2 Debug - Text Content</h4>
                
                <div className="space-y-3">
                  <div className="bg-white border rounded p-3">
                    <p className="text-xs font-medium text-blue-600 mb-2">Resume Text ({processedResumeData.length} characters):</p>
                    <div className="max-h-32 overflow-y-auto">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                        {processedResumeData.substring(0, 300)}
                        {processedResumeData.length > 300 && '...'}
                      </pre>
                    </div>
                  </div>
                  
                  {(jobDescription || tailoringSource === 'job-position' && jobPositionTitle || tailoringSource === 'institution' && institutionName || tailoringSource === 'assessment-questions' && assessmentQuestions) && <div className="bg-white border rounded p-3">
                      <p className="text-xs font-medium text-blue-600 mb-2">
                        {tailoringSource === 'job-description' && 'Job Description Text:'}
                        {tailoringSource === 'job-position' && 'Job Position:'}
                        {tailoringSource === 'institution' && 'Institution:'}
                        {tailoringSource === 'assessment-questions' && 'Assessment Questions:'}
                      </p>
                      <div className="max-h-32 overflow-y-auto">
                        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                          {tailoringSource === 'job-description' && jobDescription}
                          {tailoringSource === 'job-position' && `Title: ${jobPositionTitle}${jobPositionIndustry ? `\nIndustry: ${jobPositionIndustry}` : ''}`}
                          {tailoringSource === 'institution' && `Institution: ${institutionName}${institutionType ? `\nType: ${institutionType}` : ''}${missionAlignment ? `\nMission: ${missionAlignment}` : ''}`}
                          {tailoringSource === 'assessment-questions' && assessmentQuestions}
                        </pre>
                      </div>
                    </div>}
                </div>
              </div>}
            
            <RadioGroup value={tailoringSource} onValueChange={setTailoringSource}>
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'job-description' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="job-description" id="job-description" className="mt-0.5" />
                 <Label htmlFor="job-description" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'job-description' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                     <FileText className="w-4 h-4" />
                   </div>
                   <div>
                     <div className="font-medium">Job Description</div>
                     <div className="text-sm text-muted-foreground">Tailor to a specific job posting</div>
                   </div>
                 </Label>
               </div>
               
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'job-position' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="job-position" id="job-position" className="mt-0.5" />
                 <Label htmlFor="job-position" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'job-position' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Job Position/Industry</div>
                    <div className="text-sm text-muted-foreground">Tailor to a role type or industry</div>
                  </div>
                </Label>
              </div>
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'institution' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="institution" id="institution" className="mt-0.5" />
                 <Label htmlFor="institution" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'institution' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <Building2 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Institution/Organization</div>
                    <div className="text-sm text-muted-foreground">Tailor to a specific company or organization</div>
                  </div>
                </Label>
              </div>
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'revisions' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="revisions" id="revisions" className="mt-0.5" />
                 <Label htmlFor="revisions" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'revisions' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <Settings className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Document Revisions & Updates</div>
                    <div className="text-sm text-muted-foreground">Make revisions and updates to existing documents</div>
                  </div>
                </Label>
              </div>
              
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${tailoringSource === 'assessment-questions' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                 <RadioGroupItem value="assessment-questions" id="assessment-questions" className="mt-0.5" />
                 <Label htmlFor="assessment-questions" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${tailoringSource === 'assessment-questions' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                    <BarChart3 className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-medium">Tailor Answers to Job Assessment Questions</div>
                    <div className="text-sm text-muted-foreground">Optimize for common pre-screening and application questions</div>
                  </div>
                </Label>
              </div>
             </RadioGroup>
              </CardContent>
              
              {/* Continue Button for Step 2 */}
              {currentStep === 2 && isStepComplete(2) && !completedSteps.includes(2) && <CardContent className="pt-0 rounded-b-lg">
                  <div className="flex justify-end">
                    <Button onClick={continueToNextStep} className="bg-green-600 hover:bg-green-700 text-white">
                      Continue to Step 3
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>}
            </CollapsibleContent>
            </Card>
          </Collapsible>



        {/* Step 3: Job Description Input - For job description (swapped 2026-06-13: JD now precedes Documents) */}
        {tailoringSource === 'job-description' && <Collapsible open={stepExpanded[3]} onOpenChange={() => toggleStepExpansion(3)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(3) ? (stepExpanded[3] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[3] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(3) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(3) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 3: Job Description</span>
                          {completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-fade-in">
                              ✅ STEP 3 COMPLETE!
                            </Badge>}
                          {currentStep === 3 && !completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-pulse">
                              ⚡ PLEASE COMPLETE
                            </Badge>}
                        </CardTitle>

                        {/* Show summary when collapsed and completed */}
                          {completedSteps.includes(3) && !stepExpanded[3] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                               <CheckCircle className="w-4 h-4 text-green-500" />
                              Job details ready for tailoring
                            </div>}
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[3] ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

            <CollapsibleContent>
              <CardContent className="pt-0 space-y-6 rounded-b-lg">
                  <RadioGroup value={selectedUploadMethod} onValueChange={handleJobUploadMethodChange}>
                   <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedUploadMethod === 'upload' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                    <RadioGroupItem value="upload" id="upload-job" className="mt-0.5" />
                    <Label htmlFor="upload-job" className="flex items-center gap-3 cursor-pointer flex-1">
                       <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedUploadMethod === 'upload' ? 'bg-green-100 text-green-600' : 'bg-white'}`}>
                       <Upload className="w-4 h-4" />
                     </div>
                     <div>
                       <div className="font-medium">Upload New Job</div>
                       <div className="text-sm text-muted-foreground">Upload a job description file or add via Job Wizard</div>
                     </div>
                   </Label>
                 </div>

                 <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedUploadMethod === 'existing' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                   <RadioGroupItem value="existing" id="existing-job" className="mt-0.5" />
                   <Label htmlFor="existing-job" className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedUploadMethod === 'existing' ? 'bg-green-100 text-green-600' : 'bg-white'}`}>
                       <FileText className="w-4 h-4" />
                     </div>
                     <div>
                       <div className="font-medium">Use Existing Job</div>
                       <div className="text-sm text-muted-foreground">Select from your saved job descriptions</div>
                     </div>
                   </Label>
                 </div>

                 {/* Show existing job selection directly under this option */}
                 {selectedUploadMethod === 'existing' && <div className="ml-12 space-y-4 p-4 bg-white rounded-lg border">
                     <div className="text-sm font-medium">Select Job Description</div>
                     {loadingJobs ? <div className="flex items-center gap-2 p-4 border rounded-lg">
                         <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                         <span className="text-sm text-muted-foreground">Loading your saved jobs...</span>
                       </div> : savedJobs.length > 0 ? <div className="space-y-2">
                          {showJobDropdown && <Select value={selectedJobId} onValueChange={handleJobSelection}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Choose from your saved job descriptions" />
                              </SelectTrigger>
                              <SelectContent className="bg-popover">
                                {savedJobs.map(job => <SelectItem key={job.id} value={job.id}>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{job.jobTitle} at {job.companyName}</span>
                                      <span className="text-sm text-muted-foreground">
                                        Created {new Date(job.dateCreated).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </SelectItem>)}
                              </SelectContent>
                            </Select>}
                           {selectedJobId && !showJobDropdown && <div className="p-4 bg-slate-50 dark:bg-slate-950/20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-950/30 transition-colors" onClick={() => setShowJobDropdown(true)} title="Click to change job">
                               <div className="flex items-center gap-2 mb-2">
                                 <CheckCircle className="w-5 h-5 text-slate-600" />
                                 <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Job Selected:</span>
                               </div>
                               <div className="ml-7 space-y-1">
                                 <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                   {savedJobs.find(j => j.id === selectedJobId)?.jobTitle || 'Selected Job'}
                                 </div>
                                 <div className="text-xs text-slate-700 dark:text-slate-300">
                                   Company: {savedJobs.find(j => j.id === selectedJobId)?.companyName || 'Unknown'}
                                 </div>
                                 <div className="text-xs text-slate-700 dark:text-slate-300">
                                   Created: {savedJobs.find(j => j.id === selectedJobId) ? new Date(savedJobs.find(j => j.id === selectedJobId)!.dateCreated).toLocaleDateString() : ''}
                                 </div>
                              </div>
                            </div>}
                       </div> : <div className="text-center p-8 border rounded-lg">
                         <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                         <p className="text-lg font-medium">No saved job descriptions found</p>
                         <p className="text-muted-foreground mb-4">Create your first job description using the Job Wizard</p>
                         <Button variant="outline" onClick={() => setSelectedUploadMethod('upload')}>
                           Upload New Job
                         </Button>
                       </div>}
                   </div>}

                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedUploadMethod === 'paste' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                  <RadioGroupItem value="paste" id="paste-job" className="mt-0.5" />
                  <Label htmlFor="paste-job" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedUploadMethod === 'paste' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Paste Job Description</div>
                      <div className="text-sm text-muted-foreground">Copy and paste the job description text</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* Show job wizard button for new job */}
              {selectedUploadMethod === 'upload' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Create New Job Description</div>
                  <div className="text-center space-y-4">
                    <div className="p-6 border-2 border-dashed border-primary/25 rounded-lg bg-primary/5">
                      <Target className="w-12 h-12 mx-auto mb-4 text-primary" />
                      <h3 className="text-lg font-semibold text-primary mb-2">Job Wizard</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Use our guided wizard to create a comprehensive job description from a URL or by pasting the job posting
                      </p>
                      <Button onClick={() => setShowJobWizard(true)} className="bg-primary hover:bg-primary/90">
                        <Target className="w-4 h-4 mr-2" />
                        Open Job Wizard
                      </Button>
                    </div>
                  </div>
                </div>}


              {/* Show paste textarea for paste option */}
              {selectedUploadMethod === 'paste' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Paste Job Description</div>
                  <div className="space-y-2">
                    <Label htmlFor="job-description-paste">Job Description Text</Label>
                    <Textarea id="job-description-paste" placeholder="Paste the complete job description here..." value={jobDescription} onChange={e => handleJobDescriptionChange(e.target.value)} rows={8} className="min-h-[200px]" />
                  </div>
                </div>}

                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>}

        {/* Step 4: Select Documents to Tailor - For job description (swapped 2026-06-13: was Step 3, now after JD) */}
        {tailoringSource === 'job-description' && <Collapsible open={stepExpanded[4]} onOpenChange={() => toggleStepExpansion(4)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(4) ? (stepExpanded[4] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[4] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(4) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(4) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 4: Select Documents to Tailor</span>
                          {completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                              ✅ COMPLETED
                            </Badge>}
                          {currentStep === 4 && !completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                              ⚡ ACTIVE
                            </Badge>}
                        </CardTitle>

                        {/* Show summary when collapsed and completed */}
                         {completedSteps.includes(4) && !stepExpanded[4] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                             Documents selected: {selectedDocuments.map(doc => doc === 'resume' ? 'Resume' : doc === 'cover-letter' ? 'Cover Letter' : doc === 'linkedin-profile' ? 'LinkedIn' : doc.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(', ')}
                           </div> : <CardDescription className="text-base">
                            {completedSteps.includes(4) ? "✅ Documents selected" : "Choose which documents to tailor to this job"}
                          </CardDescription>}
                      </div>
                    </div>
                     <div className="flex items-center gap-2">
                       <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${stepExpanded[4] ? 'rotate-180' : ''}`} />
                     </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>

              {/* Only show content when step is not completed OR when completed but expanded for editing */}
              {(!completedSteps.includes(4) || stepExpanded[4]) && <CollapsibleContent className="animate-accordion-down">
                  <CardContent className="p-6 rounded-b-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[{
                          id: 'resume',
                          label: 'Resume',
                          icon: FileText,
                          description: 'Primary application document'
                        }, {
                          id: 'cover-letter',
                          label: 'Cover Letter',
                          icon: FileText,
                          description: 'Personalized introduction letter'
                        }, {
                          id: 'thank-you-letter',
                          label: 'Thank You Letter',
                          icon: FileText,
                          description: 'Post-interview follow-up'
                        }, {
                          id: 'outreach-letter',
                          label: 'Outreach Email',
                          icon: FileText,
                          description: 'Networking introduction'
                        }, {
                          id: 'executive-bio',
                          label: 'Executive Bio',
                          icon: FileText,
                          description: 'Professional summary bio'
                        }, {
                          id: 'linkedin-profile',
                          label: 'LinkedIn Profile',
                          icon: Linkedin,
                          description: 'Social media presence'
                        }, {
                          id: 'solution-asset-document',
                          label: 'Solution Asset Document',
                          icon: FileText,
                          description: 'Strategic solution presentation'
                        }, {
                          id: 'hiring-manager-contact-report',
                          label: 'Hiring Manager Contact Report',
                          icon: FileText,
                          description: 'Decision-maker contact strategy'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          const isLocked = ['solution-asset-document', 'hiring-manager-contact-report'].includes(document.id) && tailoringDepth !== 'deep';
                          return <div key={document.id}>
                       <div className={`group relative p-4 rounded-xl transition-all duration-300 ${isLocked ? 'opacity-60 cursor-not-allowed bg-slate-50 border-2 border-dashed border-slate-300' : `cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`}`} onClick={() => !isLocked && handleDocumentToggle(document.id)}>
                         {isLocked && <Badge variant="default" className="absolute -top-2 -right-2 bg-slate-600 text-white text-[10px] px-2 py-1 font-bold shadow-md">
                             🔒 Deep Tailor only
                           </Badge>}
                         {!isLocked && ['solution-asset-document', 'hiring-manager-contact-report'].includes(document.id) && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-white text-xs px-2 py-1 font-bold shadow-md">
                             NEW
                           </Badge>}
                         <div className="flex items-start space-x-3">
                              <Checkbox id={document.id} checked={isSelected} disabled={isLocked} onCheckedChange={() => !isLocked && handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                           <div className="flex-1 space-y-2">
                             <div className="flex items-center gap-2">
                               <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                               <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                {document.label}
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {document.description}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Solution Asset Career Summary Option - appears directly under solution asset document */}
                      {document.id === 'solution-asset-document' && selectedDocuments.includes('solution-asset-document') && <div className={`mt-2 p-3 rounded-lg border-2 transition-all duration-300 relative ${solutionAssetInSummary ? 'bg-green-50 border-green-500 shadow-md' : 'bg-blue-50 border-blue-300'}`}>
                          <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                            RECOMMENDED
                          </Badge>
                          <div className="flex items-center gap-3">
                            <Checkbox id="solution-asset-summary" checked={solutionAssetInSummary} onCheckedChange={checked => setSolutionAssetInSummary(checked === true)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor="solution-asset-summary" className={`text-sm font-medium cursor-pointer ${solutionAssetInSummary ? 'text-green-900' : 'text-blue-900'}`}>
                              Feature Solution Asset in the Career Summary?
                            </Label>
                          </div>
                           <p className={`text-xs mt-2 ml-6 ${solutionAssetInSummary ? 'text-slate-700' : 'text-blue-700'}`}>
                            Highlight your solution assets and value propositions in the career summary section.
                          </p>
                        </div>}
                    </div>;
                        })}
              </div>
              
              {selectedDocuments.length > 0 && <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-muted">
                  <div className="flex items-center gap-2 text-sm">
                     <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">
                      {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for tailoring
                    </span>
                  </div>
                </div>}

              {/* Tailoring depth */}
              {selectedDocuments.length > 0 && <div className="mt-6 space-y-3">
                <h4 className="font-bold text-slate-800">Choose how deep to tailor</h4>
                <RadioGroup value={tailoringDepth} onValueChange={v => setTailoringDepth(v as 'quick' | 'deep')} className="grid gap-3">
                  <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${tailoringDepth === 'quick' ? 'border-green-500 bg-green-50' : 'border-border bg-white hover:border-muted-foreground'}`}>
                    <RadioGroupItem value="quick" className="mt-1" />
                    <div>
                      <div className="font-bold text-slate-900">⚡ Quick Tailor</div>
                      <div className="text-sm text-muted-foreground">Matches the posting on a surface level only · ~60 seconds</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${tailoringDepth === 'deep' ? 'border-green-500 bg-green-50' : 'border-border bg-white hover:border-muted-foreground'}`}>
                    <RadioGroupItem value="deep" className="mt-1" />
                    <div className="flex-1">
                      <div className="font-bold text-slate-900">🚀 Most POWERFUL Deep Tailor</div>
                      <div className="text-sm text-slate-700 mt-1">
                        Tailored to the job description <span className="font-semibold">AND the company's researched pain points.</span>
                      </div>
                      <div className="text-sm text-slate-700 mt-2">
                        ✅ Unlocks <span className="font-semibold">Hiring Manager Contact Report</span> and <span className="font-semibold">Solution Asset Document</span> — both only available with Deep Tailor.
                      </div>
                      <div className="mt-2 inline-block px-2 py-1 rounded bg-amber-100 text-amber-900 text-xs font-bold">
                        📈 Users see up to 8× more interviews · 3–5 minutes
                      </div>
                    </div>
                  </label>
                </RadioGroup>

                {/* Claude Direct engine: JD-tailoring mode */}
                <div className="mt-5 space-y-3">
                  <h4 className="font-bold text-slate-800">Which mode fits this job?</h4>
                  <RadioGroup value={tailoringMode} onValueChange={v => setTailoringMode(v as 'Full JD' | 'Personalized JD')} className="grid gap-3">
                    <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${tailoringMode === 'Full JD' ? 'border-green-500 bg-green-50' : 'border-border bg-white hover:border-muted-foreground'}`}>
                      <RadioGroupItem value="Full JD" className="mt-1" />
                      <div>
                        <div className="font-bold text-slate-900">Full JD (recommended)</div>
                        <div className="text-sm text-muted-foreground">Only claims what your resume actually proves — nothing on the posting is assumed. Safest and most honest.</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${tailoringMode === 'Personalized JD' ? 'border-green-500 bg-green-50' : 'border-border bg-white hover:border-muted-foreground'}`}>
                      <RadioGroupItem value="Personalized JD" className="mt-1" />
                      <div>
                        <div className="font-bold text-slate-900">Personalized JD</div>
                        <div className="text-sm text-muted-foreground">You confirm you have actually done every responsibility, requirement, and skill on the posting. The resume mirrors the JD as closely as possible.</div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                {tailoringDepth === 'deep' && <div className="mt-2 p-4 rounded-xl border-2 border-blue-300 bg-blue-50/60 space-y-3">
                  <div className="text-sm text-slate-700">
                    Deep Tailor needs a <span className="font-semibold">Tailoring Report</span> — a JobIntel 360-style brief on the company's pain points and role requirements. We auto-generate it from your resume + the job, or you can upload your own.
                  </div>

                  {/* Primary: Auto-Generate */}
                  <button
                    type="button"
                    onClick={handleAutoGenerateTailoringReport}
                    disabled={tailoringReportGenerating}
                    className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
                  >
                    {tailoringReportGenerating ? <>⏳ Generating Tailoring Report…</> : <>⚡ Auto-Generate Tailoring Report (recommended)</>}
                  </button>

                  <div className="text-center text-xs text-muted-foreground">— or upload your own —</div>

                  <DocumentUpload
                    onUploadComplete={(text, filename) => { setTailoringReportText(text || ''); setTailoringReportFilename(filename || 'Tailoring Report'); }}
                    title="Upload Tailoring Report"
                    description="PDF or Word — a JobIntel 360 report or any job/company research"
                    acceptedTypes={['.pdf', '.doc', '.docx', '.txt']}
                  />

                  {tailoringReportText && <div className="flex items-start gap-2 text-sm text-green-800 bg-green-50 border-2 border-green-300 rounded-lg p-3">
                    <CheckCircle className="w-5 h-5 shrink-0 text-green-600" />
                    <div>
                      <div className="font-bold">Tailoring Report ready: {tailoringReportFilename}</div>
                      <div className="text-xs text-green-700 mt-0.5">{tailoringReportText.length.toLocaleString()} chars — will feed the resume tailoring AND the Hiring Manager Contact + Solution Asset documents.</div>
                    </div>
                  </div>}

                  {/* Preview panel — surfaces the BIGGEST pain points and the
                      Apollo-sourced hiring managers BEFORE the user clicks
                      Tailor. Renders only after the auto-generated report
                      populated the structured state (uploaded reports won't
                      have it; uploaded users see only the green strip). */}
                  {(tailoringReportPainPointsStructured.length > 0 || hiringManagerCandidates.length > 0) && (
                    <div className="mt-3 space-y-4">
                      {/* Pain Points */}
                      {tailoringReportPainPointsStructured.length > 0 && (
                        <div className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Target className="w-5 h-5 text-amber-700" />
                            <h5 className="font-bold text-amber-900 text-sm uppercase tracking-wide">
                              Biggest Pain Points We'll Address ({tailoringReportPainPointsStructured.length})
                            </h5>
                          </div>
                          <p className="text-xs text-amber-900/70 mb-3">
                            Review what we'll weave into your resume. Click <span className="font-semibold">Auto-Generate</span> again to refresh, or upload a different Tailoring Report.
                          </p>
                          <div className="space-y-3">
                            {tailoringReportPainPointsStructured.slice(0, 5).map((p: any, i: number) => (
                              <div key={i} className="rounded-lg border border-amber-200 bg-white p-3">
                                <div className="flex items-start gap-2">
                                  <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center">
                                    {i + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm text-slate-900">
                                      {p.problem_title || 'Untitled pain point'}
                                    </div>
                                    {p.problem_description && (
                                      <div className="text-xs text-slate-700 mt-1 leading-relaxed">
                                        {p.problem_description}
                                      </div>
                                    )}
                                    {p.recommendation && (
                                      <div className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                                        <span className="font-semibold">Recommendation:</span> {p.recommendation}
                                      </div>
                                    )}
                                    {(p.coverage_rating || p.resume_coverage) && (
                                      <div className="mt-2 flex items-center gap-2 text-[11px]">
                                        <span className="font-semibold text-slate-600">Current resume coverage:</span>
                                        <span className={`px-2 py-0.5 rounded-full font-bold ${
                                          (p.coverage_rating === 'Strong' || p.resume_coverage === 'Directly Addressed') ? 'bg-green-100 text-green-800' :
                                          (p.coverage_rating === 'Moderate' || p.resume_coverage === 'Partially Addressed') ? 'bg-amber-100 text-amber-900' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {p.coverage_rating || p.resume_coverage}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Hiring Manager Candidates */}
                      {hiringManagerCandidates.length > 0 && (
                        <div className="rounded-xl border-2 border-blue-300 bg-blue-50/60 p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-5 h-5 text-blue-700" />
                            <h5 className="font-bold text-blue-900 text-sm uppercase tracking-wide">
                              Likely Hiring Managers ({hiringManagerCandidates.length}) — from Apollo
                            </h5>
                          </div>
                          <p className="text-xs text-blue-900/70 mb-3">
                            Real people in the role's decision-maker ladder at the target company. These show up in your Hiring Manager Contact Report after tailoring.
                          </p>
                          <div className="grid sm:grid-cols-2 gap-2">
                            {hiringManagerCandidates.slice(0, 6).map((c: any, i: number) => (
                              <div key={i} className="rounded-lg border border-blue-200 bg-white p-3">
                                <div className="font-bold text-sm text-slate-900">
                                  {c.name || '(name not on file)'}
                                </div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                  {c.title || '(title not on file)'}
                                </div>
                                <div className="mt-2 space-y-1 text-[11px]">
                                  {c.linkedin_url && (
                                    <a
                                      href={c.linkedin_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block text-blue-700 hover:underline truncate"
                                    >
                                      🔗 {c.linkedin_url.replace(/^https?:\/\//, '')}
                                    </a>
                                  )}
                                  {c.email && (
                                    <div className="flex items-center gap-1.5 text-slate-700">
                                      <span>✉</span>
                                      <span className="truncate">{c.email}</span>
                                      {c.email_status && (
                                        <span className={`shrink-0 px-1.5 py-0 rounded text-[10px] font-bold ${
                                          c.email_status === 'verified' ? 'bg-green-100 text-green-800' :
                                          c.email_status === 'guessed'  ? 'bg-amber-100 text-amber-800' :
                                                                          'bg-slate-100 text-slate-700'
                                        }`}>
                                          {c.email_status}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Collapsible: full report text — for power users who
                          want to read everything before tailoring. */}
                      <details className="rounded-lg border border-slate-200 bg-slate-50/60">
                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-700 hover:text-slate-900">
                          View full Tailoring Report ({tailoringReportText.length.toLocaleString()} chars)
                        </summary>
                        <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-mono p-3 max-h-80 overflow-y-auto leading-relaxed border-t border-slate-200">
                          {tailoringReportText}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>}
              </div>}
            </CardContent>
            
            {/* Finish Selection Button for Step 4 - Always show when step 4 is expanded and not completed */}
            {stepExpanded[4] && !step4Completed && <CardContent className="pt-0 rounded-b-lg">
                <div className="flex justify-center">
                  <Button onClick={() => {
                          markStepComplete(4, true);
                          toast({
                            title: "Document selection completed!",
                            description: `${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''} selected for tailoring.`
                          });
                        }} className="bg-green-600 hover:bg-green-700 text-white" disabled={selectedDocuments.length === 0}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finished Selecting Documents
                  </Button>
                </div>
              </CardContent>}
          </CollapsibleContent>}
        </Card>
      </Collapsible>}

        {/* Step 3: Select Documents to Tailor - For job position */}
        {tailoringSource === 'job-position' && <Collapsible open={stepExpanded[3]} onOpenChange={() => toggleStepExpansion(3)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(3) ? (stepExpanded[3] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[3] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(3) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(3) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <FileText className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 3: Select Documents to Tailor</span>
                          {completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-fade-in">
                              ✅ STEP 3 COMPLETE!
                            </Badge>}
                          {currentStep === 3 && !completedSteps.includes(3) && <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm px-5 py-2 rounded-full shadow-lg border-0 font-bold animate-pulse">
                              ⚡ PLEASE COMPLETE
                            </Badge>}
                        </CardTitle>
                        
                        {/* Show summary when collapsed and completed */}
                         {completedSteps.includes(3) && !stepExpanded[3] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                             Documents selected: {selectedDocuments.map(doc => doc === 'resume' ? 'Resume' : doc === 'cover-letter' ? 'Cover Letter' : doc === 'linkedin-profile' ? 'LinkedIn' : doc.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')).join(', ')}
                           </div> : <CardDescription className="text-base">
                            {completedSteps.includes(3) ? "✅ Documents selected" : "Choose which documents to tailor for this position"}
                          </CardDescription>}
                      </div>
                    </div>
                     <div className="flex items-center gap-2">
                       <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${stepExpanded[3] ? 'rotate-180' : ''}`} />
                     </div>
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              {/* Only show content when step is not completed OR when completed but expanded for editing */}
              {(!completedSteps.includes(3) || stepExpanded[3]) && <CollapsibleContent className="animate-accordion-down">
                  <CardContent className="p-6 rounded-b-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[{
                          id: 'resume',
                          label: 'Resume',
                          icon: FileText,
                          description: 'Primary application document'
                        }, {
                          id: 'cover-letter',
                          label: 'Cover Letter',
                          icon: FileText,
                          description: 'Personalized introduction letter'
                        }, {
                          id: 'thank-you-letter',
                          label: 'Thank You Letter',
                          icon: FileText,
                          description: 'Post-interview follow-up'
                        }, {
                          id: 'outreach-letter',
                          label: 'Outreach Email',
                          icon: FileText,
                          description: 'Networking introduction'
                        }, {
                          id: 'executive-bio',
                          label: 'Executive Bio',
                          icon: FileText,
                          description: 'Professional summary bio'
                        }, {
                          id: 'linkedin-profile',
                          label: 'LinkedIn Profile',
                          icon: Linkedin,
                          description: 'Social media presence'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          const isLocked = ['solution-asset-document', 'hiring-manager-contact-report'].includes(document.id) && tailoringDepth !== 'deep';
                          return <div key={document.id}>
                       <div className={`group relative p-4 rounded-xl transition-all duration-300 ${isLocked ? 'opacity-60 cursor-not-allowed bg-slate-50 border-2 border-dashed border-slate-300' : `cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`}`} onClick={() => !isLocked && handleDocumentToggle(document.id)}>
                         {isLocked && <Badge variant="default" className="absolute -top-2 -right-2 bg-slate-600 text-white text-[10px] px-2 py-1 font-bold shadow-md">
                             🔒 Deep Tailor only
                           </Badge>}
                         {!isLocked && ['solution-asset-document', 'hiring-manager-contact-report'].includes(document.id) && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-primary to-primary/80 text-white text-xs px-2 py-1 font-bold shadow-md">
                             NEW
                           </Badge>}
                         <div className="flex items-start space-x-3">
                              <Checkbox id={document.id} checked={isSelected} disabled={isLocked} onCheckedChange={() => !isLocked && handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                           <div className="flex-1 space-y-2">
                             <div className="flex items-center gap-2">
                               <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                               <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                {document.label}
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {document.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>;
                        })}
              </div>
              
              {selectedDocuments.length > 0 && <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-muted">
                  <div className="flex items-center gap-2 text-sm">
                     <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">
                      {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for tailoring
                    </span>
                  </div>
                </div>}
                
                {/* Finish Selection Button */}
                {!step3Completed && selectedDocuments.length > 0 && <div className="mt-6">
                  <div className="flex justify-center">
                    <Button onClick={() => {
                            markStepComplete(3, true);
                            toast({
                              title: "Document selection completed!",
                              description: `${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''} selected for tailoring.`
                            });
                          }} className="bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Finished Selecting Documents
                    </Button>
                  </div>
                </div>}
            </CardContent>
          </CollapsibleContent>}
        </Card>
      </Collapsible>}

        {/* Job Assessment Questions Input - Only show for assessment-questions option */}
        {tailoringSource === 'assessment-questions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="pb-4 bg-white/90 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-slate-200 to-slate-300/70 text-slate-700">
                  <BarChart3 className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Step 3: Job Assessment Questions</CardTitle>
                  <CardDescription className="text-base">Upload or paste the assessment questions you need to answer</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 rounded-b-lg">
              <RadioGroup value={selectedAssessmentMethod} onValueChange={setSelectedAssessmentMethod}>
                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedAssessmentMethod === 'upload' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                  <RadioGroupItem value="upload" id="upload-assessment" className="mt-0.5" />
                  <Label htmlFor="upload-assessment" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedAssessmentMethod === 'upload' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                      <Upload className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Upload Assessment File</div>
                      <div className="text-sm text-muted-foreground">Upload a document containing the assessment questions</div>
                    </div>
                  </Label>
                </div>

                <div className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer group ${selectedAssessmentMethod === 'paste' ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50 bg-white hover:bg-white'}`}>
                  <RadioGroupItem value="paste" id="paste-assessment" className="mt-0.5" />
                  <Label htmlFor="paste-assessment" className="flex items-center gap-3 cursor-pointer flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedAssessmentMethod === 'paste' ? 'bg-green-100 text-green-600' : 'bg-white group-hover:bg-primary/10'}`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-medium">Paste Assessment Questions</div>
                      <div className="text-sm text-muted-foreground">Copy and paste the assessment questions text</div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>

              {/* Show upload area for upload option */}
              {selectedAssessmentMethod === 'upload' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Upload Assessment Questions</div>
                  <div className="text-center space-y-4">
                    <div className="p-6 border-2 border-dashed border-primary/25 rounded-lg bg-primary/5">
                      <Upload className="w-12 h-12 mx-auto mb-4 text-primary" />
                      <h3 className="text-lg font-semibold text-primary mb-2">Upload Document</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Upload a PDF, Word document, or text file containing the assessment questions
                      </p>
                      <DocumentUpload onUploadComplete={(text, filename) => {
                          setAssessmentQuestions(text);
                          markStepComplete(4, true); // Mark step 4 as completed when questions are uploaded
                          toast({
                            title: "Assessment questions uploaded!",
                            description: "Questions extracted and ready for resume optimization."
                          });
                        }} title="Upload Assessment Questions" description="Upload the assessment questions document" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
                    </div>
                  </div>
                </div>}

              {/* Show paste textarea for paste option */}
              {selectedAssessmentMethod === 'paste' && <div className="ml-8 space-y-4 p-4 bg-white rounded-lg border">
                  <div className="text-sm font-medium">Paste Assessment Questions</div>
                  <div className="space-y-2">
                    <Label htmlFor="assessment-questions-paste">Assessment Questions</Label>
                    <Textarea id="assessment-questions-paste" placeholder="Paste all the assessment questions here. Include any pre-screening questions, application questions, or competency-based questions you need to answer..." value={assessmentQuestions} onChange={e => {
                        setAssessmentQuestions(e.target.value);
                        // Mark step 4 as completed when assessment questions are provided
                        if (e.target.value.trim()) {
                          markStepComplete(4, true);
                        } else {
                          markStepComplete(4, false);
                        }
                      }} rows={8} className="min-h-[200px]" />
                    <p className="text-xs text-muted-foreground">
                      Include all questions such as: "Why do you want this role?", "What are your salary expectations?", 
                      "Describe your relevant experience", etc.
                    </p>
                  </div>
                </div>}

              {/* Show success indicator when questions are added */}
              {assessmentQuestions.trim() && <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      Assessment questions ready ({assessmentQuestions.split('\n').filter(line => line.trim()).length} lines)
                    </span>
                  </div>
                </div>}
            </CardContent>
          </Card>}

        {/* Additional Instructions - Show for assessment questions */}
        {tailoringSource === 'assessment-questions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="bg-white/90 rounded-t-2xl">
              <CardTitle>Step 4: Additional Instructions (Optional)</CardTitle>
              <CardDescription>Include any specific guidelines for answering the assessment questions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="assessment-additional-instructions">Additional Instructions (Optional)</Label>
                <Textarea id="assessment-additional-instructions" placeholder="Include any specific tone, style, or approach preferences for addressing the assessment questions..." value={additionalInstructions} onChange={e => setAdditionalInstructions(e.target.value)} rows={4} />
              </div>
            </CardContent>
          </Card>}

        {/* Upload Additional Documents - Show for assessment questions */}
        {tailoringSource === 'assessment-questions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="bg-white/90 rounded-t-2xl">
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Step 5: Additional Documents to Include (Optional)
              </CardTitle>
              <CardDescription>Upload supporting documents, notes, or any additional context</CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentUpload onUploadComplete={handleAdditionalDocumentUpload} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
              
              {/* Show uploaded documents */}
              {additionalDocuments.length > 0 && <div className="mt-4 space-y-2">
                  <div className="text-sm font-medium">Uploaded Documents:</div>
                  {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{doc.file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({doc.extractedText.length} characters extracted)
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-auto p-1 text-red-600 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>)}
                </div>}
            </CardContent>
          </Card>}

        {/* Step 5: Additional Instructions Question - Only show for job description */}
        {tailoringSource === 'job-description' && completedSteps.includes(4) && <Collapsible open={stepExpanded[5]} onOpenChange={() => toggleStepExpansion(5)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(5) ? (stepExpanded[5] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[5] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(5) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(5) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 5: Additional Instructions</span>
                        {completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 5 && !completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(5) && !stepExpanded[5] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                             <CheckCircle className="w-4 h-4 text-green-500" />
                            {additionalInstructionsAnswer === 'yes' ? 'Additional instructions will be provided' : 'No additional instructions needed'}
                          </div>}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[5] ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0 bg-white rounded-b-lg">
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 mx-[84px]">Do you want to add any additional instructions, documents or notes to help tailor your resume?</h3>
                    <p className="text-muted-foreground mb-6">This could include specific requirements, company culture notes, or additional context</p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => {
                            setAdditionalInstructionsAnswer('yes');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'yes' ? 'default' : 'outline'} className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white border-green-600">
                      Yes, I have additional instructions
                    </Button>
                     <Button onClick={() => {
                            setAdditionalInstructionsAnswer('no');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'no' ? 'default' : 'outline'} className={`px-4 py-2 text-sm ${additionalInstructionsAnswer === 'no' ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'}`}>
                      No, proceed with current information
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>}

        {/* Step 6: Additional Instructions Content (only if Step 5 = YES and NOT job-position) */}
        {tailoringSource !== 'job-position' && additionalInstructionsAnswer === 'yes' && <Collapsible open={stepExpanded[6]} onOpenChange={() => toggleStepExpansion(6)}>
            <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
              <CollapsibleTrigger asChild>
                <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(6) ? (stepExpanded[6] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[6] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(6) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                         {completedSteps.includes(6) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                       </div>
                      <div className="flex-1">
                        <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 6: Additional Instructions & Documents</span>
                          {completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                              ✅ COMPLETED
                            </Badge>}
                          {currentStep === 6 && !completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                              ⚡ ACTIVE
                            </Badge>}
                        </CardTitle>
                        
                        {/* Show summary when collapsed and completed */}
                          {completedSteps.includes(6) && !stepExpanded[6] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              Additional instructions and documents saved
                            </div>}
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[6] ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <CardContent className="pt-0 space-y-4 rounded-b-lg">
                <div className="space-y-2">
                  <Label htmlFor="additional-instructions-text">Additional Instructions</Label>
                  <Textarea id="additional-instructions-text" placeholder="Provide any specific guidelines, requirements, or preferences for the tailoring process..." value={additionalInstructionsText} onChange={e => setAdditionalInstructionsText(e.target.value)} rows={4} />
                </div>
                
                <div className="space-y-2">
                  <Label>Additional Documents (Optional)</Label>
                  <DocumentUpload onUploadComplete={(text, filename, fileUrl, originalFile) => {
                          // Capture BOTH the file and the extracted text so
                          // the contents flow into the tailor-resume call.
                          const file = originalFile || new File([text], filename, { type: 'text/plain' });
                          setAdditionalInstructionsDocuments(prev => [...prev, {
                            file,
                            extractedText: text || '',
                            filename: filename || file.name || 'document',
                          }]);
                        }} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
                </div>
                
                <div className="flex justify-center">
                  <Button onClick={() => {
                          markStepComplete(6, true);
                          toast({
                            title: "Additional instructions saved!",
                            description: "Your additional requirements have been noted."
                          });
                        }} className="bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Done with Additional Instructions
                  </Button>
                </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>}

        {/* Client Worksheets — the skill file's REQUIRED INPUTS. Optional to
            fill, but when present every source-material scan uses them. */}
        <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden bg-gradient-to-br from-amber-50/60 to-white border-amber-200/70">
          <Collapsible open={showWorksheets} onOpenChange={setShowWorksheets}>
            <CollapsibleTrigger asChild>
              <CardHeader className={`cursor-pointer ${showWorksheets ? 'rounded-t-2xl' : 'rounded-2xl'} bg-white/90`}>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-amber-100 to-amber-200/70 text-amber-700 text-2xl">📋</div>
                    <div>
                      <div className="text-xl font-semibold text-foreground">Client Worksheets</div>
                      <div className="text-sm text-muted-foreground font-normal">
                        New Client Worksheet + Metrics Worksheet — the skill's required source materials
                        {(newClientWorksheet.trim() || quantWorksheet.trim()) && <span className="ml-2 text-emerald-700 font-semibold">✓ added</span>}
                      </div>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="p-6 space-y-5">
                <div>
                  <Label htmlFor="new-client-worksheet" className="font-semibold">New Client Worksheet</Label>
                  <Textarea id="new-client-worksheet" value={newClientWorksheet} onChange={(e) => setNewClientWorksheet(e.target.value)}
                    placeholder="Paste the client's completed New Client / Career Transition Worksheet here…" className="mt-2 min-h-[140px]" />
                </div>
                <div>
                  <Label htmlFor="quant-worksheet" className="font-semibold">Metrics / Quantification Worksheet</Label>
                  <Textarea id="quant-worksheet" value={quantWorksheet} onChange={(e) => setQuantWorksheet(e.target.value)}
                    placeholder="Paste the client's completed Metrics Questions Worksheet here…" className="mt-2 min-h-[140px]" />
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {tailoringSource === 'job-description' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <Collapsible open={showAdvancedOptions} onOpenChange={setShowAdvancedOptions}>
              <CollapsibleTrigger asChild>
                <CardHeader className={`cursor-pointer ${showAdvancedOptions ? 'rounded-t-2xl' : 'rounded-2xl'} bg-white/90`}>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md bg-gradient-to-br from-slate-200 to-slate-300/70 text-slate-700">
                        <Settings className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-foreground">Advanced Tailoring Options</div>
                        <div className="text-sm text-muted-foreground font-normal">Fine-tune your resume tailoring preferences</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showAdvancedOptions ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <CardContent className="p-8 space-y-10 rounded-b-lg">
                  
                  {/* Career Summary Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-muted/50 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {[{
                            id: "feature-solution-asset",
                            checked: featureSolutionAsset,
                            onChange: setFeatureSolutionAsset,
                            title: "Feature Solution Asset in Summary",
                            description: "Highlight your solution assets and value propositions in the career summary",
                            recommended: true
                          }, {
                            id: "mention-company",
                            checked: mentionCompanyName,
                            onChange: setMentionCompanyName,
                            title: "Company Name Integration",
                            description: "Naturally weave the target company name into your professional summary"
                          }, {
                            id: "mention-mission",
                            checked: mentionCompanyMission,
                            onChange: setMentionCompanyMission,
                            title: "Mission Alignment",
                            description: "Reference the company's mission statement to show cultural fit"
                          }, {
                            id: "mention-education",
                            checked: mentionEducation,
                            onChange: setMentionEducation,
                            title: "Education Highlighting",
                            description: "Emphasize relevant education credentials in the summary"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'} ${option.recommended ? 'relative' : ''}`}>
                          {option.recommended && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                              RECOMMENDED
                            </Badge>}
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Advanced LinkedIn Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Linkedin className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Advanced LinkedIn Options</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: "linkedin-optimization",
                            checked: false,
                            onChange: () => {
                              // Navigate to LinkedIn Wizard
                              window.open('/linkedin-wizard', '_blank');
                            },
                            title: "Get Advanced LinkedIn Optimization",
                            description: "Optimize your LinkedIn profile with our Wizard for 25x the results. Takes 3-5 minutes",
                            clickable: true
                          }].map(option => <div key={option.id} className="group relative p-4 rounded-xl border border-border bg-card hover:bg-muted/30 hover:border-primary/30 transition-all duration-300">
                          <div className="flex items-start space-x-3">
                             {option.clickable ? <Button size="default" className="mt-1 bg-green-600 hover:bg-green-700 px-4 py-2" onClick={() => option.onChange()}>
                                 Open LinkedIn Wizard
                               </Button> : <Checkbox id={option.id} checked={option.checked} onCheckedChange={() => option.onChange()} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />}
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {option.title}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Professional Experience Framework */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a proven framework to structure your experience bullet points for maximum impact
                      </p>
                      <RadioGroup value={workExperienceFramework} onValueChange={setWorkExperienceFramework} className="space-y-3">
                        {[{
                              value: "star",
                              title: "STAR Method",
                              description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                            }, {
                              value: "par",
                              title: "PAR Method",
                              description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                            }, {
                              value: "arta",
                              title: "ARTA Method",
                              description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                            }, {
                              value: "cosr",
                              title: "COSR Method",
                              description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                            }, {
                              value: "none",
                              title: "Original Format",
                              description: "Preserve your existing bullet point structure without reformatting"
                            }].map(method => <div key={method.value} className={`group p-4 rounded-xl border transition-all duration-300 ${workExperienceFramework === method.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={method.value} id={method.value} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={method.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {method.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Additional Experience Optimization */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Target className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Content Optimization</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: "transferable-skills",
                            checked: transferableSkillsFocus,
                            onChange: setTransferableSkillsFocus,
                            title: "Transferable Skills Focus",
                            description: "Identify and highlight top 5 transferable hard skills and 3 key responsibilities that align with your target role"
                          }, {
                            id: "skill-relevance",
                            checked: skillRelevanceOptimization,
                            onChange: setSkillRelevanceOptimization,
                            title: "Skill Relevance Optimization",
                            description: "Remove bullet points that don't demonstrate transferable skills or target job requirements"
                          }, {
                            id: "irrelevant-filtering",
                            checked: irrelevantJobFiltering,
                            onChange: setIrrelevantJobFiltering,
                            title: "Irrelevant Experience Filtering",
                            description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Professional Branding Style */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-purple-400/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Professional Branding Style</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a branding style that matches your career level and target industry for maximum impact
                      </p>
                      <RadioGroup value={brandingStyle} onValueChange={setBrandingStyle} className="space-y-3">
                        {[{
                              value: "executive",
                              emoji: "🔹",
                              title: "Executive Branding Style",
                              description: "Elevated, authoritative, board-level tone with strategic language and leadership impact focus",
                              highlights: ["Title-heavy, 3rd person, factual structure", "No clichés or soft skills", "Enterprise-wide transformation language"],
                              bestFor: "C-suite and leadership candidates looking to position themselves as board-ready"
                            }, {
                              value: "plain-talk",
                              emoji: "🔹",
                              title: "Plain Talk Professionalism",
                              description: "Direct, simple, clear communication at a 6th–9th grade reading level",
                              highlights: ["Avoids buzzwords and abstract terms", "Focuses on actual accomplishments", "Easy to follow and trust"],
                              bestFor: "Operations, healthcare, education, customer service professionals"
                            }, {
                              value: "ats-optimized",
                              emoji: "🔹",
                              title: "ATS-Optimized Style",
                              description: "Keyword-dense, formulaic approach designed to pass Applicant Tracking Systems",
                              highlights: ["Heavy on job title keywords and tools", "No soft skills or fluff", "Compact, scannable format"],
                              bestFor: "IT, engineering, finance, logistics roles where ATS scanning is strict"
                            }, {
                              value: "story-snapshot",
                              emoji: "🔹",
                              title: "Story Snapshot Style",
                              description: "Narrative, semi-conversational tone with compelling transformation stories",
                              highlights: ["Hook in first sentence with real results", "Concrete numbers and transformation stories", "Clear positioning statement"],
                              bestFor: "Career changers, creatives, entrepreneurs with strong pivot stories"
                            }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${brandingStyle === style.value ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-purple-500/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={style.value} id={style.value} className="mt-1 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={style.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-purple-600 transition-colors flex items-center gap-2">
                                  <span>{style.emoji}</span>
                                  {style.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                                  {style.description}
                                </p>
                                <div className="space-y-1">
                                  <p className="text-xs font-medium text-foreground">Highlights:</p>
                                  <ul className="text-xs text-muted-foreground space-y-0.5 ml-2">
                                    {style.highlights.map((highlight, index) => (
                                      <li key={index} className="flex items-start gap-1">
                                        <span className="text-purple-500 font-medium">•</span>
                                        {highlight}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="mt-2 pt-2 border-t border-border/50">
                                  <p className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Best for: {style.bestFor}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Empty Template Selection section removed - template choice lives on the results page (Docmosis picker). */}

                  {/* Sections to Tailor */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Specific Resume Sections to Tailor</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-center space-x-3">
                            <Checkbox id={`section-${section.toLowerCase().replace(' ', '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor={`section-${section.toLowerCase().replace(' ', '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                              {section}
                            </Label>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Career Transition Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Transition Support</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'career-transition',
                            label: 'Career/Industry Transition',
                            description: 'Highlight transferable skills while minimizing gaps in direct experience for career pivots.'
                          }, {
                            id: 'military-civilian',
                            label: 'Military to Civilian Transition',
                            description: 'Translate military terminology and achievements into civilian-friendly language.'
                          }, {
                            id: 'relevancy-statements',
                            label: 'Job Relevancy Statements',
                            description: 'Add connecting statements explaining how past roles relate to your target position.'
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${careerTransitionOptions.includes(option.label) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={careerTransitionOptions.includes(option.label)} onCheckedChange={() => handleCareerTransitionToggle(option.label)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.label}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Quantification Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Add Achievements & Quantification</h3>
                    </div>
                    <RadioGroup value={quantificationLevel} onValueChange={setQuantificationLevel} className="space-y-3">
                      {[{
                            value: "basic",
                            title: "Basic Quantification",
                            description: "Quick yes/no questions to add numbers and metrics to your achievements (30-60 seconds)",
                            time: "Quick Setup"
                          }, {
                            value: "advanced",
                            title: "Advanced Quantification",
                            description: "Detailed open-ended questions for comprehensive metrics and impact analysis (5-8 minutes)",
                            time: "Comprehensive"
                          }].map(level => <div key={level.value} className={`group p-4 rounded-xl border transition-all duration-300 ${quantificationLevel === level.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={level.value} id={level.value} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={level.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {level.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {level.time}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {level.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                  {/* Writing Styles */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Resume Writing Styles & Formats</h3>
                    </div>
                    <RadioGroup value={writingStyle} onValueChange={setWritingStyle} className="space-y-3">
                      {[{
                            value: "chronological",
                            title: "Chronological Resume",
                            description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
                            tag: "Default"
                          }, {
                            value: "functional",
                            title: "Functional Resume",
                            description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
                            tag: "Career Change"
                          }, {
                            value: "combination",
                            title: "Combination (Hybrid) Resume",
                            description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
                            tag: "Balanced"
                          }, {
                            value: "targeted",
                            title: "Targeted Resume",
                            description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
                            tag: "Focused"
                          }, {
                            value: "executive",
                            title: "Executive Resume",
                            description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
                            tag: "Leadership"
                          }, {
                            value: "creative",
                            title: "Creative Resume",
                            description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
                            tag: "Design"
                          }, {
                            value: "federal",
                            title: "Federal Resume",
                            description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
                            tag: "Government"
                          }, {
                            value: "academic",
                            title: "Academic / CV",
                            description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
                            tag: "Academic"
                          }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${writingStyle === style.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={style.value} id={style.value} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={style.value} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {style.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {style.tag}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {style.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}


        {/* Step 4: Target Position Details - For job position */}
        {tailoringSource === 'job-position' && completedSteps.includes(3) && <Collapsible open={stepExpanded[4]} onOpenChange={() => toggleStepExpansion(4)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(4) ? (stepExpanded[4] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[4] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(4) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(4) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Briefcase className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 4: Target Position Details</span>
                        {completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 4 && !completedSteps.includes(4) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                       {completedSteps.includes(4) && !stepExpanded[4] ? <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                           {jobPositionTitle ? `Position: ${jobPositionTitle}${careerLevel ? ` (${careerLevel})` : ''}${jobPositionIndustry ? ` - ${jobPositionIndustry}` : ''}` : 'Position details configured'}
                         </div> : <CardDescription className="text-base">
                          {completedSteps.includes(4) ? "✅ Target position configured" : "Tell us about the role you're targeting"}
                        </CardDescription>}
                    </div>
                  </div>
                   <div className="flex items-center gap-2">
                     <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[4] ? 'rotate-180' : ''}`} />
                   </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            {/* Only show content when step is not completed OR when completed but expanded for editing */}
            {(!completedSteps.includes(4) || stepExpanded[4]) && <CollapsibleContent className="animate-accordion-down">
                <div className="space-y-6 rounded-b-3xl pt-8 px-8 pb-8 bg-gradient-to-b from-white to-gray-50/50">
                  
                  {/* Card 1: Job Position Title */}
                  <Card className="shadow-md border-2 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50">
                    <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                      <CardTitle className="text-lg flex items-center gap-3">
                        <Target className={`w-5 h-5 ${jobPositionTitle ? 'text-green-600' : 'text-blue-600'}`} />
                        <span className="font-bold text-slate-800">Job Position Title</span>
                        <span className="text-destructive">*</span>
                        {jobPositionTitle && <Badge className="bg-green-600 text-white text-xs border-0">SELECTED</Badge>}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {jobPositionTitle ? `Selected: ${jobPositionTitle}` : 'What role are you applying for?'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4">
                        <div className="relative">
                          <Input 
                            id="job-position-title" 
                            placeholder="e.g., Software Engineer, Marketing Manager, Financial Analyst" 
                            value={jobPositionTitle} 
                            onChange={e => setJobPositionTitle(e.target.value)}
                            required 
                            className={`text-base py-3 px-4 rounded-xl border-2 transition-all ${jobPositionTitle ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200 shadow-sm' : 'border-gray-200 focus:border-primary focus:ring-primary/20'}`} 
                          />
                        </div>
                        
                        <div className="relative">
                          <Select value={jobPositionTitle} onValueChange={setJobPositionTitle}>
                            <SelectTrigger className={`py-3 px-4 rounded-xl border-2 transition-all ${jobPositionTitle ? 'bg-green-50 border-green-300 shadow-sm' : 'bg-muted/30 border-gray-200 hover:border-primary/50'}`}>
                              <SelectValue placeholder="💡 Or choose from comprehensive job titles list" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 bg-background border shadow-lg z-50 rounded-xl">
                              {/* A-C */}
                              <SelectItem value="Account Executive">Account Executive</SelectItem>
                              <SelectItem value="Account Manager">Account Manager</SelectItem>
                              <SelectItem value="Accountant">Accountant</SelectItem>
                              <SelectItem value="Administrative Assistant">Administrative Assistant</SelectItem>
                              <SelectItem value="AI Engineer">AI Engineer</SelectItem>
                              <SelectItem value="Android Developer">Android Developer</SelectItem>
                              <SelectItem value="Architect">Architect</SelectItem>
                              <SelectItem value="Art Director">Art Director</SelectItem>
                              <SelectItem value="Auditor">Auditor</SelectItem>
                              <SelectItem value="Backend Developer">Backend Developer</SelectItem>
                              <SelectItem value="Brand Manager">Brand Manager</SelectItem>
                              <SelectItem value="Business Analyst">Business Analyst</SelectItem>
                              <SelectItem value="Business Development Manager">Business Development Manager</SelectItem>
                              <SelectItem value="CEO">CEO</SelectItem>
                              <SelectItem value="CFO">CFO</SelectItem>
                              <SelectItem value="CIO">CIO</SelectItem>
                              <SelectItem value="CMO">CMO</SelectItem>
                              <SelectItem value="COO">COO</SelectItem>
                              <SelectItem value="CTO">CTO</SelectItem>
                              <SelectItem value="Chief Data Officer">Chief Data Officer</SelectItem>
                              <SelectItem value="Chief Product Officer">Chief Product Officer</SelectItem>
                              <SelectItem value="Civil Engineer">Civil Engineer</SelectItem>
                              <SelectItem value="Cloud Architect">Cloud Architect</SelectItem>
                              <SelectItem value="Cloud Engineer">Cloud Engineer</SelectItem>
                              <SelectItem value="Community Manager">Community Manager</SelectItem>
                              <SelectItem value="Compliance Manager">Compliance Manager</SelectItem>
                              <SelectItem value="Consultant">Consultant</SelectItem>
                              <SelectItem value="Content Creator">Content Creator</SelectItem>
                              <SelectItem value="Content Manager">Content Manager</SelectItem>
                              <SelectItem value="Content Strategist">Content Strategist</SelectItem>
                              <SelectItem value="Creative Director">Creative Director</SelectItem>
                              <SelectItem value="Customer Success Manager">Customer Success Manager</SelectItem>
                              <SelectItem value="Customer Support Representative">Customer Support Representative</SelectItem>
                              <SelectItem value="Cybersecurity Analyst">Cybersecurity Analyst</SelectItem>
                              <SelectItem value="Cybersecurity Engineer">Cybersecurity Engineer</SelectItem>
                              
                              {/* D-F */}
                              <SelectItem value="Data Analyst">Data Analyst</SelectItem>
                              <SelectItem value="Data Engineer">Data Engineer</SelectItem>
                              <SelectItem value="Data Scientist">Data Scientist</SelectItem>
                              <SelectItem value="Database Administrator">Database Administrator</SelectItem>
                              <SelectItem value="Dentist">Dentist</SelectItem>
                              <SelectItem value="DevOps Engineer">DevOps Engineer</SelectItem>
                              <SelectItem value="Digital Marketing Manager">Digital Marketing Manager</SelectItem>
                              <SelectItem value="Digital Marketing Specialist">Digital Marketing Specialist</SelectItem>
                              <SelectItem value="Director of Engineering">Director of Engineering</SelectItem>
                              <SelectItem value="Director of Marketing">Director of Marketing</SelectItem>
                              <SelectItem value="Director of Operations">Director of Operations</SelectItem>
                              <SelectItem value="Director of Product">Director of Product</SelectItem>
                              <SelectItem value="Director of Sales">Director of Sales</SelectItem>
                              <SelectItem value="Doctor">Doctor</SelectItem>
                              <SelectItem value="Electrical Engineer">Electrical Engineer</SelectItem>
                              <SelectItem value="Event Coordinator">Event Coordinator</SelectItem>
                              <SelectItem value="Event Manager">Event Manager</SelectItem>
                              <SelectItem value="Executive Assistant">Executive Assistant</SelectItem>
                              <SelectItem value="Financial Advisor">Financial Advisor</SelectItem>
                              <SelectItem value="Financial Analyst">Financial Analyst</SelectItem>
                              <SelectItem value="Financial Planner">Financial Planner</SelectItem>
                              <SelectItem value="Frontend Developer">Frontend Developer</SelectItem>
                              <SelectItem value="Full Stack Developer">Full Stack Developer</SelectItem>
                              
                              {/* G-L */}
                              <SelectItem value="Graphic Designer">Graphic Designer</SelectItem>
                              <SelectItem value="Growth Manager">Growth Manager</SelectItem>
                              <SelectItem value="HR Business Partner">HR Business Partner</SelectItem>
                              <SelectItem value="HR Generalist">HR Generalist</SelectItem>
                              <SelectItem value="HR Manager">HR Manager</SelectItem>
                              <SelectItem value="Human Resources Director">Human Resources Director</SelectItem>
                              <SelectItem value="Human Resources Manager">Human Resources Manager</SelectItem>
                              <SelectItem value="iOS Developer">iOS Developer</SelectItem>
                              <SelectItem value="IT Manager">IT Manager</SelectItem>
                              <SelectItem value="IT Support Specialist">IT Support Specialist</SelectItem>
                              <SelectItem value="Investment Banker">Investment Banker</SelectItem>
                              <SelectItem value="Journalist">Journalist</SelectItem>
                              <SelectItem value="Lawyer">Lawyer</SelectItem>
                              <SelectItem value="Lead Developer">Lead Developer</SelectItem>
                              <SelectItem value="Legal Counsel">Legal Counsel</SelectItem>
                              <SelectItem value="Librarian">Librarian</SelectItem>
                              <SelectItem value="Machine Learning Engineer">Machine Learning Engineer</SelectItem>
                              
                              {/* M-P */}
                              <SelectItem value="Management Consultant">Management Consultant</SelectItem>
                              <SelectItem value="Marketing Coordinator">Marketing Coordinator</SelectItem>
                              <SelectItem value="Marketing Director">Marketing Director</SelectItem>
                              <SelectItem value="Marketing Manager">Marketing Manager</SelectItem>
                              <SelectItem value="Marketing Specialist">Marketing Specialist</SelectItem>
                              <SelectItem value="Mechanical Engineer">Mechanical Engineer</SelectItem>
                              <SelectItem value="Mobile App Developer">Mobile App Developer</SelectItem>
                              <SelectItem value="Network Administrator">Network Administrator</SelectItem>
                              <SelectItem value="Network Engineer">Network Engineer</SelectItem>
                              <SelectItem value="Nurse">Nurse</SelectItem>
                              <SelectItem value="Nurse Practitioner">Nurse Practitioner</SelectItem>
                              <SelectItem value="Operations Analyst">Operations Analyst</SelectItem>
                              <SelectItem value="Operations Director">Operations Director</SelectItem>
                              <SelectItem value="Operations Manager">Operations Manager</SelectItem>
                              <SelectItem value="Paralegal">Paralegal</SelectItem>
                              <SelectItem value="Pharmacist">Pharmacist</SelectItem>
                              <SelectItem value="Physical Therapist">Physical Therapist</SelectItem>
                              <SelectItem value="Physician">Physician</SelectItem>
                              <SelectItem value="Physician Assistant">Physician Assistant</SelectItem>
                              <SelectItem value="Principal Engineer">Principal Engineer</SelectItem>
                              <SelectItem value="Product Designer">Product Designer</SelectItem>
                              <SelectItem value="Product Manager">Product Manager</SelectItem>
                              <SelectItem value="Product Marketing Manager">Product Marketing Manager</SelectItem>
                              <SelectItem value="Product Owner">Product Owner</SelectItem>
                              <SelectItem value="Program Manager">Program Manager</SelectItem>
                              <SelectItem value="Project Coordinator">Project Coordinator</SelectItem>
                              <SelectItem value="Project Manager">Project Manager</SelectItem>
                              <SelectItem value="Public Relations Manager">Public Relations Manager</SelectItem>
                              <SelectItem value="Psychologist">Psychologist</SelectItem>
                              
                              {/* Q-S */}
                              <SelectItem value="QA Engineer">QA Engineer</SelectItem>
                              <SelectItem value="Quality Assurance Manager">Quality Assurance Manager</SelectItem>
                              <SelectItem value="Radiologist">Radiologist</SelectItem>
                              <SelectItem value="Real Estate Agent">Real Estate Agent</SelectItem>
                              <SelectItem value="Recruiter">Recruiter</SelectItem>
                              <SelectItem value="Regional Manager">Regional Manager</SelectItem>
                              <SelectItem value="Research Analyst">Research Analyst</SelectItem>
                              <SelectItem value="Research Scientist">Research Scientist</SelectItem>
                              <SelectItem value="Sales Director">Sales Director</SelectItem>
                              <SelectItem value="Sales Engineer">Sales Engineer</SelectItem>
                              <SelectItem value="Sales Manager">Sales Manager</SelectItem>
                              <SelectItem value="Sales Representative">Sales Representative</SelectItem>
                              <SelectItem value="Scrum Master">Scrum Master</SelectItem>
                              <SelectItem value="Security Analyst">Security Analyst</SelectItem>
                              <SelectItem value="Security Engineer">Security Engineer</SelectItem>
                              <SelectItem value="SEO Specialist">SEO Specialist</SelectItem>
                              <SelectItem value="Social Media Manager">Social Media Manager</SelectItem>
                              <SelectItem value="Social Worker">Social Worker</SelectItem>
                              <SelectItem value="Software Architect">Software Architect</SelectItem>
                              <SelectItem value="Software Developer">Software Developer</SelectItem>
                              <SelectItem value="Software Engineer">Software Engineer</SelectItem>
                              <SelectItem value="Solutions Architect">Solutions Architect</SelectItem>
                              <SelectItem value="Specialist">Specialist</SelectItem>
                              <SelectItem value="Supply Chain Manager">Supply Chain Manager</SelectItem>
                              <SelectItem value="Surgeon">Surgeon</SelectItem>
                              <SelectItem value="Systems Administrator">Systems Administrator</SelectItem>
                              <SelectItem value="Systems Analyst">Systems Analyst</SelectItem>
                              <SelectItem value="Systems Engineer">Systems Engineer</SelectItem>
                              
                              {/* T-Z */}
                              <SelectItem value="Tax Advisor">Tax Advisor</SelectItem>
                              <SelectItem value="Teacher">Teacher</SelectItem>
                              <SelectItem value="Technical Lead">Technical Lead</SelectItem>
                              <SelectItem value="Technical Writer">Technical Writer</SelectItem>
                              <SelectItem value="Territory Manager">Territory Manager</SelectItem>
                              <SelectItem value="Test Engineer">Test Engineer</SelectItem>
                              <SelectItem value="Therapist">Therapist</SelectItem>
                              <SelectItem value="Training Manager">Training Manager</SelectItem>
                              <SelectItem value="UI/UX Designer">UI/UX Designer</SelectItem>
                              <SelectItem value="UX Designer">UX Designer</SelectItem>
                              <SelectItem value="UX Researcher">UX Researcher</SelectItem>
                              <SelectItem value="Vice President">Vice President</SelectItem>
                              <SelectItem value="Vice President of Engineering">Vice President of Engineering</SelectItem>
                              <SelectItem value="Vice President of Marketing">Vice President of Marketing</SelectItem>
                              <SelectItem value="Vice President of Sales">Vice President of Sales</SelectItem>
                              <SelectItem value="Web Developer">Web Developer</SelectItem>
                              <SelectItem value="Web Designer">Web Designer</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card 2: Career Level */}
                  <Card className="shadow-md border-2 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50">
                    <CardHeader className="pb-4 bg-gradient-to-r from-purple-50 to-pink-50">
                      <CardTitle className="text-lg flex items-center gap-3">
                        <GraduationCap className={`w-5 h-5 ${careerLevel ? 'text-green-600' : 'text-purple-600'}`} />
                        <span className="font-bold text-slate-800">Career Level</span>
                        <Badge variant="secondary" className="ml-2">Optional</Badge>
                        {careerLevel && <Badge className="bg-green-600 text-white text-xs border-0">SELECTED</Badge>}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {careerLevel ? `Selected: ${careerLevel}` : 'What level is this position? This helps match expectations.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4">
                        <div className="relative">
                          <Select value={careerLevel} onValueChange={setCareerLevel}>
                            <SelectTrigger className={`py-3 px-4 rounded-xl border-2 transition-all ${careerLevel ? 'bg-green-50 border-green-300 shadow-sm' : 'bg-muted/30 border-gray-200 hover:border-primary/50'}`}>
                              <SelectValue placeholder="Select career level" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 bg-background border shadow-lg z-50 rounded-xl">
                              <SelectItem value="Intern">Intern</SelectItem>
                              <SelectItem value="Entry Level">Entry Level</SelectItem>
                              <SelectItem value="Level I">Level I</SelectItem>
                              <SelectItem value="Junior">Junior</SelectItem>
                              <SelectItem value="Level II">Level II</SelectItem>
                              <SelectItem value="Associate">Associate</SelectItem>
                              <SelectItem value="Mid-Level">Mid-Level</SelectItem>
                              <SelectItem value="Level III">Level III</SelectItem>
                              <SelectItem value="Senior">Senior</SelectItem>
                              <SelectItem value="Level IV">Level IV</SelectItem>
                              <SelectItem value="Staff">Staff</SelectItem>
                              <SelectItem value="Level V">Level V</SelectItem>
                              <SelectItem value="Principal">Principal</SelectItem>
                              <SelectItem value="Lead">Lead</SelectItem>
                              <SelectItem value="Specialist">Specialist</SelectItem>
                              <SelectItem value="Consultant">Consultant</SelectItem>
                              <SelectItem value="Manager">Manager</SelectItem>
                              <SelectItem value="Senior Manager">Senior Manager</SelectItem>
                              <SelectItem value="Director">Director</SelectItem>
                              <SelectItem value="Senior Director">Senior Director</SelectItem>
                              <SelectItem value="Vice President">Vice President</SelectItem>
                              <SelectItem value="Senior Vice President">Senior Vice President</SelectItem>
                              <SelectItem value="Executive Vice President">Executive Vice President</SelectItem>
                              <SelectItem value="C-Level (CEO, CTO, CFO, etc.)">C-Level (CEO, CTO, CFO, etc.)</SelectItem>
                              <SelectItem value="President">President</SelectItem>
                              <SelectItem value="Founder">Founder</SelectItem>
                              <SelectItem value="Co-Founder">Co-Founder</SelectItem>
                              <SelectItem value="Partner">Partner</SelectItem>
                              <SelectItem value="Managing Partner">Managing Partner</SelectItem>
                              <SelectItem value="Board Member">Board Member</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card 3: Industry */}
                  <Card className="shadow-md border-2 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50">
                    <CardHeader className="pb-4 bg-gradient-to-r from-green-50 to-teal-50">
                      <CardTitle className="text-lg flex items-center gap-3">
                        <Building2 className={`w-5 h-5 ${jobPositionIndustry ? 'text-green-600' : 'text-green-600'}`} />
                        <span className="font-bold text-slate-800">Industry</span>
                        <Badge variant="secondary" className="ml-2">Optional</Badge>
                        {jobPositionIndustry && <Badge className="bg-green-600 text-white text-xs border-0">SELECTED</Badge>}
                      </CardTitle>
                      <CardDescription className="text-sm">
                        {jobPositionIndustry ? `Selected: ${jobPositionIndustry}` : 'Which industry is this role in? This helps us tailor more specifically.'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid gap-4">
                        <div className="relative">
                          <Input 
                            id="job-position-industry" 
                            placeholder="e.g., Technology, Healthcare, Finance, Education" 
                            value={jobPositionIndustry} 
                            onChange={e => setJobPositionIndustry(e.target.value)} 
                            className={`text-base py-3 px-4 rounded-xl border-2 transition-all ${jobPositionIndustry ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-200 shadow-sm' : 'border-gray-200 focus:border-primary focus:ring-primary/20'}`} 
                          />
                        </div>
                        
                        <div className="relative">
                          <Select value={jobPositionIndustry} onValueChange={setJobPositionIndustry}>
                            <SelectTrigger className={`py-3 px-4 rounded-xl border-2 transition-all ${jobPositionIndustry ? 'bg-green-50 border-green-300 shadow-sm' : 'bg-muted/30 border-gray-200 hover:border-primary/50'}`}>
                              <SelectValue placeholder="💼 Or select from common industries" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 bg-background border shadow-lg z-50 rounded-xl">
                              <SelectItem value="Accounting">Accounting</SelectItem>
                              <SelectItem value="Advertising">Advertising</SelectItem>
                              <SelectItem value="Aerospace">Aerospace</SelectItem>
                              <SelectItem value="Agriculture">Agriculture</SelectItem>
                              <SelectItem value="Architecture">Architecture</SelectItem>
                              <SelectItem value="Arts & Design">Arts & Design</SelectItem>
                              <SelectItem value="Automotive">Automotive</SelectItem>
                              <SelectItem value="Aviation">Aviation</SelectItem>
                              <SelectItem value="Banking">Banking</SelectItem>
                              <SelectItem value="Biotechnology">Biotechnology</SelectItem>
                              <SelectItem value="Broadcasting">Broadcasting</SelectItem>
                              <SelectItem value="Business Services">Business Services</SelectItem>
                              <SelectItem value="Chemical">Chemical</SelectItem>
                              <SelectItem value="Communications">Communications</SelectItem>
                              <SelectItem value="Computer Software">Computer Software</SelectItem>
                              <SelectItem value="Construction">Construction</SelectItem>
                              <SelectItem value="Consulting">Consulting</SelectItem>
                              <SelectItem value="Consumer Goods">Consumer Goods</SelectItem>
                              <SelectItem value="Cybersecurity">Cybersecurity</SelectItem>
                              <SelectItem value="Data Analytics">Data Analytics</SelectItem>
                              <SelectItem value="Defense">Defense</SelectItem>
                              <SelectItem value="E-commerce">E-commerce</SelectItem>
                              <SelectItem value="Education">Education</SelectItem>
                              <SelectItem value="Electronics">Electronics</SelectItem>
                              <SelectItem value="Energy">Energy</SelectItem>
                              <SelectItem value="Engineering">Engineering</SelectItem>
                              <SelectItem value="Entertainment">Entertainment</SelectItem>
                              <SelectItem value="Environmental Services">Environmental Services</SelectItem>
                              <SelectItem value="Fashion">Fashion</SelectItem>
                              <SelectItem value="Finance">Finance</SelectItem>
                              <SelectItem value="Financial Services">Financial Services</SelectItem>
                              <SelectItem value="Food & Beverage">Food & Beverage</SelectItem>
                              <SelectItem value="Gaming">Gaming</SelectItem>
                              <SelectItem value="Government">Government</SelectItem>
                              <SelectItem value="Healthcare">Healthcare</SelectItem>
                              <SelectItem value="Hospitality">Hospitality</SelectItem>
                              <SelectItem value="Human Resources">Human Resources</SelectItem>
                              <SelectItem value="Information Technology">Information Technology</SelectItem>
                              <SelectItem value="Insurance">Insurance</SelectItem>
                              <SelectItem value="Internet">Internet</SelectItem>
                              <SelectItem value="Investment Banking">Investment Banking</SelectItem>
                              <SelectItem value="Legal Services">Legal Services</SelectItem>
                              <SelectItem value="Logistics">Logistics</SelectItem>
                              <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                              <SelectItem value="Marketing">Marketing</SelectItem>
                              <SelectItem value="Media">Media</SelectItem>
                              <SelectItem value="Medical Devices">Medical Devices</SelectItem>
                              <SelectItem value="Mining">Mining</SelectItem>
                              <SelectItem value="Nonprofit">Nonprofit</SelectItem>
                              <SelectItem value="Oil & Gas">Oil & Gas</SelectItem>
                              <SelectItem value="Pharmaceuticals">Pharmaceuticals</SelectItem>
                              <SelectItem value="Public Relations">Public Relations</SelectItem>
                              <SelectItem value="Publishing">Publishing</SelectItem>
                              <SelectItem value="Real Estate">Real Estate</SelectItem>
                              <SelectItem value="Renewable Energy">Renewable Energy</SelectItem>
                              <SelectItem value="Research">Research</SelectItem>
                              <SelectItem value="Retail">Retail</SelectItem>
                              <SelectItem value="Sales">Sales</SelectItem>
                              <SelectItem value="Security">Security</SelectItem>
                              <SelectItem value="Semiconductors">Semiconductors</SelectItem>
                              <SelectItem value="Social Media">Social Media</SelectItem>
                              <SelectItem value="Software">Software</SelectItem>
                              <SelectItem value="Sports">Sports</SelectItem>
                              <SelectItem value="Staffing & Recruiting">Staffing & Recruiting</SelectItem>
                              <SelectItem value="Supply Chain">Supply Chain</SelectItem>
                              <SelectItem value="Technology">Technology</SelectItem>
                              <SelectItem value="Telecommunications">Telecommunications</SelectItem>
                              <SelectItem value="Tourism">Tourism</SelectItem>
                              <SelectItem value="Transportation">Transportation</SelectItem>
                              <SelectItem value="Utilities">Utilities</SelectItem>
                              <SelectItem value="Venture Capital">Venture Capital</SelectItem>
                              <SelectItem value="Wellness">Wellness</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Tailoring Options */}
                {(jobPositionTitle || jobPositionIndustry) && <div className="space-y-4 pt-4 border-t border-border max-w-xl mx-auto mb-6">
                  {/* Basic Tailoring Option */}
                  <Card className={`border-2 transition-all cursor-pointer relative shadow-lg ${selectedTailoringType === 'basic' ? 'border-green-500 bg-gradient-to-br from-green-100 to-green-200 shadow-xl ring-4 ring-green-200' : 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200'}`} onClick={() => setSelectedTailoringType('basic')}>
                    {selectedTailoringType === 'basic' && <div className="absolute top-3 right-3">
                        <Badge className="bg-green-600 text-white border-0">SELECTED</Badge>
                      </div>}
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${selectedTailoringType === 'basic' ? 'bg-green-600 border-green-600' : 'bg-blue-500 border-blue-300'}`}>
                            {selectedTailoringType === 'basic' ? <CheckCircle className="w-5 h-5 text-white" /> : <TrendingUp className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <CardTitle className={`text-lg ${selectedTailoringType === 'basic' ? 'text-green-900 font-bold' : 'text-blue-800'}`}>
                              Basic Tailoring
                            </CardTitle>
                            <p className={`text-sm font-medium ${selectedTailoringType === 'basic' ? 'text-green-700' : 'text-blue-700'}`}>Under 45 Seconds</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className={`${selectedTailoringType === 'basic' ? 'bg-green-600' : 'bg-blue-600'} text-white`}>FAST</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 rounded-b-lg">
                      <p className={`text-sm ${selectedTailoringType === 'basic' ? 'text-green-800' : 'text-blue-800'}`}>
                        Instantly tailor your resume using our AI analysis of your target {jobPositionTitle} role{jobPositionIndustry && ` in ${jobPositionIndustry}`}. 
                        We'll optimize key sections based on industry standards and common requirements.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Advanced Tailoring Option */}
                  <Card className={`border-2 transition-all cursor-pointer relative shadow-lg ${selectedTailoringType === 'advanced' ? 'border-green-500 bg-gradient-to-br from-green-100 to-green-200 shadow-xl ring-4 ring-green-200' : 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 hover:bg-gradient-to-br hover:from-gray-100 hover:to-gray-200'}`} onClick={() => setSelectedTailoringType('advanced')}>
                    {selectedTailoringType === 'advanced' && <div className="absolute top-3 right-3">
                        <Badge className="bg-green-600 text-white border-0">SELECTED</Badge>
                      </div>}
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${selectedTailoringType === 'advanced' ? 'bg-green-600 border-green-600' : 'bg-green-500 border-green-300'}`}>
                            {selectedTailoringType === 'advanced' ? <CheckCircle className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <CardTitle className={`text-lg ${selectedTailoringType === 'advanced' ? 'text-green-900 font-bold' : 'text-green-800'}`}>
                              Advanced Tailoring
                            </CardTitle>
                            <p className="text-sm text-green-700 font-medium">2-3 Minutes • 15x More Accurate</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="bg-green-600 text-white">BEST RESULTS</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 rounded-b-lg">
                      <p className="text-sm text-green-800">
                        Get maximum precision by answering a few targeted questions about your experience and goals. 
                        This creates a highly customized resume that's 15x more likely to get you noticed by hiring managers and ATS systems.
                      </p>
                       {selectedTailoringType === 'advanced' && <div className="mt-4 pt-4 border-t border-green-200 space-y-4">
                           <div>
                              <Label className="text-sm font-medium text-green-800">Load Saved Analysis (Optional)</Label>
                              <Select value={selectedSavedAnalysisId} onValueChange={handleSavedAnalysisSelection} onOpenChange={open => {
                              if (open && savedJobAnalyses.length === 0) {
                                loadSavedJobAnalyses();
                              }
                            }}>
                                <SelectTrigger className="mt-1 bg-background border shadow-sm z-50">
                                  <SelectValue placeholder="Select a saved analysis or create new" />
                                </SelectTrigger>
                                <SelectContent className="bg-background border shadow-lg z-50">
                                  <SelectItem value="clear">
                                    <div className="flex items-center gap-2">
                                      <span className="text-green-600">✨</span>
                                      <span className="font-medium">Create New Analysis</span>
                                    </div>
                                  </SelectItem>
                                  {loadingSavedAnalyses ? <SelectItem value="loading" disabled>Loading...</SelectItem> : savedJobAnalyses.length > 0 ? <>
                                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
                                        Saved Analyses
                                      </div>
                                      {savedJobAnalyses.map(analysis => <SelectItem key={analysis.id} value={analysis.id}>
                                          <div className="flex items-center gap-2">
                                            <span className="text-blue-600">📋</span>
                                            <span>{analysis.position_display_name}</span>
                                          </div>
                                        </SelectItem>)}
                                    </> : <SelectItem value="none" disabled>No saved analyses found</SelectItem>}
                                </SelectContent>
                              </Select>
                           </div>
                            {/* Progress Bar for Question Generation */}
                            {loadingAnalysis && <Card className="p-4 mb-4 bg-green-50 border-green-200">
                                <div className="text-center space-y-3">
                                  <div className="flex items-center justify-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                                    <span className="text-sm font-medium text-green-800">
                                      Generating Strategic Questions... {Math.round(analysisProgress)}%
                                    </span>
                                  </div>
                                  
                                  <div className="text-xs text-green-700">
                                    {analysisProgress < 30 ? "Analyzing job requirements..." : analysisProgress < 60 ? "Generating strategic questions..." : analysisProgress < 85 ? "Tailoring questions to role..." : analysisProgress < 100 ? "Finalizing analysis..." : "Complete!"}
                                  </div>
                                </div>
                              </Card>}

                            <Button onClick={async e => {
                            e.stopPropagation();
                            if (selectedSavedAnalysisId) {
                              // Use saved analysis - no need to generate new one
                              setShowAdvancedWizard(true);
                            } else {
                              // Generate new analysis
                              await generateJobAnalysis();
                              setShowAdvancedWizard(true);
                            }
                          }} className="w-full bg-green-600 hover:bg-green-700 text-white" disabled={!jobPositionTitle || loadingAnalysis}>
                              {loadingAnalysis ? 'Analyzing Job...' : selectedSavedAnalysisId && selectedSavedAnalysisId !== '' && selectedSavedAnalysisId !== 'none' ? 'Use Saved Analysis' : 'Answer Questions Now'}
                            </Button>
                         </div>}
                    </CardContent>
                  </Card>
                </div>}
                
                {/* Finished Selecting Position Details Button */}
                {!completedSteps.includes(4) && <div className="mt-8 pt-6 pb-8 border-t border-gray-200/50">
                  <div className="flex justify-center">
                    <Button onClick={() => {
                        if (!jobPositionTitle.trim()) {
                          toast({
                            title: "Position Title Required",
                            description: "Please enter a job position title to continue.",
                            variant: "destructive"
                          });
                          return;
                        }
                        setStep4Completed(true);
                        markStepComplete(4, true);
                        toast({
                          title: "Position details completed!",
                          description: `Target position: ${jobPositionTitle} configured successfully.`
                        });
                      }} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-md" disabled={!jobPositionTitle.trim()}>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Finished Selecting Position Details
                    </Button>
                  </div>
                </div>}
              </CollapsibleContent>}
          </Card>
        </Collapsible>}

        {/* Step 5: Additional Instructions Question - For job position */}
        {tailoringSource === 'job-position' && completedSteps.includes(4) && <Collapsible open={stepExpanded[5]} onOpenChange={() => toggleStepExpansion(5)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(5) ? (stepExpanded[5] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[5] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(5) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(5) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                        <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 5: Additional Instructions</span>
                        {completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 5 && !completedSteps.includes(5) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(5) && !stepExpanded[5] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                             <CheckCircle className="w-4 h-4 text-green-500" />
                            {additionalInstructionsAnswer === 'yes' ? 'Additional instructions will be provided' : 'No additional instructions needed'}
                          </div>}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[5] ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <CardContent className="pt-0 bg-white rounded-b-lg">
                <div className="space-y-6 pt-8 px-4 pb-8">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 mx-[84px]">Do you want to add any additional instructions, documents or notes to help tailor your resume?</h3>
                    <p className="text-muted-foreground mb-6">This could include specific requirements, company culture notes, or additional context</p>
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => {
                            setAdditionalInstructionsAnswer('yes');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'yes' ? 'default' : 'outline'} className="px-6 py-3 text-sm bg-green-600 hover:bg-green-700 text-white border-green-600 rounded-xl transition-all hover:scale-105 shadow-md">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Yes, I have additional instructions
                    </Button>
                     <Button onClick={() => {
                            setAdditionalInstructionsAnswer('no');
                            markStepComplete(5, true);
                          }} variant={additionalInstructionsAnswer === 'no' ? 'default' : 'outline'} className={`px-6 py-3 text-sm rounded-xl transition-all hover:scale-105 shadow-md ${additionalInstructionsAnswer === 'no' ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'}`}>
                      <X className="w-4 h-4 mr-2" />
                      No, proceed with current information
                    </Button>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>}

        {/* Step 6: Additional Instructions & Documents Content (only if Step 5 = YES) - For job position */}
        {tailoringSource === 'job-position' && additionalInstructionsAnswer === 'yes' && <Collapsible open={stepExpanded[6]} onOpenChange={() => toggleStepExpansion(6)}>
          <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CollapsibleTrigger asChild>
              <CardHeader className={`pb-6 cursor-pointer ${completedSteps.includes(6) ? (stepExpanded[6] ? 'rounded-t-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20' : 'rounded-2xl bg-gradient-to-r from-accent-light/30 to-primary-light/20') : (stepExpanded[6] ? 'rounded-t-2xl bg-white/90' : 'rounded-2xl bg-white/90')}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                     <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-md ${completedSteps.includes(6) ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-200 to-slate-300/70'} text-slate-700`}>
                       {completedSteps.includes(6) ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Paperclip className="w-5 h-5" />}
                     </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl flex items-center gap-3">
                          <span className="font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Step 6: Additional Instructions & Docs</span>
                        {completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0">
                            ✅ COMPLETED
                          </Badge>}
                        {currentStep === 6 && !completedSteps.includes(6) && <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs px-4 py-1.5 rounded-full shadow-sm border-0 animate-pulse">
                            ⚡ ACTIVE
                          </Badge>}
                      </CardTitle>
                      
                      {/* Show summary when collapsed and completed */}
                        {completedSteps.includes(6) && !stepExpanded[6] && <div className="text-sm font-medium text-slate-700 mt-2 p-3 bg-slate-100 rounded-lg border border-slate-300 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            Additional instructions and documents saved
                          </div>}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground ${stepExpanded[6] ? 'rotate-180' : ''}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            
            {/* Only show content when step is not completed OR when completed but expanded for editing */}
            {(!completedSteps.includes(6) || stepExpanded[6]) && <CollapsibleContent className="animate-accordion-down">
                <CardContent className="space-y-6 rounded-b-3xl pt-8 px-8 pb-8 bg-gradient-to-b from-white to-gray-50/50">
                  {/* Additional Instructions Section */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="additional-instructions" className="text-base font-semibold">Additional Instructions</Label>
                      <p className="text-sm text-muted-foreground mt-1">Provide any specific guidelines, requirements, or preferences for tailoring</p>
                    </div>
                    <Textarea id="additional-instructions" placeholder="Include any specific guidelines, requirements, or preferences for the tailoring process..." value={additionalInstructions} onChange={e => {
                        setAdditionalInstructions(e.target.value);
                      }} rows={4} />
                  </div>

                  <div className="border-t border-border pt-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-base font-semibold">Additional Instructions & Docs</Label>
                        <p className="text-sm text-muted-foreground mt-1">Upload any additional documents, notes, or context files</p>
                      </div>
                      
                      <DocumentUpload onUploadComplete={handleAdditionalDocumentUpload} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
                      
                      {additionalDocuments.length > 0 && <div className="space-y-2">
                          <Label className="text-sm font-medium">Uploaded Documents ({additionalDocuments.length}):</Label>
                          {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                              <div className="flex items-center gap-2">
                                 <FileText className="w-4 h-4 text-slate-600" />
                                <span className="text-sm font-medium">{doc.file.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({doc.extractedText.length} characters extracted)
                                </span>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-auto p-1 text-red-600 hover:text-red-700">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>)}
                        </div>}
                    </div>
                  </div>
                  
                  {/* Complete step button */}
                  {!completedSteps.includes(6) && <div className="flex justify-center pt-4">
                    <Button onClick={() => {
                        setStep6Completed(true);
                        markStepComplete(6, true);
                        toast({
                          title: "Additional instructions completed!",
                          description: "Your additional instructions and documents have been saved."
                        });
                      }} className="bg-green-600 hover:bg-green-700 text-white">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Complete Additional Instructions
                    </Button>
                  </div>}
                </CardContent>
              </CollapsibleContent>}
          </Card>
        </Collapsible>}

        {/* Advanced Tailoring Options - For job position */}
        {tailoringSource === 'job-position' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <Collapsible open={showAdvancedOptionsJob} onOpenChange={setShowAdvancedOptionsJob}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer bg-white/90 rounded-t-2xl border-b border-gray-200">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-foreground">Advanced Tailoring Options</div>
                        <div className="text-sm text-muted-foreground font-normal">Fine-tune your resume tailoring preferences</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showAdvancedOptionsJob ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <CardContent className="p-8 space-y-10 rounded-b-lg">
                  
                  {/* Career Summary Options - Hidden for Job Position/Industry tailoring */}
                  {/* Guard was inverted (!== 'job-position' inside a job-position-only card)
                      so this section never rendered. Company name/mission toggles removed:
                      there is no target company in position mode. */}
                  {<section className="space-y-6">
                      <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                          <BarChart3 className="w-4 h-4 text-primary" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {[{
                            id: "feature-solution-asset-pos",
                            checked: featureSolutionAsset,
                            onChange: setFeatureSolutionAsset,
                            title: "Feature Solution Asset in Summary",
                            description: "Highlight your solution assets and value propositions in the career summary",
                            recommended: true
                          }, {
                            id: "mention-education-pos",
                            checked: mentionEducation,
                            onChange: setMentionEducation,
                            title: "Education Highlighting",
                            description: "Emphasize relevant education credentials in the summary"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'} ${option.recommended ? 'relative' : ''}`}>
                            {option.recommended && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                                RECOMMENDED
                              </Badge>}
                            <div className="flex items-start space-x-3">
                               <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {option.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {option.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </div>
                    </section>}

                  {/* Professional Experience Framework */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a proven framework to structure your experience bullet points for maximum impact
                      </p>
                      <RadioGroup value={workExperienceFramework} onValueChange={setWorkExperienceFramework} className="space-y-3">
                        {[{
                              value: "star",
                              title: "STAR Method",
                              description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                            }, {
                              value: "par",
                              title: "PAR Method",
                              description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                            }, {
                              value: "arta",
                              title: "ARTA Method",
                              description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                            }, {
                              value: "cosr",
                              title: "COSR Method",
                              description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                            }, {
                              value: "none",
                              title: "Original Format",
                              description: "Preserve your existing bullet point structure without reformatting"
                            }].map(method => <div key={method.value} className={`group p-4 rounded-xl border transition-all duration-300 ${workExperienceFramework === method.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={method.value} id={`method-pos-${method.value}`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={`method-pos-${method.value}`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {method.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Experience Optimization */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Optimization</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'transferable-skills-pos',
                            checked: transferableSkillsFocus,
                            onChange: setTransferableSkillsFocus,
                            title: "Transferable Skills Focus",
                            description: "Highlight the top transferable hard skills that align with the target role"
                          }, {
                            id: 'skill-relevance-pos',
                            checked: skillRelevanceOptimization,
                            onChange: setSkillRelevanceOptimization,
                            title: "Skill Relevance Optimization",
                            description: "Remove or minimize bullets that don't demonstrate target-relevant skills"
                          }, {
                            id: 'irrelevant-filtering-pos',
                            checked: irrelevantJobFiltering,
                            onChange: setIrrelevantJobFiltering,
                            title: "Irrelevant Experience Filtering",
                            description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Unreachable Template Selection removed (inverted guard made it dead; template choice lives on the results page). */}

                  {/* Sections to Tailor */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Specific Resume Sections to Tailor</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-center space-x-3">
                            <Checkbox id={`section-pos-${section.toLowerCase().replace(' ', '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor={`section-pos-${section.toLowerCase().replace(' ', '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                              {section}
                            </Label>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Career Transition Options */}
                  <section className="space-y-6">
                        <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Transition Support</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'career-transition-pos',
                            label: 'Career/Industry Transition',
                            description: 'Highlight transferable skills while minimizing gaps in direct experience for career pivots.'
                          }, {
                            id: 'military-civilian-pos',
                            label: 'Military to Civilian Transition',
                            description: 'Translate military terminology and achievements into civilian-friendly language.'
                          }, {
                            id: 'relevancy-statements-pos',
                            label: 'Job Relevancy Statements',
                            description: 'Add connecting statements explaining how past roles relate to your target position.'
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${careerTransitionOptions.includes(option.label) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={careerTransitionOptions.includes(option.label)} onCheckedChange={() => handleCareerTransitionToggle(option.label)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.label}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Quantification Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Add Achievements & Quantification</h3>
                    </div>
                    <RadioGroup value={quantificationLevel} onValueChange={setQuantificationLevel} className="space-y-3">
                      {[{
                            value: "basic",
                            title: "Basic Quantification",
                            description: "Quick yes/no questions to add numbers and metrics to your achievements (30-60 seconds)",
                            time: "Quick Setup"
                          }, {
                            value: "advanced",
                            title: "Advanced Quantification",
                            description: "Detailed open-ended questions for comprehensive metrics and impact analysis (5-8 minutes)",
                            time: "Comprehensive"
                          }].map(level => <div key={level.value} className={`group p-4 rounded-xl border transition-all duration-300 ${quantificationLevel === level.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={level.value} id={`${level.value}-pos`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${level.value}-pos`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {level.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {level.time}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {level.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                  {/* Writing Styles */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Resume Writing Styles & Formats</h3>
                    </div>
                    <RadioGroup value={writingStyle} onValueChange={setWritingStyle} className="space-y-3">
                      {[{
                            value: "chronological",
                            title: "Chronological Resume",
                            description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
                            tag: "Default"
                          }, {
                            value: "functional",
                            title: "Functional Resume",
                            description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
                            tag: "Career Change"
                          }, {
                            value: "combination",
                            title: "Combination (Hybrid) Resume",
                            description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
                            tag: "Balanced"
                          }, {
                            value: "targeted",
                            title: "Targeted Resume",
                            description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
                            tag: "Focused"
                          }, {
                            value: "executive",
                            title: "Executive Resume",
                            description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
                            tag: "Leadership"
                          }, {
                            value: "creative",
                            title: "Creative Resume",
                            description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
                            tag: "Design"
                          }, {
                            value: "federal",
                            title: "Federal Resume",
                            description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
                            tag: "Government"
                          }, {
                            value: "academic",
                            title: "Academic / CV",
                            description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
                            tag: "Academic"
                          }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${writingStyle === style.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={style.value} id={`${style.value}-pos`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${style.value}-pos`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {style.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {style.tag}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {style.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}

        {/* Select Documents to Tailor - For institution (first) */}
        {tailoringSource === 'institution' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 3: Select Documents to Tailor</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Choose which professional documents you want to tailor for this institution
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 rounded-b-lg">
              {/* Professional Documents Section */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-primary" />
                    Professional Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[{
                          id: 'resume',
                          label: 'Resume',
                          icon: FileText,
                          description: 'Primary application document',
                          category: 'professional'
                        }, {
                          id: 'cover-letter',
                          label: 'Cover Letter',
                          icon: FileText,
                          description: 'Personalized introduction letter',
                          category: 'professional'
                        }, {
                          id: 'linkedin-profile',
                          label: 'LinkedIn Profile',
                          icon: Linkedin,
                          description: 'Professional social media',
                          category: 'professional'
                        }, {
                          id: 'executive-bio',
                          label: 'Executive Bio',
                          icon: FileText,
                          description: 'Professional summary bio',
                          category: 'professional'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          const isLocked = ['solution-asset-document', 'hiring-manager-contact-report'].includes(document.id) && tailoringDepth !== 'deep';
                          return <div key={document.id} className={`group relative p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                          <div className="flex items-start space-x-3">
                               <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                             <div className="flex-1 space-y-2">
                               <div className="flex items-center gap-2">
                                 <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-metallic-foreground' : 'text-muted-foreground group-hover:text-metallic-dark'}`} />
                                 <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-metallic-foreground' : 'text-foreground group-hover:text-metallic-dark'}`}>
                                  {document.label}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          </div>
                        </div>;
                        })}
                  </div>
                </div>

                {/* Academic & Institutional documents section removed: those document
                    types (admissions letter, statement of intent, personal mission,
                    mission alignment, fit assessment, impact statement) had NO
                    producer or template anywhere in the system, so selecting them
                    silently did nothing. The remaining grid is the REAL document
                    set, all institution-tailored by the stage 8 letters engine. */}

                {/* Communication Documents Section */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Communication Documents
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[{
                          id: 'thank-you-letter',
                          label: 'Thank You Letter',
                          icon: FileText,
                          description: 'Post-meeting follow-up',
                          category: 'communication'
                        }, {
                          id: 'outreach-letter',
                          label: 'Outreach Email',
                          icon: FileText,
                          description: 'Introduction and networking',
                          category: 'communication'
                        }].map(document => {
                          const Icon = document.icon;
                          const isSelected = selectedDocuments.includes(document.id);
                          const isLocked = ['solution-asset-document', 'hiring-manager-contact-report'].includes(document.id) && tailoringDepth !== 'deep';
                          return <div key={document.id} className={`group p-4 rounded-xl transition-all duration-300 cursor-pointer ${isSelected ? 'bg-green-50 border-2 border-green-500 shadow-md' : 'bg-white border border-border hover:border-muted-foreground hover:shadow-sm'}`} onClick={() => handleDocumentToggle(document.id)}>
                          <div className="flex items-start space-x-3">
                            <Checkbox id={document.id} checked={isSelected} onCheckedChange={() => handleDocumentToggle(document.id)} className="mt-1 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 transition-colors ${isSelected ? 'text-green-600' : 'text-muted-foreground group-hover:text-primary'}`} />
                                <Label htmlFor={document.id} className={`font-medium cursor-pointer transition-colors ${isSelected ? 'text-green-600' : 'text-foreground group-hover:text-primary'}`}>
                                  {document.label}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {document.description}
                              </p>
                            </div>
                          </div>
                        </div>;
                        })}
                  </div>
                </div>
              </div>
              
              {selectedDocuments.length > 0 && <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-muted">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">
                      {selectedDocuments.length} document{selectedDocuments.length > 1 ? 's' : ''} selected for institutional tailoring
                    </span>
                  </div>
                </div>}
            </CardContent>
          </Card>}

        {/* Institution/Organization Details - Only show for institution option */}
        {tailoringSource === 'institution' && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Step 4: Institution/Organization Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 rounded-b-lg">
              <div className="space-y-2">
                <Label htmlFor="institution-name">Institution/Organization Name *</Label>
                <Input id="institution-name" placeholder="Enter institution or organization name" value={institutionName} onChange={e => {
                      setInstitutionName(e.target.value);
                      // Mark step 4 as completed when institution name is provided
                      if (e.target.value.trim()) {
                        setStep4Completed(true);
                      } else {
                        setStep4Completed(false);
                      }
                    }} required />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="institution-type">Institution Type</Label>
                <Select value={institutionType} onValueChange={setInstitutionType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select institution type (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="academic">Academic</SelectItem>
                    <SelectItem value="nonprofit">Nonprofit</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="religious">Religious</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mission-alignment">Mission Alignment Statement (Optional)</Label>
                <Textarea id="mission-alignment" placeholder="Summarize in 1–2 sentences how your goals or values align with this institution's mission..." value={missionAlignment} onChange={e => setMissionAlignment(e.target.value)} rows={3} />
              </div>
            </CardContent>
          </Card>}


        {/* Institution-specific Additional Instructions - After document selection */}
        {tailoringSource === 'institution' && <Card>
            <CardHeader>
              <CardTitle>Step 5: Institutional Guidelines & Alignment</CardTitle>
              <CardDescription>Please provide any specific guidelines or expectations the institution has for this document</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="institutional-guidelines">Institutional Guidelines</Label>
                  <Textarea id="institutional-guidelines" placeholder="This may include admissions requirements, personal values the institution emphasizes, tone of voice they prefer (formal, mission-driven, faith-centered, etc.), or specific experiences or themes you've been asked to include..." value={additionalInstructions} onChange={e => setAdditionalInstructions(e.target.value)} rows={4} />
                </div>
                
                {selectedDocuments.some(doc => ['executive-bio'].includes(doc)) && <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium text-sm mb-2">For Executive Bio:</h4>
                    <p className="text-sm text-muted-foreground">
                      Are there any particular achievements, themes, or beliefs you want to highlight that align with this institution's values, mission, or impact areas?
                    </p>
                  </div>}
              </div>
            </CardContent>
          </Card>}


        {/* Upload Additional Documents - Show for institution (last) */}
        {tailoringSource === 'institution' && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Step 6: Upload Additional Documents or Notes (Optional)
              </CardTitle>
              <CardDescription>Upload supporting documents, notes, or any additional context</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 rounded-b-lg">
              <DocumentUpload onUploadComplete={handleAdditionalDocumentUpload} title="Upload Additional Documents" description="Upload supporting documents, notes, or any additional context" acceptedTypes={['.pdf', '.doc', '.docx', '.txt']} />
              
              {additionalDocuments.length > 0 && <div className="space-y-2">
                  <Label className="text-sm font-medium">Uploaded Documents ({additionalDocuments.length}):</Label>
                  {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-medium">{doc.file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({doc.extractedText.length} characters extracted)
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(index)} className="h-auto p-1 text-red-600 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>)}
                </div>}
            </CardContent>
          </Card>}

        {/* Advanced Tailoring Options - For institution */}
        {tailoringSource === 'institution' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <Collapsible open={showAdvancedOptionsInstitution} onOpenChange={setShowAdvancedOptionsInstitution}>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer bg-white/90 rounded-t-2xl border-b border-gray-200">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Settings className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xl font-semibold text-foreground">Advanced Tailoring Options</div>
                        <div className="text-sm text-muted-foreground font-normal">Fine-tune your institutional document preferences</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showAdvancedOptionsInstitution ? 'rotate-180' : ''}`} />
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent className="animate-accordion-down">
                <CardContent className="p-8 space-y-10 rounded-b-lg">
                  
                  {/* Career Summary Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Summary Enhancement</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {[{
                            id: "feature-solution-asset-inst",
                            checked: featureSolutionAsset,
                            onChange: setFeatureSolutionAsset,
                            title: "Feature Solution Asset in Summary",
                            description: "Highlight your solution assets and value propositions in the career summary",
                            recommended: true
                          }, {
                            id: "mention-institution-inst",
                            checked: mentionCompanyName,
                            onChange: setMentionCompanyName,
                            title: "Institution Name Integration",
                            description: "Naturally weave the target institution name into your professional summary"
                          }, {
                            id: "mention-mission-inst",
                            checked: mentionCompanyMission,
                            onChange: setMentionCompanyMission,
                            title: "Mission Alignment",
                            description: "Reference the institution's mission statement to show alignment with values"
                          }, {
                            id: "mention-education-inst",
                            checked: mentionEducation,
                            onChange: setMentionEducation,
                            title: "Education Highlighting",
                            description: "Emphasize relevant education credentials and academic achievements"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'} ${option.recommended ? 'relative' : ''}`}>
                          {option.recommended && <Badge variant="default" className="absolute -top-2 -right-2 bg-gradient-to-r from-green-600 to-green-500 text-white text-xs px-2 py-1 font-bold shadow-md">
                              RECOMMENDED
                            </Badge>}
                          <div className="flex items-start space-x-3">
                             <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Professional Experience Framework */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Formatting Framework</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Choose a proven framework to structure your experience for institutional applications
                      </p>
                      <RadioGroup value={workExperienceFramework} onValueChange={setWorkExperienceFramework} className="space-y-3">
                        {[{
                              value: "star",
                              title: "STAR Method",
                              description: "Situation-Task-Action-Result format for compelling storytelling and measurable outcomes"
                            }, {
                              value: "par",
                              title: "PAR Method",
                              description: "Problem-Action-Result structure to showcase your problem-solving capabilities"
                            }, {
                              value: "arta",
                              title: "ARTA Method",
                              description: "Action-Result-Tools-Achievement framework emphasizing technical skills and tools"
                            }, {
                              value: "cosr",
                              title: "COSR Method",
                              description: "Challenge-Outcome-Skills-Results approach highlighting your effectiveness under pressure"
                            }, {
                              value: "none",
                              title: "Original Format",
                              description: "Preserve your existing experience structure without reformatting"
                            }].map(method => <div key={method.value} className={`group p-4 rounded-xl border transition-all duration-300 ${workExperienceFramework === method.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem value={method.value} id={`method-inst-${method.value}`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                              <div className="space-y-2 flex-1">
                                <Label htmlFor={`method-inst-${method.value}`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {method.title}
                                </Label>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {method.description}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      </RadioGroup>
                    </div>
                  </section>

                  {/* Experience Optimization */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Experience Optimization</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'irrelevant-filtering-inst',
                            checked: irrelevantJobFiltering,
                            onChange: setIrrelevantJobFiltering,
                            title: "Irrelevant Experience Filtering",
                            description: "Minimize unrelated positions to 2 concise bullets while maintaining relevant qualifications"
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${option.checked ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <Checkbox id={option.id} checked={option.checked} onCheckedChange={checked => option.onChange(checked === true)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.title}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Template Selection */}
                  

                  {/* Sections to Tailor */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Specific Resume Sections to Tailor</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-center space-x-3">
                            <Checkbox id={`section-inst-${section.toLowerCase().replace(' ', '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <Label htmlFor={`section-inst-${section.toLowerCase().replace(' ', '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                              {section}
                            </Label>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Career Transition Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Career Transition Support</h3>
                    </div>
                    <div className="space-y-4">
                      {[{
                            id: 'career-transition-inst',
                            label: 'Career/Industry Transition',
                            description: 'Highlight transferable skills while minimizing gaps in direct experience for career pivots.'
                          }, {
                            id: 'military-civilian-inst',
                            label: 'Military to Civilian Transition',
                            description: 'Translate military terminology and achievements into civilian-friendly language.'
                          }, {
                            id: 'relevancy-statements-inst',
                            label: 'Institutional Relevancy Statements',
                            description: 'Add connecting statements explaining how past roles relate to institutional values and goals.'
                          }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 ${careerTransitionOptions.includes(option.label) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <Checkbox id={option.id} checked={careerTransitionOptions.includes(option.label)} onCheckedChange={() => handleCareerTransitionToggle(option.label)} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <Label htmlFor={option.id} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                {option.label}
                              </Label>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {option.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </section>

                  {/* Quantification Options */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Add Achievements & Quantification</h3>
                    </div>
                    <RadioGroup value={quantificationLevel} onValueChange={setQuantificationLevel} className="space-y-3">
                      {[{
                            value: "basic",
                            title: "Basic Quantification",
                            description: "Quick yes/no questions to add numbers and metrics to your achievements (30-60 seconds)",
                            time: "Quick Setup"
                          }, {
                            value: "advanced",
                            title: "Advanced Quantification",
                            description: "Detailed open-ended questions for comprehensive metrics and impact analysis (5-8 minutes)",
                            time: "Comprehensive"
                          }].map(level => <div key={level.value} className={`group p-4 rounded-xl border transition-all duration-300 ${quantificationLevel === level.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={level.value} id={`${level.value}-inst`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${level.value}-inst`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {level.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {level.time}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {level.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                  {/* Writing Styles */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3 pb-3 border-b border-border bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 px-4 py-2 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Resume Writing Styles & Formats</h3>
                    </div>
                    <RadioGroup value={writingStyle} onValueChange={setWritingStyle} className="space-y-3">
                      {[{
                            value: "chronological",
                            title: "Chronological Resume",
                            description: "The most traditional format. Lists work history in reverse chronological order (most recent first). Best for people with a steady career path and clear progression in the same field.",
                            tag: "Default"
                          }, {
                            value: "functional",
                            title: "Functional Resume",
                            description: "Focuses on skills and achievements rather than work history. Work experience is grouped under skill categories. Often used by career changers or those with employment gaps.",
                            tag: "Career Change"
                          }, {
                            value: "combination",
                            title: "Combination (Hybrid) Resume",
                            description: "Blends chronological and functional. Starts with a strong skills or summary section, then lists detailed work history. Good for highlighting both transferable skills and a solid work background.",
                            tag: "Balanced"
                          }, {
                            value: "targeted",
                            title: "Targeted Resume",
                            description: "Tailored specifically for one job posting or industry. Every section is aligned to the employer's needs. Often used for high-competition roles.",
                            tag: "Focused"
                          }, {
                            value: "executive",
                            title: "Executive Resume",
                            description: "Designed for senior managers, directors, and C-level candidates. Emphasizes leadership, strategic impact, quantifiable achievements, and scope of responsibility. Often longer than standard resumes (2–3 pages) with boardroom-ready language.",
                            tag: "Leadership"
                          }, {
                            value: "creative",
                            title: "Creative Resume",
                            description: "Used in fields like design, marketing, or media. May include visuals, infographics, or non-traditional layouts. Works best when employers value creativity and originality.",
                            tag: "Design"
                          }, {
                            value: "federal",
                            title: "Federal Resume",
                            description: "Required for U.S. government jobs. Much longer and more detailed than standard resumes. Includes exact dates, hours worked, supervisors, and detailed duty descriptions.",
                            tag: "Government"
                          }, {
                            value: "academic",
                            title: "Academic / CV",
                            description: "Used in education, research, and medicine. Comprehensive record of publications, teaching, research, grants, and service. Much longer than a standard resume (can be many pages).",
                            tag: "Academic"
                          }].map(style => <div key={style.value} className={`group p-4 rounded-xl border transition-all duration-300 ${writingStyle === style.value ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`}>
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={style.value} id={`${style.value}-inst`} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`${style.value}-inst`} className="text-sm font-semibold text-foreground cursor-pointer group-hover:text-primary transition-colors">
                                  {style.title}
                                </Label>
                                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                  {style.tag}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground leading-relaxed">
                                {style.description}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </RadioGroup>
                  </section>

                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>}

        {/* Select Documents to Revise - For revisions */}
        {tailoringSource === 'revisions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 3: Select Documents to Revise/Update</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Choose which professional documents you want to revise and update
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 rounded-b-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[{
                      value: 'resume',
                      label: 'Resume',
                      icon: FileText,
                      description: 'Revise and update your resume'
                    }, {
                      value: 'cover-letter',
                      label: 'Cover Letter',
                      icon: Mail,
                      description: 'Update your cover letter'
                    }, {
                      value: 'linkedin-profile',
                      label: 'LinkedIn Profile',
                      icon: Linkedin,
                      description: 'Enhance your LinkedIn profile'
                    }, {
                      value: 'outreach-letter',
                      label: 'Outreach Letter',
                      icon: TrendingUp,
                      description: 'Improve your networking outreach letters'
                    }, {
                      value: 'thank-you-letter',
                      label: 'Thank You Letter',
                      icon: Heart,
                      description: 'Polish your thank you letters'
                    }, {
                      value: 'executive-bio',
                      label: 'Executive Bio',
                      icon: Users,
                      description: 'Enhance your executive biography'
                    }].map(doc => <div key={doc.value} className={`group p-4 rounded-xl border transition-all duration-300 cursor-pointer ${selectedDocuments.includes(doc.value) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`} onClick={() => handleDocumentToggle(doc.value)}>
                    <div className="flex items-start space-x-3">
                      <Checkbox id={doc.value} checked={selectedDocuments.includes(doc.value)} onChange={() => {}} className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${selectedDocuments.includes(doc.value) ? 'bg-green-100 text-green-600' : 'bg-muted group-hover:bg-primary/10'}`}>
                            <doc.icon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-medium text-sm">{doc.label}</div>
                            <div className="text-xs text-muted-foreground">{doc.description}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Revisions Instructions - For revisions */}
        {tailoringSource === 'revisions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 4: Upload Documents & Instructions</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Upload all files requiring updates/revisions along with any notes, reference materials, or additional instructions
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6 rounded-b-lg">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="revision-instructions" className="text-sm font-medium">
                    Detailed Revision Instructions
                  </Label>
                  <Textarea id="revision-instructions" placeholder="Provide comprehensive instructions for your document revisions. Include specific changes, preferences, target audience, tone requirements, and any other relevant details..." value={revisionInstructions} onChange={e => setRevisionInstructions(e.target.value)} rows={6} className="mt-1" />
                </div>
                
                <div className="border-t pt-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">
                      Upload All Relevant Files
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Include all documents needing updates, reference materials, style guides, examples, notes, and any additional content that will help with the revision process.
                    </p>
                  </div>
                  
                  <div className="mt-4">
                    <DocumentUpload onUploadComplete={(text, filename) => {
                          const file = new File([text], filename || 'revision-document.txt', {
                            type: 'text/plain'
                          });
                          setAdditionalDocuments(prev => [...prev, {
                            file,
                            extractedText: text
                          }]);
                        }} title="Upload Documents & Materials" description="Upload documents, notes, instructions, or reference materials (PDF, Word, TXT)" acceptedTypes={['.doc', '.docx', '.txt', '.pdf']} />
                  </div>
                  
                  {additionalDocuments.length > 0 && <div className="mt-4 space-y-2">
                      <Label className="text-sm font-medium">Uploaded Files:</Label>
                      <div className="space-y-2">
                        {additionalDocuments.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{doc.file.name}</span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setAdditionalDocuments(prev => prev.filter((_, i) => i !== index))} className="h-auto p-1 text-muted-foreground hover:text-destructive">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </CardContent>
          </Card>}

        {/* Preset Revision Options - For revisions */}
        {tailoringSource === 'revisions' && <Card className="shadow-lg border-2 rounded-2xl mb-8 overflow-hidden transition-all duration-300 bg-gradient-to-br from-slate-50 to-slate-100/70 border-slate-300/50 shadow-slate-200/60">
            <CardHeader className="border-b border-border bg-white/90 rounded-t-2xl">&
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                  <Settings className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-semibold">Step 5: Preset Revision Options</CardTitle>
                  <CardDescription className="text-muted-foreground mt-1">
                    Select common revision types to apply to your documents
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[{
                      id: 'remove-redundancies',
                      emoji: '✂️',
                      label: 'Remove Redundancies'
                    }, {
                      id: 'change-designs',
                      emoji: '🎨',
                      label: 'Change Designs/Templates'
                    }, {
                      id: 'improve-formatting',
                      emoji: '📝',
                      label: 'Improve the Formatting'
                    }, {
                      id: 'remove-skills-gaps',
                      emoji: '🔧',
                      label: 'Remove Skills Gaps'
                    }, {
                      id: 'highlight-transferable',
                      emoji: '🌟',
                      label: 'Highlight Transferable Skills'
                    }, {
                      id: 'optimize-work-history',
                      emoji: '📈',
                      label: 'Optimize Work History'
                    }, {
                      id: 'add-metrics',
                      emoji: '📊',
                      label: 'Add Metrics & Numbers'
                    }, {
                      id: 'fact-check-content',
                      emoji: '🔍',
                      label: 'Fact Check Content for Accuracy'
                    }].map(option => <div key={option.id} className={`group p-4 rounded-xl border transition-all duration-300 cursor-pointer ${selectedRevisionOptions.includes(option.id) ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border bg-card hover:bg-muted/30 hover:border-primary/30'}`} onClick={() => {
                      const newOptions = selectedRevisionOptions.includes(option.id) ? selectedRevisionOptions.filter(id => id !== option.id) : [...selectedRevisionOptions, option.id];
                      setSelectedRevisionOptions(newOptions);
                      // Mark step 4 as completed when at least one revision option is selected
                      setStep4Completed(newOptions.length > 0);
                    }}>
                    <div className="flex items-center space-x-3">
                      <Checkbox id={option.id} checked={selectedRevisionOptions.includes(option.id)} onChange={() => {}} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                      <div className="flex items-center gap-3 flex-1">
                        <span className="text-lg">{option.emoji}</span>
                        <Label htmlFor={option.id} className="text-sm font-medium cursor-pointer group-hover:text-primary transition-colors">
                          {option.label}
                        </Label>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>}

        {/* Advanced Revision Options — the only advanced controls that apply in
            revision mode (writing style + which sections to revise). The
            directive generator suppresses everything else for this source. */}
        {tailoringSource === 'revisions' && <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-xl font-semibold">Advanced Revision Options (Optional)</CardTitle>
              <CardDescription>Change the resume's format, or limit the revision to specific sections. Leave untouched to revise everything in the original format.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Writing Style</h4>
                <Select value={writingStyle} onValueChange={setWritingStyle}>
                  <SelectTrigger className="w-full max-w-sm">
                    <SelectValue placeholder="Keep current format (chronological)" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="chronological">Chronological (default — no change)</SelectItem>
                    <SelectItem value="functional">Functional (skills-grouped)</SelectItem>
                    <SelectItem value="combination">Combination (skills + chronological)</SelectItem>
                    <SelectItem value="executive">Executive (boardroom register)</SelectItem>
                    <SelectItem value="federal">Federal (full-detail government format)</SelectItem>
                    <SelectItem value="academic">Academic / CV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Sections to Revise</h4>
                <p className="text-xs text-muted-foreground mb-3">Unchecked sections are reproduced from your original resume word for word.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['Career Summary', 'Skills', 'Career Highlights', 'Work History', 'Education', 'Title & headline'].map(section => <div key={section} className={`group p-3 rounded-lg border transition-all duration-300 ${sectionsToTailor.includes(section) ? 'bg-green-50 border-green-500 shadow-sm' : 'bg-white border-border hover:bg-muted/30 hover:border-primary/30'}`}>
                      <div className="flex items-center space-x-3">
                        <Checkbox id={`section-rev-${section.toLowerCase().replace(/ /g, '-')}`} checked={sectionsToTailor.includes(section)} onCheckedChange={() => handleSectionToggle(section)} className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600" />
                        <Label htmlFor={`section-rev-${section.toLowerCase().replace(/ /g, '-')}`} className={`text-sm font-medium cursor-pointer transition-colors ${sectionsToTailor.includes(section) ? 'text-green-900' : 'text-foreground group-hover:text-primary'}`}>
                          {section}
                        </Label>
                      </div>
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>}

        {/* Submit Button */}
        <div className="flex justify-center pt-8 space-y-4">
          <div className="w-full max-w-2xl">
            {/* Note: Session interrupted banner removed - now handled by TailoringProcessingModal */}
            
            {/* Progress Bar */}
            {isProcessing && <div className="mb-4 space-y-2 animate-fade-in">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Processing your resume...</span>
                  {/* Timer and percentage display */}
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 font-mono text-base font-semibold text-primary">
                      <Clock className="w-4 h-4" />
                      {formatTime(elapsedTime)}
                    </span>
                    <span className="font-semibold">{progressPercentage.toFixed(0)}%</span>
                  </div>
                </div>
                
                {/* Main Progress Bar - Wider and Green */}
                <div className="relative w-full h-4 bg-muted rounded-full overflow-hidden shadow-inner">
                  <div className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out shadow-sm ${isComplete || progressPercentage === 100 ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-green-500 to-green-400'}`} style={{
                        width: `${progressPercentage}%`
                      }} />
                  {/* Animated shine effect */}
                  <div className="absolute top-0 h-full w-8 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" style={{
                        left: `${Math.max(0, progressPercentage - 8)}%`,
                        opacity: progressPercentage > 5 ? 1 : 0
                      }} />
                  {/* Success checkmark and final time when complete */}
                  {(isComplete || progressPercentage === 100) && <div className="absolute inset-0 flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5 text-white animate-scale-in" />
                      <span className="text-white text-xs font-medium">
                        {formatTime(elapsedTime)}
                      </span>
                    </div>}
                </div>
                
                {/* Progress Steps */}
                <div className="flex justify-between text-xs text-muted-foreground/70 mt-1">
                  <span className={progressPercentage >= 15 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    Analyzing
                  </span>
                  <span className={progressPercentage >= 40 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    Tailoring
                  </span>
                  <span className={progressPercentage >= 70 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    Optimizing
                  </span>
                  <span className={progressPercentage >= 85 ? isComplete || progressPercentage === 100 ? "text-green-600 font-medium" : "text-green-600 font-medium" : ""}>
                    {isComplete || progressPercentage === 100 ? "Complete!" : "Finalizing"}
                  </span>
                </div>
              </div>}

            {/* Interrupted-run banner: the browser reloaded the tab mid-run
                (e.g. Chrome Memory Saver discarding a background tab); the
                completed stages are checkpointed, so one click resumes. */}
            {interruptedRun && !isProcessing && (
              <div className="mt-12 p-5 rounded-2xl border border-amber-300 bg-amber-50 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⏸️</span>
                  <div>
                    <p className="font-semibold text-amber-900">A tailoring run was interrupted when the browser reloaded this tab — resuming automatically…</p>
                    <p className="text-sm text-amber-800 mt-1">
                      Finished steps restore instantly from checkpoints and the timer continues from the original start
                      ({Math.floor((Date.now() - interruptedRun.startTs) / 60000)} min ago). If it does not resume by itself,
                      press <b>Start Tailoring</b>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Final Tailoring Section */}
            <div className="mt-12 p-8 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 rounded-2xl shadow-lg border border-gray-200">
              <div className="text-center mb-8">

                <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-3">Ready to Start Tailoring?</h2>
                <p className="text-lg text-slate-600 dark:text-slate-300 max-w-lg mx-auto">
                  All steps completed! Start the AI-powered tailoring process now.
                </p>
              </div>

              {/* Advanced Tailoring transparency — hidden toggles must never
                  silently shape a run. If ANY advanced option is active it is
                  listed here with a one-click reset to the standard method. */}
              {activeAdvancedLabels().length > 0 && (
                <div className="mb-8 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-bold text-amber-900 flex items-center gap-2">
                        ⚠️ Advanced Tailoring is ON for this run
                      </p>
                      <p className="text-sm text-amber-800 mt-1">
                        These options will change the standard output: <span className="font-semibold">{activeAdvancedLabels().join(' · ')}</span>
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetAdvancedOptions}
                      className="border-amber-400 text-amber-900 hover:bg-amber-100 font-semibold shrink-0"
                    >
                      Reset to Standard
                    </Button>
                  </div>
                </div>
              )}

              {/* Career Highlights count — standard 5; user may remove the
                  section entirely or pick 2-10 bullets. */}
              <Card className="shadow-lg border rounded-2xl mb-8 overflow-hidden bg-gradient-to-br from-primary-light/5 to-accent-light/8 border-primary/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="highlight-count-select" className="text-lg font-semibold text-slate-700">
                        Career Highlights
                      </Label>
                      <p className="text-sm text-slate-500">
                        How many Career Highlight bullets the resume gets. Standard is 5 — or remove the section completely.
                      </p>
                    </div>
                    <Select value={String(highlightCount)} onValueChange={(v) => setHighlightCount(Number(v))}>
                      <SelectTrigger id="highlight-count-select" className="w-56 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Career Highlights</SelectItem>
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <SelectItem key={n} value={String(n)}>{n} highlights{n === 5 ? ' (Standard)' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Generate Analysis Toggle - Moved to bottom */}
              <Card className={`shadow-lg border rounded-2xl mb-8 overflow-hidden ${generateAnalysis ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-primary-light/5 to-accent-light/8 border-primary/20'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Label htmlFor="generate-analysis-switch-bottom" className={`text-lg font-semibold flex items-center gap-3 ${generateAnalysis ? 'text-green-800' : 'text-slate-700'}`}>
                        {generateAnalysis && <span className="text-green-600 text-xl">✅</span>}
                        Generate Match Score & Analysis
                        {generateAnalysis && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-3 py-1 rounded-full border-0">ENABLED</Badge>}
                      </Label>
                      <p className={`text-sm ${generateAnalysis ? 'text-green-700' : 'text-slate-500'}`}>
                        Compare BEFORE and AFTER resume match scores with detailed analysis
                      </p>
                    </div>
                    <Switch id="generate-analysis-switch-bottom" checked={generateAnalysis} onCheckedChange={setGenerateAnalysis} className={generateAnalysis ? 'data-[state=checked]:bg-green-600' : ''} />
                  </div>
                </CardContent>
              </Card>

              <Card className={`shadow-lg border rounded-2xl mb-8 overflow-hidden ${autoFixFormatting ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-br from-primary-light/5 to-accent-light/8 border-primary/20'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <Label htmlFor="auto-fix-formatting-switch" className={`text-lg font-semibold flex items-center gap-3 ${autoFixFormatting ? 'text-green-800' : 'text-slate-700'}`}>
                        {autoFixFormatting && <span className="text-green-600 text-xl">✅</span>}
                        Auto-Fix Formatting
                        {autoFixFormatting && <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-3 py-1 rounded-full border-0">ENABLED</Badge>}
                      </Label>
                      <p className={`text-sm ${autoFixFormatting ? 'text-green-700' : 'text-slate-500'}`}>
                        Automatically run the formatting fixers on the finished resume, no Perfect Fit or Layout QA clicks needed
                      </p>
                    </div>
                    <Switch id="auto-fix-formatting-switch" checked={autoFixFormatting} onCheckedChange={setAutoFixFormatting} className={autoFixFormatting ? 'data-[state=checked]:bg-green-600' : ''} />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-center">
                <Button onClick={handleSubmit} size="lg" className="px-16 py-6 h-auto text-xl font-bold bg-gradient-to-r from-primary via-blue-600 to-indigo-600 hover:from-primary/90 hover:via-blue-600/90 hover:to-indigo-600/90 transition-all duration-300 shadow-2xl hover:shadow-primary/30 transform hover:scale-105 rounded-2xl relative overflow-hidden min-w-[320px] max-w-[420px] border-0" disabled={
                  !step1Complete || 
                  !processedResumeData || 
                  !tailoringSource || 
                  isProcessing || 
                  (tailoringSource === 'job-description' && selectedUploadMethod === 'paste' && !jobDescription) || 
                  (tailoringSource === 'job-description' && selectedUploadMethod === 'existing' && !selectedJobId) || 
                  (tailoringSource === 'job-position' && !jobPositionTitle) || 
                  (tailoringSource === 'institution' && !institutionName) || 
                  (tailoringSource === 'assessment-questions' && !assessmentQuestions.trim()) ||
                  (tailoringSource === 'revisions' && selectedRevisionOptions.length === 0 && !revisionInstructions.trim())
                }>
                  {isProcessing ? <div className="flex items-center">
                      <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mr-4" />
                      <span>Tailoring... {formatTime(elapsedTime)} ({progressPercentage.toFixed(0)}%)</span>
                    </div> : <div className="flex items-center">
                      <span className="mr-4 text-2xl">🚀</span>
                      <span>Start Tailoring</span>
                    </div>}
                </Button>
                
                {/* Cancel Button - Only show during processing */}
                {isProcessing && <Button onClick={cancelTailoring} size="lg" variant="outline" className="ml-6 h-auto px-10 py-6 text-lg font-semibold hover:bg-red-50 hover:text-red-600 border-2 border-red-300 text-red-600 transition-all duration-300 shadow-lg rounded-2xl">
                    <X className="w-5 h-5 mr-3" />
                    Stop Tailoring
                  </Button>}
              </div>
            </div>
          </div>
        </div>

        {/* Processing Warning Modal */}
        <TailoringProcessingModal
          isOpen={isProcessing && !quickStartActiveRef.current}
          elapsedTime={elapsedTime}
          progressPercentage={progressPercentage}
          isComplete={isComplete}
          onCancel={cancelTailoring}
        />

        {/* Job Wizard Modal */}
        {/* Assessment Answers generation popup */}
        {generatingAnswers && <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                <BarChart3 className="w-8 h-8 text-white animate-pulse" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">Answering Your Assessment Questions</h3>
              <p className="text-sm text-slate-500 mb-6">
                Writing first-person answers to every question, grounded in your real resume. Nothing is invented. Usually 20 to 40 seconds.
              </p>
              <div className="w-full h-3 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 transition-all duration-700" style={{ width: `${answersProgress}%` }} />
              </div>
              <p className="mt-2 text-xs font-bold text-slate-600">{answersProgress}%</p>
            </div>
          </div>}

        {/* Assessment Answers result overlay */}
        {showAnswersResult && assessmentAnswers && <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-slate-900">Your Assessment Answers</h3>
                  <p className="text-xs text-slate-500">First-person answers built only from your resume. Anything marked [CONFIRM: …] needs your personal answer filled in.</p>
                </div>
                <button onClick={() => setShowAnswersResult(false)} className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-2">×</button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <pre className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed font-sans">{assessmentAnswers}</pre>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-3 justify-end">
                <Button variant="outline" onClick={() => {
                  navigator.clipboard.writeText(assessmentAnswers);
                  toast({ title: 'Copied', description: 'All answers copied to your clipboard.' });
                }}>Copy All Answers</Button>
                <Button variant="outline" onClick={() => {
                  const blob = new Blob([assessmentAnswers], { type: 'text/markdown;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'assessment-answers.md';
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                }}>Download (.md)</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowAnswersResult(false)}>Done</Button>
              </div>
            </div>
          </div>}

        {/* Tailoring Report generation popup — progress bar so the user knows it's running */}
        {tailoringReportGenerating && <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <BarChart3 className="w-8 h-8 text-white animate-pulse" />
              </div>
              <h3 className="text-xl font-black text-slate-900 mb-1">Generating Your Tailoring Report</h3>
              <p className="text-sm text-slate-500 mb-6">
                Mining the job's pain points, matching them against your resume, and locating the hiring manager. This usually takes 30 to 60 seconds.
              </p>
              <div className="w-full h-3 rounded-full bg-slate-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-700"
                  style={{ width: `${tailoringReportProgress}%` }}
                />
              </div>
              <p className="mt-2 text-xs font-bold text-slate-600">{tailoringReportProgress}%</p>
              <p className="mt-4 text-[11px] text-slate-400">
                {tailoringReportProgress < 30 ? 'Analyzing the job description…'
                  : tailoringReportProgress < 60 ? 'Matching pain points against your resume…'
                  : tailoringReportProgress < 90 ? 'Searching for the hiring manager…'
                  : 'Finalizing your report…'}
              </p>
            </div>
          </div>}

        {showJobWizard && <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto p-4">
            <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full mx-auto my-4">
              <JobWizard onClose={() => setShowJobWizard(false)} onSave={savedJob => {
                    // Add the newly created job to the saved jobs list
                    setSavedJobs(prev => [savedJob, ...prev]);
                    // Select the newly created job and set job data ready
                    setSelectedJobId(savedJob.id);
                    // Construct job description from the saved job data
                    const jobDesc = `Company: ${savedJob.companyName}
Job Title: ${savedJob.jobTitle}
Company Mission: ${savedJob.companyMission}

Key Responsibilities:
${savedJob.selectedResponsibilities.map(resp => `• ${resp}`).join('\n')}

Requirements:
${savedJob.selectedRequirements.map(req => `• ${req}`).join('\n')}

Required Skills:
${savedJob.selectedSkills.map(skill => `• ${skill}`).join('\n')}`;
                    // COMPLETE JD (full job) — used to SCORE the tailored resume.
                    const completeJd = buildCompleteJd(savedJob);
                    setCompleteJdText(completeJd);
                    console.log('Debug - Setting job wizard data:', {
                      title: savedJob.jobTitle,
                      descLength: jobDesc.length,
                      completeJdLength: completeJd.length
                    });
                    setProcessedJobData({
                      title: savedJob.jobTitle,
                      description: jobDesc
                    });
                    setJobDataReady(true);
                    // Switch to existing job mode and close wizard
                    setSelectedUploadMethod('existing');
                    setShowJobWizard(false);
                    // Show success toast
                    toast({
                      title: "Job Created Successfully!",
                      description: `${savedJob.jobTitle} at ${savedJob.companyName} has been created and selected.`
                    });
                  }} />
            </div>
          </div>}
            </div>
          </div>
        </div>
      </div>

    {/* Advanced Tailoring Wizard - 3 Steps */}
    {showAdvancedWizard && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        {wizardStep === 'responsibilities' && <Step1Responsibilities responsibilities={jobAnalysis?.responsibilities || []} selectedResponsibilities={selectedResponsibilities} onSelectionChange={setSelectedResponsibilities} onNext={() => setWizardStep('requirements')} onCancel={() => {
          setShowAdvancedWizard(false);
          setWizardStep('responsibilities');
        }} />}
        
        {wizardStep === 'requirements' && <Step2Requirements requirements={jobAnalysis?.requirements || []} selectedRequirements={selectedRequirements} onSelectionChange={setSelectedRequirements} onNext={() => setWizardStep('skills')} onBack={() => setWizardStep('responsibilities')} />}
        
        {wizardStep === 'skills' && <Step3Skills skills={jobAnalysis?.skills || []} selectedSkills={selectedSkills} onSelectionChange={setSelectedSkills} onSave={async () => {
          setSavingAnalysis(true);
          try {
            const savedAnalysis = await JobAnalysisService.saveJobAnalysis({
              jobTitle: jobPositionTitle,
              industry: jobPositionIndustry,
              selectedResponsibilities,
              selectedRequirements,
              selectedSkills,
              allResponsibilities: jobAnalysis?.responsibilities || [],
              allRequirements: jobAnalysis?.requirements || [],
              allSkills: jobAnalysis?.skills || []
            });

            // Automatically select the newly created analysis
            setSelectedSavedAnalysisId(savedAnalysis.id);
            setShowAdvancedWizard(false);
            setWizardStep('responsibilities');
            loadSavedJobAnalyses(); // Refresh the list
            toast({
              title: "Analysis saved and selected!",
              description: "Your advanced tailoring details are ready for use. You can now proceed with tailoring."
            });
          } catch (error) {
            toast({
              title: "Save failed",
              description: "Could not save the analysis. Please try again.",
              variant: "destructive"
            });
          } finally {
            setSavingAnalysis(false);
          }
        }} onBack={() => setWizardStep('requirements')} isSaving={savingAnalysis} />}
      </div>}

     {/* Debug Viewer - Hidden from users but kept for debugging */}
     {(debugData.length > 0 || true) && <div className="hidden p-6 mt-8 border-t bg-muted/30">
         <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
           <div className="flex items-center gap-2">
             <span className="text-yellow-600">⚠️</span>
             <p className="text-sm text-yellow-800">
               <strong>Debug Mode Active:</strong> Found {debugData.length} previous tailoring requests. 
               Check console for loading details. This data persists across page refreshs.
             </p>
           </div>
         </div>
         <TailoringDebugViewer debugData={debugData} />
        </div>}
      </div>
    </div>;
}

// Error boundary: a runtime crash anywhere in the wizard must never blank the
// whole page. Shows the actual error + a one-click reset that clears this
// page's saved state (the most common crash source is stale persisted state
// from an older app version).
class TailoringErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Tailoring page crashed:', error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-blue-100 p-6 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-8 text-center">
            <h1 className="text-2xl font-black text-slate-900 mb-2">The tailoring studio hit an error</h1>
            <p className="text-sm text-slate-500 mb-4">This is usually saved wizard state from an older version of the app. Resetting it fixes it.</p>
            <pre className="text-left text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 mb-5 overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto">
              {String(this.state.error?.message || this.state.error)}
              {this.state.error?.stack ? `\n\n${String(this.state.error.stack).slice(0, 1200)}` : ''}
            </pre>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl"
              onClick={() => {
                try {
                  sessionStorage.removeItem('resume-tailoring-claude-direct-state');
                  localStorage.removeItem('tailoring-debug-data');
                } catch { /* ignore */ }
                window.location.reload();
              }}
            >
              Reset Saved State & Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Main export with PaywallGuard
function ResumeTailoringClaudeDirect() {
  return (
    <TailoringErrorBoundary>
      <PaywallGuard feature="Resume Tailoring">
        <ResumeTailoringClaudeDirectContent />
      </PaywallGuard>
    </TailoringErrorBoundary>
  );
}

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(ResumeTailoringClaudeDirect);